import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles, Star, Puzzle, RefreshCw, Check, X, Trophy, Palette, Music, BookOpen, Eye, Edit2, Trash2, Play, Pause, Volume2, Lightbulb, RotateCcw, Crown, Swords, Code, Keyboard, Shield, Gamepad2, Mic, ShoppingBag, User, Copy } from 'lucide-react';
import api, { questsAPI, constellationsAPI, beatsAPI, songsAPI, storiesAPI, betaAPI, billingAPI } from '../api';
import { toast } from 'sonner';
import VibeCodingGarden from '../components/VibeCodingGarden';
import MusicLearningGarden from '../components/MusicLearningGarden';
import GameForge from '../components/GameForge';
import TempleListens from '../components/TempleListens';
import SparkStore from '../components/SparkStore';
import { FeedbackButton } from '../components/FeedbackButton';
import ActivityLimitModal from '../components/ActivityLimitModal';
import { FullBodyAvatarPreview } from '../components/FullBodyAvatar';
import useKeyboardNavigation from '../hooks/useKeyboardNavigation';

// ============ MUSIC FORGE - RHYTHM PATTERNS ============
const RHYTHM_PATTERNS = {
  beginner: [
    { pattern: [1, 0, 1, 0], bpm: 80, name: 'Steady Beat' },
    { pattern: [1, 1, 0, 0], bpm: 80, name: 'Double Start' },
    { pattern: [1, 0, 0, 1], bpm: 70, name: 'Echo Beat' },
    { pattern: [1, 1, 1, 0], bpm: 85, name: 'Triple Tap' }
  ],
  intermediate: [
    { pattern: [1, 0, 1, 1, 0, 1], bpm: 90, name: 'Swing Beat' },
    { pattern: [1, 1, 0, 1, 0, 1], bpm: 95, name: 'Gallop Rhythm' },
    { pattern: [1, 0, 1, 0, 1, 1], bpm: 90, name: 'Building Beat' }
  ],
  advanced: [
    { pattern: [1, 0, 1, 1, 0, 1, 0, 1], bpm: 100, name: 'Complex Pattern' },
    { pattern: [1, 1, 0, 0, 1, 0, 1, 1], bpm: 100, name: 'Syncopation' }
  ]
};

// ============ CHESS GARDEN - ENHANCED LEARNING SYSTEM ============
// Temple-themed piece definitions with custom SVG icons
const CHESS_PIECES = {
  // White pieces - Temple defenders
  K: { name: 'Temple King', symbol: '♔', color: 'white', icon: 'guardian' },
  Q: { name: 'Temple Queen', symbol: '♕', color: 'white', icon: 'sorceress' },
  R: { name: 'Temple Rook', symbol: '♖', color: 'white', icon: 'tower' },
  N: { name: 'Temple Knight', symbol: '♘', color: 'white', icon: 'dragon' },
  B: { name: 'Temple Bishop', symbol: '♗', color: 'white', icon: 'sage' },
  P: { name: 'Temple Pawn', symbol: '♙', color: 'white', icon: 'explorer' },
  // Black pieces - Shadow realm
  k: { name: 'Shadow King', symbol: '♚', color: 'black', icon: 'shadow_guardian' },
  q: { name: 'Shadow Queen', symbol: '♛', color: 'black', icon: 'shadow_sorceress' },
  r: { name: 'Shadow Rook', symbol: '♜', color: 'black', icon: 'shadow_tower' },
  n: { name: 'Shadow Knight', symbol: '♞', color: 'black', icon: 'shadow_dragon' },
  b: { name: 'Shadow Bishop', symbol: '♝', color: 'black', icon: 'shadow_sage' },
  p: { name: 'Shadow Pawn', symbol: '♟', color: 'black', icon: 'shadow_explorer' }
};

// Roblox/Minecraft style 3D blocky chess pieces
const TEMPLE_PIECE_SVGS = {
  // White King - Blocky crown with 3D shading
  guardian: (size = 24) => (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" className="drop-shadow-lg filter">
      {/* Base block */}
      <rect x="8" y="22" width="16" height="6" fill="#E5E7EB" stroke="#9CA3AF" strokeWidth="0.5"/>
      <rect x="8" y="22" width="16" height="2" fill="#F3F4F6"/>
      {/* Body block */}
      <rect x="10" y="14" width="12" height="8" fill="#F3F4F6" stroke="#9CA3AF" strokeWidth="0.5"/>
      <rect x="10" y="14" width="12" height="2" fill="#FFFFFF"/>
      <rect x="10" y="14" width="2" height="8" fill="#FFFFFF" opacity="0.5"/>
      {/* Crown base */}
      <rect x="8" y="10" width="16" height="4" fill="#FCD34D" stroke="#F59E0B" strokeWidth="0.5"/>
      <rect x="8" y="10" width="16" height="1" fill="#FDE68A"/>
      {/* Crown points */}
      <rect x="8" y="4" width="4" height="6" fill="#FCD34D" stroke="#F59E0B" strokeWidth="0.5"/>
      <rect x="8" y="4" width="4" height="1.5" fill="#FDE68A"/>
      <rect x="14" y="2" width="4" height="8" fill="#FCD34D" stroke="#F59E0B" strokeWidth="0.5"/>
      <rect x="14" y="2" width="4" height="2" fill="#FDE68A"/>
      <rect x="20" y="4" width="4" height="6" fill="#FCD34D" stroke="#F59E0B" strokeWidth="0.5"/>
      <rect x="20" y="4" width="4" height="1.5" fill="#FDE68A"/>
      {/* Gem on crown */}
      <rect x="15" y="4" width="2" height="2" fill="#EF4444"/>
      <rect x="15" y="4" width="2" height="0.5" fill="#FCA5A5"/>
    </svg>
  ),
  // White Queen - Tall elegant blocky piece
  sorceress: (size = 24) => (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" className="drop-shadow-lg">
      {/* Base */}
      <rect x="8" y="24" width="16" height="4" fill="#E5E7EB" stroke="#9CA3AF" strokeWidth="0.5"/>
      <rect x="8" y="24" width="16" height="1" fill="#F3F4F6"/>
      {/* Body */}
      <rect x="10" y="12" width="12" height="12" fill="#F3F4F6" stroke="#9CA3AF" strokeWidth="0.5"/>
      <rect x="10" y="12" width="12" height="2" fill="#FFFFFF"/>
      <rect x="10" y="12" width="2" height="12" fill="#FFFFFF" opacity="0.5"/>
      {/* Crown ring */}
      <rect x="8" y="8" width="16" height="4" fill="#A855F7" stroke="#7C3AED" strokeWidth="0.5"/>
      <rect x="8" y="8" width="16" height="1" fill="#C084FC"/>
      {/* Spiky crown */}
      <polygon points="10,8 12,2 14,8" fill="#A855F7" stroke="#7C3AED" strokeWidth="0.5"/>
      <polygon points="14,8 16,1 18,8" fill="#A855F7" stroke="#7C3AED" strokeWidth="0.5"/>
      <polygon points="18,8 20,2 22,8" fill="#A855F7" stroke="#7C3AED" strokeWidth="0.5"/>
      {/* Orb on top */}
      <circle cx="16" cy="3" r="2" fill="#FBBF24" stroke="#F59E0B" strokeWidth="0.5"/>
      <circle cx="15.5" cy="2.5" r="0.5" fill="#FEF3C7"/>
    </svg>
  ),
  // White Rook - Blocky castle tower
  tower: (size = 24) => (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" className="drop-shadow-lg">
      {/* Base */}
      <rect x="6" y="24" width="20" height="4" fill="#E5E7EB" stroke="#9CA3AF" strokeWidth="0.5"/>
      <rect x="6" y="24" width="20" height="1" fill="#F3F4F6"/>
      {/* Tower body */}
      <rect x="8" y="10" width="16" height="14" fill="#D1D5DB" stroke="#9CA3AF" strokeWidth="0.5"/>
      <rect x="8" y="10" width="16" height="2" fill="#E5E7EB"/>
      <rect x="8" y="10" width="2" height="14" fill="#E5E7EB" opacity="0.7"/>
      {/* Battlements */}
      <rect x="6" y="4" width="5" height="6" fill="#D1D5DB" stroke="#9CA3AF" strokeWidth="0.5"/>
      <rect x="6" y="4" width="5" height="1.5" fill="#E5E7EB"/>
      <rect x="13.5" y="4" width="5" height="6" fill="#D1D5DB" stroke="#9CA3AF" strokeWidth="0.5"/>
      <rect x="13.5" y="4" width="5" height="1.5" fill="#E5E7EB"/>
      <rect x="21" y="4" width="5" height="6" fill="#D1D5DB" stroke="#9CA3AF" strokeWidth="0.5"/>
      <rect x="21" y="4" width="5" height="1.5" fill="#E5E7EB"/>
      {/* Window */}
      <rect x="14" y="16" width="4" height="6" fill="#374151"/>
    </svg>
  ),
  // White Knight - Blocky horse head
  dragon: (size = 24) => (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" className="drop-shadow-lg">
      {/* Base */}
      <rect x="8" y="24" width="16" height="4" fill="#E5E7EB" stroke="#9CA3AF" strokeWidth="0.5"/>
      <rect x="8" y="24" width="16" height="1" fill="#F3F4F6"/>
      {/* Body/neck */}
      <rect x="12" y="14" width="8" height="10" fill="#F3F4F6" stroke="#9CA3AF" strokeWidth="0.5"/>
      <rect x="12" y="14" width="8" height="2" fill="#FFFFFF"/>
      {/* Head block */}
      <rect x="8" y="6" width="12" height="10" fill="#E5E7EB" stroke="#9CA3AF" strokeWidth="0.5"/>
      <rect x="8" y="6" width="12" height="2" fill="#F3F4F6"/>
      <rect x="8" y="6" width="2" height="10" fill="#F3F4F6" opacity="0.5"/>
      {/* Snout */}
      <rect x="4" y="10" width="6" height="4" fill="#D1D5DB" stroke="#9CA3AF" strokeWidth="0.5"/>
      <rect x="4" y="10" width="6" height="1" fill="#E5E7EB"/>
      {/* Ear */}
      <rect x="16" y="2" width="4" height="6" fill="#D1D5DB" stroke="#9CA3AF" strokeWidth="0.5"/>
      <rect x="16" y="2" width="4" height="1.5" fill="#E5E7EB"/>
      {/* Eye */}
      <rect x="10" y="8" width="3" height="2" fill="#1E293B"/>
      <rect x="10" y="8" width="1" height="1" fill="#FFFFFF"/>
      {/* Mane */}
      <rect x="18" y="8" width="3" height="8" fill="#9CA3AF"/>
    </svg>
  ),
  // White Bishop - Blocky pointed hat piece
  sage: (size = 24) => (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" className="drop-shadow-lg">
      {/* Base */}
      <rect x="8" y="24" width="16" height="4" fill="#E5E7EB" stroke="#9CA3AF" strokeWidth="0.5"/>
      <rect x="8" y="24" width="16" height="1" fill="#F3F4F6"/>
      {/* Body */}
      <rect x="10" y="14" width="12" height="10" fill="#F3F4F6" stroke="#9CA3AF" strokeWidth="0.5"/>
      <rect x="10" y="14" width="12" height="2" fill="#FFFFFF"/>
      <rect x="10" y="14" width="2" height="10" fill="#FFFFFF" opacity="0.5"/>
      {/* Mitre/hat base */}
      <rect x="10" y="10" width="12" height="4" fill="#A78BFA" stroke="#7C3AED" strokeWidth="0.5"/>
      <rect x="10" y="10" width="12" height="1" fill="#C4B5FD"/>
      {/* Pointed top */}
      <polygon points="10,10 16,2 22,10" fill="#A78BFA" stroke="#7C3AED" strokeWidth="0.5"/>
      <polygon points="12,10 16,4 16,10" fill="#C4B5FD" opacity="0.5"/>
      {/* Cross on hat */}
      <rect x="15" y="4" width="2" height="4" fill="#FCD34D"/>
      <rect x="14" y="5" width="4" height="2" fill="#FCD34D"/>
      {/* Slit */}
      <rect x="15" y="12" width="2" height="2" fill="#374151"/>
    </svg>
  ),
  // White Pawn - Small blocky piece
  explorer: (size = 24) => (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" className="drop-shadow-lg">
      {/* Base */}
      <rect x="10" y="24" width="12" height="4" fill="#E5E7EB" stroke="#9CA3AF" strokeWidth="0.5"/>
      <rect x="10" y="24" width="12" height="1" fill="#F3F4F6"/>
      {/* Body */}
      <rect x="12" y="16" width="8" height="8" fill="#F3F4F6" stroke="#9CA3AF" strokeWidth="0.5"/>
      <rect x="12" y="16" width="8" height="2" fill="#FFFFFF"/>
      <rect x="12" y="16" width="1.5" height="8" fill="#FFFFFF" opacity="0.5"/>
      {/* Neck */}
      <rect x="14" y="12" width="4" height="4" fill="#E5E7EB" stroke="#9CA3AF" strokeWidth="0.5"/>
      {/* Head */}
      <rect x="12" y="6" width="8" height="6" rx="1" fill="#E5E7EB" stroke="#9CA3AF" strokeWidth="0.5"/>
      <rect x="12" y="6" width="8" height="1.5" fill="#F3F4F6"/>
      <rect x="12" y="6" width="1.5" height="6" fill="#F3F4F6" opacity="0.5"/>
    </svg>
  ),
  
  // Black pieces (darker, more ominous versions)
  shadow_guardian: (size = 24) => (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" className="drop-shadow-lg">
      {/* Base block */}
      <rect x="8" y="22" width="16" height="6" fill="#1F2937" stroke="#374151" strokeWidth="0.5"/>
      <rect x="8" y="22" width="16" height="2" fill="#374151"/>
      {/* Body block */}
      <rect x="10" y="14" width="12" height="8" fill="#1F2937" stroke="#374151" strokeWidth="0.5"/>
      <rect x="10" y="14" width="12" height="2" fill="#374151"/>
      <rect x="10" y="14" width="2" height="8" fill="#374151" opacity="0.5"/>
      {/* Crown base */}
      <rect x="8" y="10" width="16" height="4" fill="#7C3AED" stroke="#5B21B6" strokeWidth="0.5"/>
      <rect x="8" y="10" width="16" height="1" fill="#8B5CF6"/>
      {/* Crown points */}
      <rect x="8" y="4" width="4" height="6" fill="#7C3AED" stroke="#5B21B6" strokeWidth="0.5"/>
      <rect x="8" y="4" width="4" height="1.5" fill="#8B5CF6"/>
      <rect x="14" y="2" width="4" height="8" fill="#7C3AED" stroke="#5B21B6" strokeWidth="0.5"/>
      <rect x="14" y="2" width="4" height="2" fill="#8B5CF6"/>
      <rect x="20" y="4" width="4" height="6" fill="#7C3AED" stroke="#5B21B6" strokeWidth="0.5"/>
      <rect x="20" y="4" width="4" height="1.5" fill="#8B5CF6"/>
      {/* Gem */}
      <rect x="15" y="4" width="2" height="2" fill="#10B981"/>
      <rect x="15" y="4" width="2" height="0.5" fill="#34D399"/>
    </svg>
  ),
  shadow_sorceress: (size = 24) => (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" className="drop-shadow-lg">
      {/* Base */}
      <rect x="8" y="24" width="16" height="4" fill="#1F2937" stroke="#374151" strokeWidth="0.5"/>
      <rect x="8" y="24" width="16" height="1" fill="#374151"/>
      {/* Body */}
      <rect x="10" y="12" width="12" height="12" fill="#1F2937" stroke="#374151" strokeWidth="0.5"/>
      <rect x="10" y="12" width="12" height="2" fill="#374151"/>
      <rect x="10" y="12" width="2" height="12" fill="#374151" opacity="0.5"/>
      {/* Crown ring */}
      <rect x="8" y="8" width="16" height="4" fill="#DC2626" stroke="#B91C1C" strokeWidth="0.5"/>
      <rect x="8" y="8" width="16" height="1" fill="#EF4444"/>
      {/* Spiky crown */}
      <polygon points="10,8 12,2 14,8" fill="#DC2626" stroke="#B91C1C" strokeWidth="0.5"/>
      <polygon points="14,8 16,1 18,8" fill="#DC2626" stroke="#B91C1C" strokeWidth="0.5"/>
      <polygon points="18,8 20,2 22,8" fill="#DC2626" stroke="#B91C1C" strokeWidth="0.5"/>
      {/* Orb */}
      <circle cx="16" cy="3" r="2" fill="#10B981" stroke="#059669" strokeWidth="0.5"/>
      <circle cx="15.5" cy="2.5" r="0.5" fill="#6EE7B7"/>
    </svg>
  ),
  shadow_tower: (size = 24) => (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" className="drop-shadow-lg">
      {/* Base */}
      <rect x="6" y="24" width="20" height="4" fill="#1F2937" stroke="#374151" strokeWidth="0.5"/>
      <rect x="6" y="24" width="20" height="1" fill="#374151"/>
      {/* Tower body */}
      <rect x="8" y="10" width="16" height="14" fill="#111827" stroke="#374151" strokeWidth="0.5"/>
      <rect x="8" y="10" width="16" height="2" fill="#1F2937"/>
      <rect x="8" y="10" width="2" height="14" fill="#1F2937" opacity="0.7"/>
      {/* Battlements */}
      <rect x="6" y="4" width="5" height="6" fill="#111827" stroke="#374151" strokeWidth="0.5"/>
      <rect x="6" y="4" width="5" height="1.5" fill="#1F2937"/>
      <rect x="13.5" y="4" width="5" height="6" fill="#111827" stroke="#374151" strokeWidth="0.5"/>
      <rect x="13.5" y="4" width="5" height="1.5" fill="#1F2937"/>
      <rect x="21" y="4" width="5" height="6" fill="#111827" stroke="#374151" strokeWidth="0.5"/>
      <rect x="21" y="4" width="5" height="1.5" fill="#1F2937"/>
      {/* Window - glowing */}
      <rect x="14" y="16" width="4" height="6" fill="#7C3AED"/>
    </svg>
  ),
  shadow_dragon: (size = 24) => (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" className="drop-shadow-lg">
      {/* Base */}
      <rect x="8" y="24" width="16" height="4" fill="#1F2937" stroke="#374151" strokeWidth="0.5"/>
      <rect x="8" y="24" width="16" height="1" fill="#374151"/>
      {/* Body/neck */}
      <rect x="12" y="14" width="8" height="10" fill="#1F2937" stroke="#374151" strokeWidth="0.5"/>
      <rect x="12" y="14" width="8" height="2" fill="#374151"/>
      {/* Head block */}
      <rect x="8" y="6" width="12" height="10" fill="#111827" stroke="#374151" strokeWidth="0.5"/>
      <rect x="8" y="6" width="12" height="2" fill="#1F2937"/>
      <rect x="8" y="6" width="2" height="10" fill="#1F2937" opacity="0.5"/>
      {/* Snout */}
      <rect x="4" y="10" width="6" height="4" fill="#1F2937" stroke="#374151" strokeWidth="0.5"/>
      <rect x="4" y="10" width="6" height="1" fill="#374151"/>
      {/* Ear */}
      <rect x="16" y="2" width="4" height="6" fill="#1F2937" stroke="#374151" strokeWidth="0.5"/>
      <rect x="16" y="2" width="4" height="1.5" fill="#374151"/>
      {/* Eye - glowing red */}
      <rect x="10" y="8" width="3" height="2" fill="#EF4444"/>
      <rect x="10" y="8" width="1" height="1" fill="#FCA5A5"/>
      {/* Mane */}
      <rect x="18" y="8" width="3" height="8" fill="#374151"/>
    </svg>
  ),
  shadow_sage: (size = 24) => (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" className="drop-shadow-lg">
      {/* Base */}
      <rect x="8" y="24" width="16" height="4" fill="#1F2937" stroke="#374151" strokeWidth="0.5"/>
      <rect x="8" y="24" width="16" height="1" fill="#374151"/>
      {/* Body */}
      <rect x="10" y="14" width="12" height="10" fill="#1F2937" stroke="#374151" strokeWidth="0.5"/>
      <rect x="10" y="14" width="12" height="2" fill="#374151"/>
      <rect x="10" y="14" width="2" height="10" fill="#374151" opacity="0.5"/>
      {/* Mitre/hat base */}
      <rect x="10" y="10" width="12" height="4" fill="#7C3AED" stroke="#5B21B6" strokeWidth="0.5"/>
      <rect x="10" y="10" width="12" height="1" fill="#8B5CF6"/>
      {/* Pointed top */}
      <polygon points="10,10 16,2 22,10" fill="#7C3AED" stroke="#5B21B6" strokeWidth="0.5"/>
      <polygon points="12,10 16,4 16,10" fill="#8B5CF6" opacity="0.5"/>
      {/* Cross on hat - glowing */}
      <rect x="15" y="4" width="2" height="4" fill="#10B981"/>
      <rect x="14" y="5" width="4" height="2" fill="#10B981"/>
      {/* Slit - glowing */}
      <rect x="15" y="12" width="2" height="2" fill="#EF4444"/>
    </svg>
  ),
  shadow_explorer: (size = 24) => (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" className="drop-shadow-lg">
      {/* Base */}
      <rect x="10" y="24" width="12" height="4" fill="#1F2937" stroke="#374151" strokeWidth="0.5"/>
      <rect x="10" y="24" width="12" height="1" fill="#374151"/>
      {/* Body */}
      <rect x="12" y="16" width="8" height="8" fill="#1F2937" stroke="#374151" strokeWidth="0.5"/>
      <rect x="12" y="16" width="8" height="2" fill="#374151"/>
      <rect x="12" y="16" width="1.5" height="8" fill="#374151" opacity="0.5"/>
      {/* Neck */}
      <rect x="14" y="12" width="4" height="4" fill="#111827" stroke="#374151" strokeWidth="0.5"/>
      {/* Head */}
      <rect x="12" y="6" width="8" height="6" rx="1" fill="#111827" stroke="#374151" strokeWidth="0.5"/>
      <rect x="12" y="6" width="8" height="1.5" fill="#1F2937"/>
      <rect x="12" y="6" width="1.5" height="6" fill="#1F2937" opacity="0.5"/>
    </svg>
  )
};

// Learning cards for each piece - Temple themed
const CHESS_LEARNING_CARDS = [
  {
    id: 'king',
    name: 'Temple Guardian',
    symbol: '👑',
    description: 'The most important piece! If your Guardian is trapped (checkmate), you lose the game.',
    movement: 'Moves ONE square in any direction - up, down, left, right, or diagonal.',
    tip: 'Keep your Guardian safe! Castle early to protect it.',
    exampleBoard: { K: 'd4' },
    highlightSquares: ['c3', 'c4', 'c5', 'd3', 'd5', 'e3', 'e4', 'e5'],
    color: '#FBBF24'
  },
  {
    id: 'queen',
    name: 'Star Sorceress',
    symbol: '⭐',
    description: 'The most powerful piece! She combines the powers of the Rook and Bishop.',
    movement: 'Moves any number of squares in straight lines OR diagonals.',
    tip: 'The Queen is very valuable - don\'t lose her early!',
    exampleBoard: { Q: 'd4' },
    highlightSquares: ['a1', 'a4', 'a7', 'b2', 'b4', 'b6', 'c3', 'c4', 'c5', 'd1', 'd2', 'd3', 'd5', 'd6', 'd7', 'd8', 'e3', 'e4', 'e5', 'f2', 'f4', 'f6', 'g1', 'g4', 'g7', 'h4', 'h8'],
    color: '#A855F7'
  },
  {
    id: 'rook',
    name: 'Rook',
    symbol: '♖',
    description: 'A strong piece that looks like a castle tower!',
    movement: 'Moves any number of squares in STRAIGHT lines - up, down, left, or right.',
    tip: 'Rooks are great on open files (columns with no pawns).',
    exampleBoard: { R: 'd4' },
    highlightSquares: ['a4', 'b4', 'c4', 'e4', 'f4', 'g4', 'h4', 'd1', 'd2', 'd3', 'd5', 'd6', 'd7', 'd8'],
    color: '#3B82F6'
  },
  {
    id: 'knight',
    name: 'Knight',
    symbol: '♘',
    description: 'The tricky horse! It\'s the only piece that can jump over others.',
    movement: 'Moves in an "L" shape: 2 squares one way, then 1 square sideways.',
    tip: 'Knights are sneaky - they can attack pieces that other pieces can\'t reach!',
    exampleBoard: { N: 'd4' },
    highlightSquares: ['c2', 'e2', 'b3', 'f3', 'b5', 'f5', 'c6', 'e6'],
    color: '#22C55E'
  }
];

// Enhanced puzzles with visual boards and progressive difficulty
const CHESS_PUZZLES_ENHANCED = {
  // Level 1: Piece Recognition (text-based)
  recognition: [
    {
      level: 1,
      type: 'recognition',
      question: 'Which piece is the most important to protect?',
      options: ['King', 'Queen', 'Rook', 'Knight'],
      answer: 'King',
      explanation: 'The King is the heart of your army! If it\'s checkmated, the game is over.',
      hint: 'If you lose this piece, you lose the game!',
      sparks: 5
    },
    {
      level: 1,
      type: 'recognition',
      question: 'Which piece can jump over other pieces?',
      options: ['King', 'Queen', 'Rook', 'Knight'],
      answer: 'Knight',
      explanation: 'The Knight is like a horse that can hop over any piece in its way!',
      hint: 'Think about which piece looks like a horse.',
      sparks: 5
    },
    {
      level: 1,
      type: 'recognition',
      question: 'Which piece is the most powerful on the board?',
      options: ['King', 'Queen', 'Rook', 'Knight'],
      answer: 'Queen',
      explanation: 'The Queen can move like both a Rook (straight) and a Bishop (diagonal)!',
      hint: 'This piece can do what Rooks AND Bishops can do.',
      sparks: 5
    },
    {
      level: 1,
      type: 'recognition',
      question: 'Which piece moves in a straight line like a tower?',
      options: ['King', 'Queen', 'Rook', 'Knight'],
      answer: 'Rook',
      explanation: 'The Rook looks like a castle tower and moves in straight lines!',
      hint: 'It looks like a tower or castle.',
      sparks: 5
    },
    {
      level: 1,
      type: 'recognition',
      question: 'How many squares can a King move at one time?',
      options: ['1 square', '2 squares', 'Any number', 'It cannot move'],
      answer: '1 square',
      explanation: 'The King moves slowly but surely - one square at a time in any direction!',
      hint: 'The King is careful and takes small steps.',
      sparks: 5
    },
    {
      level: 1,
      type: 'recognition',
      question: 'What shape does the Knight\'s move make?',
      options: ['Straight line', 'Diagonal line', 'L-shape', 'Circle'],
      answer: 'L-shape',
      explanation: 'The Knight jumps in an L-shape: 2 squares one way, then 1 square to the side!',
      hint: 'Think of the letter L.',
      sparks: 5
    }
  ],
  // Level 2: Movement Understanding (with board visuals)
  movement: [
    {
      level: 2,
      type: 'movement',
      question: 'The King is at D4. Which highlighted square can it move to?',
      board: { K: 'd4' },
      highlight: ['c3', 'c4', 'c5', 'd3', 'd5', 'e3', 'e4', 'e5'],
      options: ['A1', 'E5', 'H8', 'A8'],
      answer: 'E5',
      explanation: 'The King can only move ONE square at a time, and E5 is next to D4!',
      hint: 'Remember: the King moves only one square in any direction.',
      sparks: 8
    },
    {
      level: 2,
      type: 'movement',
      question: 'The Knight is at B1. Where will it land after its L-shaped jump?',
      board: { N: 'b1' },
      highlight: ['a3', 'c3', 'd2'],
      options: ['B3', 'C3', 'D1', 'A1'],
      answer: 'C3',
      explanation: 'Knights move in an L-shape: 2 squares up + 1 square right = C3!',
      hint: 'Count: 2 squares one way, then 1 square to the side.',
      sparks: 8
    },
    {
      level: 2,
      type: 'movement',
      question: 'Can the Rook at A1 reach A8 in one move?',
      board: { R: 'a1' },
      highlight: ['a2', 'a3', 'a4', 'a5', 'a6', 'a7', 'a8', 'b1', 'c1', 'd1', 'e1', 'f1', 'g1', 'h1'],
      options: ['Yes!', 'No'],
      answer: 'Yes!',
      explanation: 'Rooks move in straight lines as far as they want!',
      hint: 'Rooks can travel any distance in straight lines.',
      sparks: 8
    },
    {
      level: 2,
      type: 'movement',
      question: 'The Queen at D4 wants to reach H8. Can she do it in one move?',
      board: { Q: 'd4' },
      highlight: ['a1', 'a4', 'a7', 'b2', 'b6', 'c3', 'c5', 'd1', 'd8', 'e3', 'e5', 'f2', 'f6', 'g1', 'g7', 'h4', 'h8'],
      options: ['Yes, diagonally!', 'No, too far'],
      answer: 'Yes, diagonally!',
      explanation: 'The Queen can move diagonally like a Bishop, so D4 to H8 is possible!',
      hint: 'Queens can move like both Rooks AND Bishops.',
      sparks: 8
    },
    {
      level: 2,
      type: 'movement',
      question: 'Knight at F3. How many squares can it jump to?',
      board: { N: 'f3' },
      highlight: ['d2', 'd4', 'e1', 'e5', 'g1', 'g5', 'h2', 'h4'],
      options: ['4 squares', '6 squares', '8 squares', '2 squares'],
      answer: '8 squares',
      explanation: 'A Knight in the middle of the board can reach 8 different squares with its L-jump!',
      hint: 'Count all the green highlighted squares.',
      sparks: 8
    },
    {
      level: 2,
      type: 'movement',
      question: 'Can the King at E1 reach E3 in ONE move?',
      board: { K: 'e1' },
      highlight: ['d1', 'd2', 'e2', 'f1', 'f2'],
      options: ['Yes', 'No, too far'],
      answer: 'No, too far',
      explanation: 'The King can only move one square at a time. E3 is two squares away!',
      hint: 'Kings are slow - they move just one step.',
      sparks: 8
    }
  ],
  // Level 3: Capture & Check Puzzles
  capture: [
    {
      level: 3,
      type: 'capture',
      question: 'White Rook at A1, Black King at A8. Can the Rook capture the King?',
      board: { R: 'a1', k: 'a8' },
      highlight: ['a8'],
      options: ['Yes, capture it!', 'No, can\'t reach'],
      answer: 'Yes, capture it!',
      explanation: 'The Rook moves straight up the A-file to capture the King at A8!',
      hint: 'Rooks move in straight lines...',
      sparks: 10
    },
    {
      level: 3,
      type: 'capture',
      question: 'Knight at D4, enemy pieces at B3, E6, F5, and H4. Which can the Knight capture?',
      board: { N: 'd4', q: 'b3', r: 'e6', n: 'f5', k: 'h4' },
      highlight: ['b3', 'c2', 'e2', 'f3', 'f5', 'e6', 'c6', 'b5'],
      options: ['Queen at B3', 'Rook at E6', 'Knight at F5', 'All three!'],
      answer: 'All three!',
      explanation: 'B3, E6, and F5 are all L-shaped jumps away from D4. The Knight can reach them all!',
      hint: 'Check each piece - is it an L-shape away?',
      sparks: 10
    },
    {
      level: 3,
      type: 'capture',
      question: 'Queen at E4. Can she capture the Rook at A8 in one move?',
      board: { Q: 'e4', r: 'a8' },
      highlight: ['a8', 'b7', 'c6', 'd5', 'h7', 'h1', 'a4', 'e8', 'e1'],
      options: ['Yes, diagonally', 'Yes, straight line', 'No'],
      answer: 'No',
      explanation: 'A8 is not on the same row, column, or diagonal as E4. The Queen can\'t reach it!',
      hint: 'Check: is A8 in a straight line or diagonal from E4?',
      sparks: 10
    },
    {
      level: 3,
      type: 'check',
      question: 'White Rook at D1, Black King at D8. Is the King in CHECK?',
      board: { R: 'd1', k: 'd8' },
      highlight: ['d2', 'd3', 'd4', 'd5', 'd6', 'd7', 'd8'],
      options: ['Yes, check!', 'No, safe'],
      answer: 'Yes, check!',
      explanation: 'The Rook attacks straight up the D-file. The King is under attack = CHECK!',
      hint: 'If a piece can capture the King, it\'s check!',
      sparks: 10
    },
    {
      level: 3,
      type: 'check',
      question: 'Knight at C3, King at E2. Is the King in check?',
      board: { N: 'c3', k: 'e2' },
      highlight: ['a2', 'a4', 'b1', 'b5', 'd1', 'd5', 'e2', 'e4'],
      options: ['Yes, check!', 'No, safe'],
      answer: 'Yes, check!',
      explanation: 'The Knight at C3 can jump to E2 in an L-shape. The King is in check!',
      hint: 'Can the Knight reach the King with its L-jump?',
      sparks: 10
    },
    {
      level: 3,
      type: 'safe_move',
      question: 'Your King is at E1. Where is the SAFEST square to move?',
      board: { K: 'e1', r: 'd8', q: 'h4' },
      highlight: ['d1', 'd2', 'e2', 'f1', 'f2'],
      options: ['D1 - behind Rook line', 'F2 - away from both', 'E2 - forward', 'D2 - close to Rook'],
      answer: 'F2 - away from both',
      explanation: 'F2 is not on the Rook\'s attack lines and not where the Queen can reach!',
      hint: 'Avoid squares that enemy pieces can attack.',
      sparks: 10
    }
  ],
  // Level 4: Checkmate Recognition & Tactics
  checkmate: [
    {
      level: 4,
      type: 'checkmate',
      question: 'White Queen at G7, Black King at H8, White King at F6. Is this checkmate?',
      board: { Q: 'g7', k: 'h8', K: 'f6' },
      highlight: ['g8', 'h7'],
      options: ['Yes, checkmate!', 'No, King can escape'],
      answer: 'Yes, checkmate!',
      explanation: 'The Black King is attacked by the Queen. G8 and H7 are also covered. No escape = checkmate!',
      hint: 'Where can the Black King go? Are all escape squares blocked?',
      sparks: 12
    },
    {
      level: 4,
      type: 'checkmate',
      question: 'Rook at A8, Black King at H8. Is this checkmate?',
      board: { R: 'a8', k: 'h8' },
      highlight: ['g8', 'g7', 'h7'],
      options: ['Yes, checkmate!', 'No, King can escape'],
      answer: 'No, King can escape',
      explanation: 'The King can escape to G7 or H7! The Rook only attacks along the 8th rank.',
      hint: 'Check if there are safe squares the King can run to.',
      sparks: 12
    },
    {
      level: 4,
      type: 'checkmate',
      question: 'Two Rooks at A8 and A1, Black King at H8. Is this checkmate?',
      board: { R: 'a8', r: 'a1', k: 'h8' },
      options: ['Yes, checkmate!', 'No, King can escape'],
      answer: 'No, King can escape',
      explanation: 'The King can still escape to G7 or H7! We need to control more squares.',
      hint: 'Two Rooks attack two rows, but the King can slip away diagonally.',
      sparks: 12
    },
    {
      level: 4,
      type: 'checkmate_in_one',
      question: 'White Queen at D1, Black King at H1, White King at F3. Where should Queen move for checkmate?',
      board: { Q: 'd1', k: 'h1', K: 'f3' },
      highlight: ['g1', 'h3'],
      options: ['Queen to H3', 'Queen to G1', 'Queen to A1', 'Queen to D8'],
      answer: 'Queen to G1',
      explanation: 'Queen to G1 puts the King in check, and H2 is covered by the King. Checkmate!',
      hint: 'Look for a move that attacks the King AND blocks all escapes.',
      sparks: 15
    },
    {
      level: 4,
      type: 'checkmate_in_one',
      question: 'White Rook at A1, White King at G6, Black King at H8. Find checkmate in one move!',
      board: { R: 'a1', K: 'g6', k: 'h8' },
      highlight: ['a8'],
      options: ['Rook to H1', 'Rook to A8', 'Rook to A7', 'King to H6'],
      answer: 'Rook to A8',
      explanation: 'Rook to A8 is check! G8 is blocked by the White King\'s control. Checkmate!',
      hint: 'Use the Rook to attack the back rank.',
      sparks: 15
    },
    {
      level: 4,
      type: 'board_position',
      question: 'Which side has the better position?',
      board: { K: 'g1', Q: 'd4', R: 'a1', r: 'h1', k: 'e8', q: 'd8', n: 'f6' },
      options: ['White (more active pieces)', 'Black (King is safer)', 'Equal position', 'Cannot tell'],
      answer: 'White (more active pieces)',
      explanation: 'White\'s Queen and Rook are both active and controlling the board. Black is on defense.',
      hint: 'Look at which pieces are doing more - controlling squares and threatening.',
      sparks: 12
    }
  ]
};

// Predefined chess puzzles (progressive difficulty) - legacy format for compatibility
const CHESS_PUZZLES = [
  ...CHESS_PUZZLES_ENHANCED.recognition,
  ...CHESS_PUZZLES_ENHANCED.movement,
  ...CHESS_PUZZLES_ENHANCED.capture,
  ...CHESS_PUZZLES_ENHANCED.checkmate
];

// ============ PUZZLE SHAPE DEFINITIONS ============
const PUZZLE_SHAPES = {
  circle: { emoji: '🔵', name: 'Circle' },
  square: { emoji: '🟦', name: 'Square' },
  triangle: { emoji: '🔺', name: 'Triangle' },
  diamond: { emoji: '💎', name: 'Diamond' },
  star: { emoji: '⭐', name: 'Star' },
  moon: { emoji: '🌙', name: 'Moon' },
  heart: { emoji: '❤️', name: 'Heart' },
  hexagon: { emoji: '⬡', name: 'Hexagon' },
  spiral: { emoji: '🌀', name: 'Spiral' },
  crystal: { emoji: '💠', name: 'Crystal' }
};

const PUZZLE_COLORS = {
  red: { bg: '#EF4444', emoji: '🔴' },
  blue: { bg: '#3B82F6', emoji: '🔵' },
  green: { bg: '#22C55E', emoji: '🟢' },
  yellow: { bg: '#F59E0B', emoji: '🟡' },
  purple: { bg: '#8B5CF6', emoji: '🟣' },
  orange: { bg: '#F97316', emoji: '🟠' },
  pink: { bg: '#EC4899', emoji: '💗' },
  teal: { bg: '#14B8A6', emoji: '🩵' }
};

// ============ PUZZLE GENERATORS ============
const generatePatternPuzzle = (difficulty) => {
  const shapes = Object.keys(PUZZLE_SHAPES);
  const colors = Object.keys(PUZZLE_COLORS);
  
  // Pattern length based on difficulty
  const patternLength = difficulty === 1 ? 2 : difficulty === 2 ? 3 : 4;
  const sequenceLength = difficulty === 1 ? 4 : difficulty === 2 ? 5 : 6;
  
  // Create pattern
  const pattern = [];
  for (let i = 0; i < patternLength; i++) {
    pattern.push({
      shape: shapes[Math.floor(Math.random() * shapes.length)],
      color: colors[Math.floor(Math.random() * colors.length)]
    });
  }
  
  // Generate sequence
  const sequence = [];
  for (let i = 0; i < sequenceLength; i++) {
    sequence.push(pattern[i % patternLength]);
  }
  
  // The answer is the next item in the pattern
  const answer = pattern[sequenceLength % patternLength];
  
  // Generate wrong options
  const options = [answer];
  while (options.length < 4) {
    const wrong = {
      shape: shapes[Math.floor(Math.random() * shapes.length)],
      color: colors[Math.floor(Math.random() * colors.length)]
    };
    if (!options.some(o => o.shape === wrong.shape && o.color === wrong.color)) {
      options.push(wrong);
    }
  }
  
  // Shuffle options
  options.sort(() => Math.random() - 0.5);
  
  return {
    type: 'pattern',
    question: 'What comes next in the pattern?',
    sequence,
    options,
    answer,
    hint: `Look at the repeating pattern of ${patternLength} items`,
    sparks: 5 + difficulty * 3
  };
};

const generateMathPuzzle = (difficulty) => {
  const shapes = Object.keys(PUZZLE_SHAPES).slice(0, 6);
  const shape = shapes[Math.floor(Math.random() * shapes.length)];
  const shapeEmoji = PUZZLE_SHAPES[shape].emoji;
  
  let question, answer, options, hint;
  
  if (difficulty === 1) {
    // Simple addition: shape + shape = X
    const value = Math.floor(Math.random() * 5) + 1;
    const sum = value * 2;
    question = `If ${shapeEmoji} + ${shapeEmoji} = ${sum}, what is ${shapeEmoji}?`;
    answer = String(value);
    options = [answer, String(value + 1), String(value - 1 || 1), String(value + 2)];
    hint = `The same shape twice equals ${sum}. Divide ${sum} by 2!`;
  } else if (difficulty === 2) {
    // Two shapes: A + B = X
    const shapes2 = shapes.filter(s => s !== shape);
    const shape2 = shapes2[Math.floor(Math.random() * shapes2.length)];
    const val1 = Math.floor(Math.random() * 5) + 2;
    const val2 = Math.floor(Math.random() * 5) + 2;
    question = `If ${shapeEmoji} = ${val1} and ${PUZZLE_SHAPES[shape2].emoji} = ${val2}, what is ${shapeEmoji} + ${PUZZLE_SHAPES[shape2].emoji}?`;
    answer = String(val1 + val2);
    options = [answer, String(val1 + val2 + 1), String(val1 + val2 - 1), String(val1 * val2)];
    hint = `Add the two values together: ${val1} + ${val2}`;
  } else {
    // Multiplication or complex
    const value = Math.floor(Math.random() * 4) + 2;
    const multiplier = Math.floor(Math.random() * 3) + 2;
    question = `If ${shapeEmoji} = ${value}, what is ${shapeEmoji} × ${multiplier}?`;
    answer = String(value * multiplier);
    options = [answer, String(value * multiplier + 1), String(value + multiplier), String(value * (multiplier + 1))];
    hint = `Multiply ${value} by ${multiplier}`;
  }
  
  return {
    type: 'math',
    question,
    options: options.sort(() => Math.random() - 0.5),
    answer,
    hint,
    sparks: 5 + difficulty * 3
  };
};

const generateOddOneOutPuzzle = (difficulty) => {
  const shapes = Object.keys(PUZZLE_SHAPES);
  const colors = Object.keys(PUZZLE_COLORS);
  
  const count = difficulty === 1 ? 4 : difficulty === 2 ? 5 : 6;
  const mainShape = shapes[Math.floor(Math.random() * shapes.length)];
  const mainColor = colors[Math.floor(Math.random() * colors.length)];
  
  // Decide what makes the odd one different
  const diffType = difficulty === 1 ? 'shape' : Math.random() > 0.5 ? 'shape' : 'color';
  
  const items = [];
  for (let i = 0; i < count; i++) {
    items.push({ shape: mainShape, color: mainColor });
  }
  
  // Make one different
  const oddIndex = Math.floor(Math.random() * count);
  if (diffType === 'shape') {
    const otherShapes = shapes.filter(s => s !== mainShape);
    items[oddIndex].shape = otherShapes[Math.floor(Math.random() * otherShapes.length)];
  } else {
    const otherColors = colors.filter(c => c !== mainColor);
    items[oddIndex].color = otherColors[Math.floor(Math.random() * otherColors.length)];
  }
  
  return {
    type: 'odd_one_out',
    question: 'Which one is different?',
    items,
    options: items.map((_, i) => `Position ${i + 1}`),
    answer: `Position ${oddIndex + 1}`,
    hint: diffType === 'shape' ? 'Look at the shapes carefully' : 'Look at the colors carefully',
    sparks: 5 + difficulty * 3
  };
};

const generateSequencePuzzle = (difficulty) => {
  let sequence, answer, hint;
  
  if (difficulty === 1) {
    // Simple counting: 2, 4, 6, 8, ?
    const start = Math.floor(Math.random() * 3) + 1;
    const step = Math.floor(Math.random() * 3) + 2;
    sequence = [];
    for (let i = 0; i < 4; i++) sequence.push(start + step * i);
    answer = String(start + step * 4);
    hint = `Each number increases by ${step}`;
  } else if (difficulty === 2) {
    // Doubles or squares
    const type = Math.random() > 0.5 ? 'double' : 'add_increasing';
    if (type === 'double') {
      const start = Math.floor(Math.random() * 3) + 1;
      sequence = [start, start * 2, start * 4, start * 8];
      answer = String(start * 16);
      hint = 'Each number doubles!';
    } else {
      const start = Math.floor(Math.random() * 3) + 1;
      sequence = [start, start + 1, start + 3, start + 6];
      answer = String(start + 10);
      hint = 'Look at how much is added each time (+1, +2, +3...)';
    }
  } else {
    // Fibonacci-like or complex
    const a = Math.floor(Math.random() * 3) + 1;
    const b = Math.floor(Math.random() * 3) + 2;
    sequence = [a, b, a + b, b + (a + b)];
    answer = String((a + b) + (b + (a + b)));
    hint = 'Each number is the sum of the two before it!';
  }
  
  const options = [answer];
  while (options.length < 4) {
    const wrong = String(Number(answer) + Math.floor(Math.random() * 7) - 3);
    if (!options.includes(wrong) && wrong !== answer) {
      options.push(wrong);
    }
  }
  
  return {
    type: 'sequence',
    question: `What comes next? ${sequence.join(', ')}, ?`,
    sequence,
    options: options.sort(() => Math.random() - 0.5),
    answer,
    hint,
    sparks: 5 + difficulty * 3
  };
};

// Master puzzle generator
const generatePuzzle = (solvedCount = 0) => {
  // Difficulty scales with puzzles solved
  const difficulty = Math.min(3, Math.floor(solvedCount / 3) + 1);
  
  const generators = [generatePatternPuzzle, generateMathPuzzle, generateOddOneOutPuzzle, generateSequencePuzzle];
  const generator = generators[Math.floor(Math.random() * generators.length)];
  
  return {
    ...generator(difficulty),
    id: Date.now(),
    difficulty
  };
};

// Constellation templates
const CONSTELLATIONS = [
  {
    id: 'big_dipper',
    name: 'Big Dipper',
    stars: 7,
    connections: [[0,1], [1,2], [2,3], [3,4], [4,5], [5,6], [6,3]],
    hint: 'It looks like a ladle or cup!',
    sparks: 15,
    // Educational content for learning mode
    lesson: {
      title: "The Big Dipper",
      facts: [
        "The Big Dipper is one of the most recognizable star patterns in the Northern sky!",
        "It's not actually a constellation - it's an 'asterism' (a pattern of stars within a larger constellation called Ursa Major).",
        "The two stars at the end of the 'bowl' point to Polaris, the North Star!",
        "People have used the Big Dipper to navigate for thousands of years."
      ],
      funFact: "In the UK, it's called 'The Plough'. In France, it's 'The Saucepan'!"
    },
    // Pre-defined star positions for the template (normalized 0-1)
    templateStars: [
      {x: 0.2, y: 0.3}, {x: 0.3, y: 0.25}, {x: 0.4, y: 0.3}, {x: 0.5, y: 0.4},
      {x: 0.6, y: 0.5}, {x: 0.7, y: 0.45}, {x: 0.65, y: 0.6}
    ]
  },
  {
    id: 'orion',
    name: 'Orion the Hunter',
    stars: 7,
    connections: [[0,1], [1,2], [3,4], [4,5], [1,4], [2,6], [0,6]],
    hint: 'Three stars make his belt!',
    sparks: 20,
    lesson: {
      title: "Orion the Hunter",
      facts: [
        "Orion is named after a legendary hunter from Greek mythology!",
        "The three stars in a row are called 'Orion's Belt' - they're among the brightest stars in the night sky.",
        "Below Orion's Belt, you can see the Orion Nebula - a nursery where new stars are being born!",
        "Betelgeuse, the red star at Orion's shoulder, is so huge that if it replaced our Sun, it would reach past Mars!"
      ],
      funFact: "Ancient Egyptians aligned the Great Pyramids with Orion's Belt!"
    },
    templateStars: [
      {x: 0.35, y: 0.2}, {x: 0.4, y: 0.35}, {x: 0.45, y: 0.5},
      {x: 0.3, y: 0.65}, {x: 0.5, y: 0.65}, {x: 0.6, y: 0.5},
      {x: 0.55, y: 0.2}
    ]
  },
  {
    id: 'triangle',
    name: 'Summer Triangle',
    stars: 3,
    connections: [[0,1], [1,2], [2,0]],
    hint: 'Connect three bright stars to form a shape!',
    sparks: 10,
    lesson: {
      title: "The Summer Triangle",
      facts: [
        "The Summer Triangle is made of three of the brightest stars in the summer sky!",
        "Each star belongs to a different constellation: Vega (Lyra), Deneb (Cygnus), and Altair (Aquila).",
        "Vega is one of the brightest stars we can see - ancient astronomers used it as a reference point!",
        "The Milky Way runs right through the middle of the Summer Triangle!"
      ],
      funFact: "Vega was the first star (besides the Sun) ever to be photographed!"
    },
    templateStars: [
      {x: 0.5, y: 0.2}, {x: 0.25, y: 0.7}, {x: 0.75, y: 0.65}
    ]
  }
];

// Generate static starfield once
const generateStarfield = (width, height, count = 80) => {
  const stars = [];
  // Use a seeded random for consistency
  let seed = 12345;
  const random = () => {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    return seed / 0x7fffffff;
  };
  
  for (let i = 0; i < count; i++) {
    stars.push({
      x: random() * width,
      y: random() * height,
      size: random() * 1.5 + 0.5,
      brightness: random() * 0.4 + 0.1,
      twinkleOffset: random() * Math.PI * 2
    });
  }
  return stars;
};

// Optimized Constellation Builder Component
const ConstellationBuilder = ({ constellation, onComplete, onBack }) => {
  const canvasRef = useRef(null);
  const bgCanvasRef = useRef(null);
  const animationRef = useRef(null);
  const lastDrawRef = useRef(0);
  
  const [placedStars, setPlacedStars] = useState([]);
  const [connections, setConnections] = useState([]);
  const [connectStart, setConnectStart] = useState(null);
  const [mousePos, setMousePos] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [sparkleParticles, setSparkleParticles] = useState([]);
  
  // Static starfield - memoized so it doesn't regenerate
  const starfield = useMemo(() => generateStarfield(800, 400, 80), []);
  
  // Draw background once on mount
  useEffect(() => {
    const bgCanvas = bgCanvasRef.current;
    if (!bgCanvas) return;
    
    const ctx = bgCanvas.getContext('2d');
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, bgCanvas.width, bgCanvas.height);
    
    // Draw static starfield
    starfield.forEach(star => {
      ctx.fillStyle = `rgba(255, 255, 255, ${star.brightness})`;
      ctx.beginPath();
      ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
      ctx.fill();
    });
  }, [starfield]);
  
  // Optimized draw function using requestAnimationFrame
  const draw = useCallback((timestamp) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Throttle to ~60fps
    if (timestamp - lastDrawRef.current < 16) {
      animationRef.current = requestAnimationFrame(draw);
      return;
    }
    lastDrawRef.current = timestamp;
    
    const ctx = canvas.getContext('2d');
    
    // Clear only the interactive layer
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw connections with glow effect
    connections.forEach(([start, end]) => {
      if (placedStars[start] && placedStars[end]) {
        const startStar = placedStars[start];
        const endStar = placedStars[end];
        
        // Outer glow
        ctx.strokeStyle = 'rgba(96, 165, 250, 0.3)';
        ctx.lineWidth = 6;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(startStar.x, startStar.y);
        ctx.lineTo(endStar.x, endStar.y);
        ctx.stroke();
        
        // Inner line
        ctx.strokeStyle = 'rgba(147, 197, 253, 0.9)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(startStar.x, startStar.y);
        ctx.lineTo(endStar.x, endStar.y);
        ctx.stroke();
      }
    });
    
    // Draw preview line when connecting
    if (connectStart !== null && mousePos && placedStars[connectStart]) {
      const startStar = placedStars[connectStart];
      
      // Dashed preview line
      ctx.strokeStyle = 'rgba(251, 191, 36, 0.6)';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(startStar.x, startStar.y);
      ctx.lineTo(mousePos.x, mousePos.y);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    
    // Draw stars
    placedStars.forEach((star, index) => {
      const isSelected = index === connectStart;
      const pulseIntensity = isSelected ? 1.2 + Math.sin(timestamp / 200) * 0.3 : 1;
      
      // Outer glow
      const gradient = ctx.createRadialGradient(star.x, star.y, 0, star.x, star.y, 25 * pulseIntensity);
      gradient.addColorStop(0, isSelected ? 'rgba(251, 191, 36, 0.6)' : 'rgba(255, 255, 200, 0.5)');
      gradient.addColorStop(0.5, isSelected ? 'rgba(251, 191, 36, 0.2)' : 'rgba(255, 255, 200, 0.2)');
      gradient.addColorStop(1, 'rgba(255, 255, 200, 0)');
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(star.x, star.y, 25 * pulseIntensity, 0, Math.PI * 2);
      ctx.fill();
      
      // Star core
      ctx.fillStyle = isSelected ? '#FBBF24' : '#FFFFFF';
      ctx.shadowColor = isSelected ? '#FBBF24' : '#FFFFFF';
      ctx.shadowBlur = 10;
      ctx.beginPath();
      ctx.arc(star.x, star.y, 6, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;
    });
    
    // Draw completion sparkles
    if (sparkleParticles.length > 0) {
      sparkleParticles.forEach((particle, i) => {
        const age = (timestamp - particle.birth) / 1000;
        if (age > 2) return;
        
        const alpha = Math.max(0, 1 - age / 2);
        const size = particle.size * (1 + age);
        
        ctx.fillStyle = `rgba(255, 215, 0, ${alpha})`;
        ctx.beginPath();
        ctx.arc(
          particle.x + Math.sin(age * particle.speed) * 20,
          particle.y + age * particle.speed * 30,
          size,
          0, Math.PI * 2
        );
        ctx.fill();
      });
      
      // Clean up old particles
      setSparkleParticles(prev => prev.filter(p => (timestamp - p.birth) / 1000 < 2));
    }
    
    // Continue animation if needed
    if (connectStart !== null || sparkleParticles.length > 0) {
      animationRef.current = requestAnimationFrame(draw);
    }
  }, [placedStars, connections, connectStart, mousePos, sparkleParticles]);
  
  // Start animation when state changes
  useEffect(() => {
    animationRef.current = requestAnimationFrame(draw);
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [draw]);
  
  // Handle canvas click
  const handleClick = useCallback((e) => {
    if (isComplete) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    
    // Check if clicking near existing star
    const clickedStar = placedStars.findIndex(star => 
      Math.hypot(star.x - x, star.y - y) < 25
    );
    
    if (clickedStar >= 0) {
      if (connectStart !== null && connectStart !== clickedStar) {
        // Complete connection
        const exists = connections.some(c => 
          (c[0] === connectStart && c[1] === clickedStar) ||
          (c[0] === clickedStar && c[1] === connectStart)
        );
        if (!exists) {
          setConnections(prev => [...prev, [connectStart, clickedStar]]);
        }
        setConnectStart(null);
      } else {
        // Start new connection
        setConnectStart(clickedStar);
      }
    } else if (placedStars.length < constellation.stars) {
      // Place new star
      setPlacedStars(prev => [...prev, { x, y }]);
      setConnectStart(null);
    }
  }, [placedStars, connections, connectStart, constellation.stars, isComplete]);
  
  // Throttled mouse move handler
  const handleMouseMove = useCallback((e) => {
    if (connectStart === null) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    setMousePos({
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    });
  }, [connectStart]);
  
  // Check completion
  const checkCompletion = useCallback(() => {
    if (connections.length >= constellation.connections.length) {
      setIsComplete(true);
      
      // Create sparkle particles
      const particles = [];
      placedStars.forEach(star => {
        for (let i = 0; i < 5; i++) {
          particles.push({
            x: star.x,
            y: star.y,
            size: Math.random() * 3 + 2,
            speed: Math.random() * 30 + 20,
            birth: performance.now()
          });
        }
      });
      setSparkleParticles(particles);
      
      toast.success(`Beautiful! You created ${constellation.name}! +${constellation.sparks} sparks!`);
      setTimeout(() => onComplete(constellation.sparks), 2000);
    } else {
      toast.info(`Keep connecting! ${constellation.connections.length - connections.length} more lines needed.`);
    }
  }, [connections, constellation, placedStars, onComplete]);
  
  // Reset function
  const reset = useCallback(() => {
    setPlacedStars([]);
    setConnections([]);
    setConnectStart(null);
    setMousePos(null);
    setIsComplete(false);
    setSparkleParticles([]);
  }, []);
  
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-white font-semibold">{constellation.name}</h3>
          <p className="text-slate-400 text-sm">
            Click to place {constellation.stars} stars, then click stars to connect them!
          </p>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-blue-400 text-sm">
            Stars: {placedStars.length}/{constellation.stars}
          </span>
          <span className="text-purple-400 text-sm">
            Lines: {connections.length}/{constellation.connections.length}
          </span>
        </div>
      </div>

      <div className="relative rounded-xl overflow-hidden border border-white/10">
        {/* Background layer (static) */}
        <canvas
          ref={bgCanvasRef}
          width={800}
          height={400}
          className="w-full"
          style={{ maxHeight: '400px' }}
        />
        {/* Interactive layer (dynamic) */}
        <canvas
          ref={canvasRef}
          width={800}
          height={400}
          onClick={handleClick}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setMousePos(null)}
          className="absolute top-0 left-0 w-full cursor-crosshair"
          style={{ maxHeight: '400px' }}
        />
      </div>

      <div className="flex items-center justify-between mt-4">
        <p className="text-slate-500 text-sm">
          {isComplete 
            ? '✨ Constellation complete!' 
            : connectStart !== null 
              ? 'Click another star to connect' 
              : 'Click to place a star or click an existing star to start connecting'
          }
        </p>
        <div className="flex gap-3">
          <button
            onClick={onBack}
            className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
          >
            ← Back
          </button>
          <button
            onClick={reset}
            className="px-4 py-2 bg-slate-800/50 hover:bg-slate-700/50 text-white rounded-lg transition-all flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Reset
          </button>
          <button
            onClick={checkCompletion}
            disabled={placedStars.length < constellation.stars || isComplete}
            className="px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 rounded-lg transition-all disabled:opacity-50"
          >
            {isComplete ? 'Complete!' : 'Check Constellation'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Stage indicators for learning flow
const STAGES = {
  LESSON: 'lesson',
  OBSERVATION: 'observation',
  PRACTICE: 'practice',
  CREATIVE: 'creative'
};

// Enhanced Constellation Learning Experience with 4 Stages
const ConstellationLearningFlow = ({ constellation, onComplete, onBack, childId }) => {
  const [stage, setStage] = useState(STAGES.LESSON);
  const [lessonFactIndex, setLessonFactIndex] = useState(0);
  const [practiceComplete, setPracticeComplete] = useState(false);
  const [creativeMode, setCreativeMode] = useState(false);
  const [customConstellation, setCustomConstellation] = useState(null);
  
  // Observation mode canvas refs
  const obsCanvasRef = useRef(null);
  const obsBgCanvasRef = useRef(null);
  const obsAnimRef = useRef(null);
  
  // Memoized starfield for observation
  const starfield = useMemo(() => generateStarfield(800, 400, 80), []);
  
  // Draw observation mode (shows the completed constellation)
  useEffect(() => {
    if (stage !== STAGES.OBSERVATION) return;
    
    const bgCanvas = obsBgCanvasRef.current;
    const canvas = obsCanvasRef.current;
    if (!bgCanvas || !canvas) return;
    
    const bgCtx = bgCanvas.getContext('2d');
    const ctx = canvas.getContext('2d');
    
    // Draw background
    bgCtx.fillStyle = '#0F172A';
    bgCtx.fillRect(0, 0, bgCanvas.width, bgCanvas.height);
    
    // Draw starfield
    starfield.forEach(star => {
      bgCtx.fillStyle = `rgba(255, 255, 255, ${star.brightness})`;
      bgCtx.beginPath();
      bgCtx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
      bgCtx.fill();
    });
    
    // Scale template stars to canvas size
    const templateStars = constellation.templateStars.map(s => ({
      x: s.x * 800,
      y: s.y * 400
    }));
    
    // Animate the constellation drawing
    let connectionIndex = 0;
    let starIndex = 0;
    let progress = 0;
    
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw completed connections
      for (let i = 0; i < connectionIndex; i++) {
        const [start, end] = constellation.connections[i];
        const startStar = templateStars[start];
        const endStar = templateStars[end];
        
        // Glow effect
        ctx.strokeStyle = 'rgba(96, 165, 250, 0.4)';
        ctx.lineWidth = 6;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(startStar.x, startStar.y);
        ctx.lineTo(endStar.x, endStar.y);
        ctx.stroke();
        
        // Inner line
        ctx.strokeStyle = 'rgba(147, 197, 253, 0.9)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(startStar.x, startStar.y);
        ctx.lineTo(endStar.x, endStar.y);
        ctx.stroke();
      }
      
      // Draw animating connection
      if (connectionIndex < constellation.connections.length) {
        const [start, end] = constellation.connections[connectionIndex];
        const startStar = templateStars[start];
        const endStar = templateStars[end];
        
        const currentX = startStar.x + (endStar.x - startStar.x) * progress;
        const currentY = startStar.y + (endStar.y - startStar.y) * progress;
        
        ctx.strokeStyle = 'rgba(251, 191, 36, 0.8)';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(startStar.x, startStar.y);
        ctx.lineTo(currentX, currentY);
        ctx.stroke();
        
        progress += 0.03;
        if (progress >= 1) {
          connectionIndex++;
          progress = 0;
        }
      }
      
      // Draw stars
      templateStars.forEach((star, i) => {
        const isActive = i <= Math.max(
          ...constellation.connections.slice(0, connectionIndex + 1).flat()
        );
        
        // Glow
        const gradient = ctx.createRadialGradient(star.x, star.y, 0, star.x, star.y, 20);
        gradient.addColorStop(0, isActive ? 'rgba(255, 255, 200, 0.6)' : 'rgba(255, 255, 200, 0.2)');
        gradient.addColorStop(1, 'rgba(255, 255, 200, 0)');
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(star.x, star.y, 20, 0, Math.PI * 2);
        ctx.fill();
        
        // Core
        ctx.fillStyle = isActive ? '#FFFFFF' : 'rgba(255, 255, 255, 0.4)';
        ctx.shadowColor = '#FFFFFF';
        ctx.shadowBlur = isActive ? 10 : 0;
        ctx.beginPath();
        ctx.arc(star.x, star.y, 5, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
      });
      
      if (connectionIndex < constellation.connections.length) {
        obsAnimRef.current = requestAnimationFrame(animate);
      }
    };
    
    animate();
    
    return () => {
      if (obsAnimRef.current) {
        cancelAnimationFrame(obsAnimRef.current);
      }
    };
  }, [stage, constellation, starfield]);
  
  // Handle practice completion
  const handlePracticeComplete = (sparks) => {
    setPracticeComplete(true);
    // Don't trigger onComplete yet - let user explore creative mode
  };
  
  // Handle final completion (from creative mode or skip)
  const handleFinalComplete = () => {
    onComplete(constellation.sparks);
  };
  
  // Render stage progress indicator
  const renderStageIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-6">
      {Object.values(STAGES).map((s, i) => (
        <React.Fragment key={s}>
          <div 
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
              stage === s 
                ? 'bg-blue-500 text-white scale-110' 
                : Object.values(STAGES).indexOf(stage) > i 
                  ? 'bg-green-500/30 text-green-400' 
                  : 'bg-slate-700 text-slate-500'
            }`}
          >
            {i + 1}
          </div>
          {i < Object.values(STAGES).length - 1 && (
            <div className={`w-8 h-0.5 ${
              Object.values(STAGES).indexOf(stage) > i ? 'bg-green-500/50' : 'bg-slate-700'
            }`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
  
  // STAGE 1: Mini-Lesson
  if (stage === STAGES.LESSON) {
    const lesson = constellation.lesson;
    const isLastFact = lessonFactIndex >= lesson.facts.length - 1;
    
    return (
      <div data-testid="constellation-lesson-stage">
        {renderStageIndicator()}
        
        <div className="text-center mb-6">
          <Star className="w-12 h-12 text-yellow-400 mx-auto mb-3" />
          <h3 className="text-2xl font-philosopher font-bold text-white mb-2">
            {lesson.title}
          </h3>
          <p className="text-slate-400">Learn about this constellation before you build it!</p>
        </div>
        
        <div className="bg-slate-900/50 rounded-xl p-6 mb-6 min-h-[120px]">
          <p className="text-white text-lg leading-relaxed">
            {lesson.facts[lessonFactIndex]}
          </p>
        </div>
        
        <div className="flex items-center justify-between mb-4">
          <div className="flex gap-1">
            {lesson.facts.map((_, i) => (
              <div 
                key={i} 
                className={`w-2 h-2 rounded-full transition-all ${
                  i === lessonFactIndex ? 'bg-blue-400 w-4' : i < lessonFactIndex ? 'bg-green-400' : 'bg-slate-600'
                }`}
              />
            ))}
          </div>
          <span className="text-slate-500 text-sm">
            {lessonFactIndex + 1} of {lesson.facts.length}
          </span>
        </div>
        
        {isLastFact && lesson.funFact && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-6">
            <p className="text-yellow-300 text-sm">
              <span className="font-semibold">Fun Fact:</span> {lesson.funFact}
            </p>
          </div>
        )}
        
        <div className="flex justify-between">
          <button
            onClick={onBack}
            className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
          >
            ← Back
          </button>
          <div className="flex gap-3">
            {lessonFactIndex > 0 && (
              <button
                onClick={() => setLessonFactIndex(prev => prev - 1)}
                className="px-4 py-2 bg-slate-800/50 text-slate-300 rounded-lg hover:bg-slate-700/50 transition-all"
              >
                Previous
              </button>
            )}
            <button
              onClick={() => {
                if (isLastFact) {
                  setStage(STAGES.OBSERVATION);
                } else {
                  setLessonFactIndex(prev => prev + 1);
                }
              }}
              className="px-6 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 rounded-lg transition-all"
            >
              {isLastFact ? 'See the Constellation →' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // STAGE 2: Observation Mode
  if (stage === STAGES.OBSERVATION) {
    return (
      <div data-testid="constellation-observation-stage">
        {renderStageIndicator()}
        
        <div className="text-center mb-4">
          <h3 className="text-xl font-philosopher font-bold text-white mb-2">
            Watch {constellation.name} Form
          </h3>
          <p className="text-slate-400 text-sm">
            Study how the stars connect. You'll recreate this pattern next!
          </p>
        </div>
        
        <div className="relative rounded-xl overflow-hidden border border-white/10 mb-4">
          <canvas
            ref={obsBgCanvasRef}
            width={800}
            height={400}
            className="w-full"
            style={{ maxHeight: '400px' }}
          />
          <canvas
            ref={obsCanvasRef}
            width={800}
            height={400}
            className="absolute top-0 left-0 w-full"
            style={{ maxHeight: '400px' }}
          />
        </div>
        
        <div className="bg-slate-900/50 rounded-xl p-4 mb-4">
          <p className="text-slate-300 text-sm">
            <span className="text-yellow-400 font-semibold">Tip:</span> {constellation.hint}
          </p>
        </div>
        
        <div className="flex justify-between">
          <button
            onClick={() => {
              setStage(STAGES.LESSON);
              setLessonFactIndex(0);
            }}
            className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
          >
            ← Review Lesson
          </button>
          <button
            onClick={() => setStage(STAGES.PRACTICE)}
            className="px-6 py-2 bg-green-500/20 hover:bg-green-500/30 text-green-300 rounded-lg transition-all"
          >
            I'm Ready to Try! →
          </button>
        </div>
      </div>
    );
  }
  
  // STAGE 3: Practice Mode
  if (stage === STAGES.PRACTICE && !practiceComplete) {
    return (
      <div data-testid="constellation-practice-stage">
        {renderStageIndicator()}
        
        <div className="text-center mb-4">
          <h3 className="text-xl font-philosopher font-bold text-white mb-2">
            Build {constellation.name}
          </h3>
          <p className="text-slate-400 text-sm">
            Now it's your turn! Place the stars and connect them.
          </p>
        </div>
        
        <ConstellationBuilder 
          constellation={constellation}
          onComplete={(sparks) => {
            setPracticeComplete(true);
          }}
          onBack={() => setStage(STAGES.OBSERVATION)}
        />
      </div>
    );
  }
  
  // STAGE 4: Creative Mode (after practice is complete)
  if (practiceComplete || stage === STAGES.CREATIVE) {
    return (
      <div data-testid="constellation-creative-stage">
        {renderStageIndicator()}
        
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-2xl font-philosopher font-bold text-white mb-2">
            Congratulations!
          </h3>
          <p className="text-slate-400">
            You've mastered {constellation.name}! You earned <span className="text-yellow-400 font-semibold">+{constellation.sparks} sparks</span>!
          </p>
        </div>
        
        <div className="bg-slate-900/50 rounded-xl p-6 mb-6">
          <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
            <Palette className="w-5 h-5 text-purple-400" />
            Creative Mode Unlocked!
          </h4>
          <p className="text-slate-400 text-sm mb-4">
            Now you can create your own constellation! Place stars anywhere and connect them however you like.
          </p>
          
          {!creativeMode ? (
            <div className="flex gap-3">
              <button
                onClick={() => setCreativeMode(true)}
                className="flex-1 px-4 py-3 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 rounded-lg transition-all"
                data-testid="enter-creative-mode"
              >
                Create My Own Constellation
              </button>
              <button
                onClick={handleFinalComplete}
                className="px-4 py-3 bg-green-500/20 hover:bg-green-500/30 text-green-300 rounded-lg transition-all"
                data-testid="finish-constellation"
              >
                Finish & Return
              </button>
            </div>
          ) : (
            <CreativeConstellationBuilder 
              childId={childId}
              onSave={(name, savedId) => {
                setCustomConstellation(name);
                toast.success(`Your constellation "${name}" has been saved to the Temple Sky! +5 sparks!`);
              }}
              onBack={() => setCreativeMode(false)}
            />
          )}
        </div>
        
        {creativeMode && (
          <div className="flex justify-center">
            <button
              onClick={handleFinalComplete}
              className="px-6 py-2 bg-slate-800/50 hover:bg-slate-700/50 text-white rounded-lg transition-all"
            >
              Done Creating
            </button>
          </div>
        )}
      </div>
    );
  }
  
  return null;
};

// Creative Mode - Free-form constellation creation
const CreativeConstellationBuilder = ({ childId, onSave, onBack }) => {
  const canvasRef = useRef(null);
  const bgCanvasRef = useRef(null);
  const [stars, setStars] = useState([]);
  const [connections, setConnections] = useState([]);
  const [connectStart, setConnectStart] = useState(null);
  const [constellationName, setConstellationName] = useState('');
  const [showNameInput, setShowNameInput] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const starfield = useMemo(() => generateStarfield(800, 300, 60), []);
  
  // Draw background
  useEffect(() => {
    const bgCanvas = bgCanvasRef.current;
    if (!bgCanvas) return;
    
    const ctx = bgCanvas.getContext('2d');
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, bgCanvas.width, bgCanvas.height);
    
    starfield.forEach(star => {
      ctx.fillStyle = `rgba(255, 255, 255, ${star.brightness})`;
      ctx.beginPath();
      ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
      ctx.fill();
    });
  }, [starfield]);
  
  // Draw interactive layer
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw connections
    connections.forEach(([start, end]) => {
      if (stars[start] && stars[end]) {
        ctx.strokeStyle = 'rgba(167, 139, 250, 0.4)';
        ctx.lineWidth = 5;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(stars[start].x, stars[start].y);
        ctx.lineTo(stars[end].x, stars[end].y);
        ctx.stroke();
        
        ctx.strokeStyle = 'rgba(196, 181, 253, 0.9)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(stars[start].x, stars[start].y);
        ctx.lineTo(stars[end].x, stars[end].y);
        ctx.stroke();
      }
    });
    
    // Draw stars
    stars.forEach((star, i) => {
      const isSelected = i === connectStart;
      
      const gradient = ctx.createRadialGradient(star.x, star.y, 0, star.x, star.y, 18);
      gradient.addColorStop(0, isSelected ? 'rgba(251, 191, 36, 0.6)' : 'rgba(196, 181, 253, 0.5)');
      gradient.addColorStop(1, 'rgba(196, 181, 253, 0)');
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(star.x, star.y, 18, 0, Math.PI * 2);
      ctx.fill();
      
      ctx.fillStyle = isSelected ? '#FBBF24' : '#C4B5FD';
      ctx.shadowColor = isSelected ? '#FBBF24' : '#C4B5FD';
      ctx.shadowBlur = 8;
      ctx.beginPath();
      ctx.arc(star.x, star.y, 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;
    });
  }, [stars, connections, connectStart]);
  
  const handleClick = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    
    // Check if clicking existing star
    const clickedStar = stars.findIndex(star => 
      Math.hypot(star.x - x, star.y - y) < 20
    );
    
    if (clickedStar >= 0) {
      if (connectStart !== null && connectStart !== clickedStar) {
        const exists = connections.some(c => 
          (c[0] === connectStart && c[1] === clickedStar) ||
          (c[0] === clickedStar && c[1] === connectStart)
        );
        if (!exists) {
          setConnections(prev => [...prev, [connectStart, clickedStar]]);
        }
        setConnectStart(null);
      } else {
        setConnectStart(clickedStar);
      }
    } else if (stars.length < 12) {
      setStars(prev => [...prev, { x, y }]);
      setConnectStart(null);
    }
  };
  
  const handleSave = async () => {
    if (stars.length < 3 || connections.length < 2) {
      toast.error('Add at least 3 stars and 2 connections!');
      return;
    }
    if (!constellationName.trim()) {
      toast.error('Give your constellation a name!');
      return;
    }
    
    if (!childId) {
      toast.error('Unable to save - no child selected');
      return;
    }
    
    setSaving(true);
    try {
      // Normalize star positions to 0-100 range for SVG viewBox rendering
      const normalizedStars = stars.map(s => ({
        x: (s.x / 800) * 100,  // Canvas width -> 0-100 range
        y: (s.y / 300) * 100   // Canvas height -> 0-100 range
      }));
      
      const response = await constellationsAPI.save(childId, {
        name: constellationName.trim(),
        stars: normalizedStars,
        connections: connections
      });
      
      onSave(constellationName, response.data.id);
      setShowNameInput(false);
    } catch (error) {
      console.error('Error saving constellation:', error);
      toast.error('Failed to save constellation. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div>
      <div className="relative rounded-xl overflow-hidden border border-purple-500/30 mb-4">
        <canvas
          ref={bgCanvasRef}
          width={800}
          height={300}
          className="w-full"
        />
        <canvas
          ref={canvasRef}
          width={800}
          height={300}
          onClick={handleClick}
          className="absolute top-0 left-0 w-full cursor-crosshair"
        />
      </div>
      
      <div className="flex items-center justify-between text-sm text-slate-400 mb-4">
        <span>Stars: {stars.length}/12 • Lines: {connections.length}</span>
        <button
          onClick={() => {
            setStars([]);
            setConnections([]);
            setConnectStart(null);
          }}
          className="text-slate-500 hover:text-white"
        >
          Clear All
        </button>
      </div>
      
      {showNameInput ? (
        <div className="flex gap-2">
          <input
            type="text"
            value={constellationName}
            onChange={(e) => setConstellationName(e.target.value)}
            placeholder="Name your constellation..."
            className="flex-1 px-4 py-2 bg-slate-900/50 border border-white/10 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:border-purple-500/50"
            autoFocus
            disabled={saving}
          />
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 rounded-lg disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save to Temple Sky'}
          </button>
        </div>
      ) : (
        <button
          onClick={() => setShowNameInput(true)}
          disabled={stars.length < 3 || connections.length < 2}
          className="w-full px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Name & Save My Constellation
        </button>
      )}
    </div>
  );
};

// ============ MY CONSTELLATIONS GALLERY ============

const MyConstellationsGallery = ({ childId, onBack }) => {
  const [constellations, setConstellations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConstellation, setSelectedConstellation] = useState(null);
  const [editingName, setEditingName] = useState(null);
  const [newName, setNewName] = useState('');
  const canvasRef = useRef(null);
  const bgCanvasRef = useRef(null);
  
  const starfield = useMemo(() => generateStarfield(600, 300, 50), []);

  useEffect(() => {
    if (childId) {
      loadConstellations();
    }
  }, [childId]);

  const loadConstellations = async () => {
    try {
      setLoading(true);
      const response = await constellationsAPI.getAll(childId);
      setConstellations(response.data || []);
    } catch (error) {
      console.error('Error loading constellations:', error);
      toast.error('Failed to load constellations');
    } finally {
      setLoading(false);
    }
  };

  // Draw selected constellation
  useEffect(() => {
    if (!selectedConstellation) return;
    
    const bgCanvas = bgCanvasRef.current;
    const canvas = canvasRef.current;
    if (!bgCanvas || !canvas) return;
    
    const bgCtx = bgCanvas.getContext('2d');
    const ctx = canvas.getContext('2d');
    
    // Background
    bgCtx.fillStyle = '#0F172A';
    bgCtx.fillRect(0, 0, 600, 300);
    starfield.forEach(star => {
      bgCtx.fillStyle = `rgba(255, 255, 255, ${star.brightness})`;
      bgCtx.beginPath();
      bgCtx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
      bgCtx.fill();
    });
    
    // Clear interactive layer
    ctx.clearRect(0, 0, 600, 300);
    
    // Scale stars from normalized (0-1) to canvas size
    const stars = selectedConstellation.stars.map(s => ({
      x: s.x * 600,
      y: s.y * 300
    }));
    
    // Draw connections
    selectedConstellation.connections.forEach(([start, end]) => {
      if (stars[start] && stars[end]) {
        ctx.strokeStyle = 'rgba(147, 51, 234, 0.4)';
        ctx.lineWidth = 5;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(stars[start].x, stars[start].y);
        ctx.lineTo(stars[end].x, stars[end].y);
        ctx.stroke();
        
        ctx.strokeStyle = 'rgba(196, 181, 253, 0.9)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(stars[start].x, stars[start].y);
        ctx.lineTo(stars[end].x, stars[end].y);
        ctx.stroke();
      }
    });
    
    // Draw stars
    stars.forEach(star => {
      const gradient = ctx.createRadialGradient(star.x, star.y, 0, star.x, star.y, 15);
      gradient.addColorStop(0, 'rgba(196, 181, 253, 0.6)');
      gradient.addColorStop(1, 'rgba(196, 181, 253, 0)');
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(star.x, star.y, 15, 0, Math.PI * 2);
      ctx.fill();
      
      ctx.fillStyle = '#C4B5FD';
      ctx.shadowColor = '#C4B5FD';
      ctx.shadowBlur = 8;
      ctx.beginPath();
      ctx.arc(star.x, star.y, 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;
    });
  }, [selectedConstellation, starfield]);

  const handleRename = async (id) => {
    if (!newName.trim()) return;
    
    try {
      await constellationsAPI.rename(childId, id, newName.trim());
      toast.success('Constellation renamed!');
      setEditingName(null);
      loadConstellations();
    } catch (error) {
      toast.error('Failed to rename');
    }
  };

  const handleDelete = async (id, name) => {
    if (!confirm(`Delete "${name}"? This cannot be undone.`)) return;
    
    try {
      await constellationsAPI.delete(childId, id);
      toast.success('Constellation deleted');
      setSelectedConstellation(null);
      loadConstellations();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  if (loading) {
    return (
      <div className="glass-panel p-8 rounded-2xl text-center">
        <div className="animate-spin w-8 h-8 border-2 border-white/20 border-t-indigo-500 rounded-full mx-auto mb-4"></div>
        <p className="text-slate-400">Loading your constellations...</p>
      </div>
    );
  }

  return (
    <div className="glass-panel p-8 rounded-2xl">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-philosopher font-bold text-white flex items-center gap-3">
          <Eye className="w-7 h-7 text-indigo-400" />
          My Constellations
        </h2>
        <button
          onClick={onBack}
          className="text-slate-400 hover:text-white transition-colors"
        >
          ← Back
        </button>
      </div>

      {selectedConstellation ? (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              {editingName === selectedConstellation.id ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    className="px-3 py-1 bg-slate-900/50 border border-white/10 rounded-lg text-white text-lg"
                    autoFocus
                  />
                  <button
                    onClick={() => handleRename(selectedConstellation.id)}
                    className="text-green-400 hover:text-green-300"
                  >
                    <Check className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setEditingName(null)}
                    className="text-slate-400 hover:text-white"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <>
                  <h3 className="text-xl font-semibold text-white">{selectedConstellation.name}</h3>
                  <button
                    onClick={() => {
                      setEditingName(selectedConstellation.id);
                      setNewName(selectedConstellation.name);
                    }}
                    className="text-slate-400 hover:text-white"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleDelete(selectedConstellation.id, selectedConstellation.name)}
                className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg"
              >
                <Trash2 className="w-5 h-5" />
              </button>
              <button
                onClick={() => setSelectedConstellation(null)}
                className="text-slate-400 hover:text-white"
              >
                Close
              </button>
            </div>
          </div>
          
          <div className="relative rounded-xl overflow-hidden border border-indigo-500/30">
            <canvas ref={bgCanvasRef} width={600} height={300} className="w-full" />
            <canvas ref={canvasRef} width={600} height={300} className="absolute top-0 left-0 w-full" />
          </div>
          
          <p className="text-slate-500 text-sm mt-3">
            Created: {new Date(selectedConstellation.created_at).toLocaleDateString()}
          </p>
        </div>
      ) : constellations.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {constellations.map((constellation) => (
            <button
              key={constellation.id}
              onClick={() => setSelectedConstellation(constellation)}
              className="p-4 bg-slate-900/50 hover:bg-indigo-500/10 border border-white/10 hover:border-indigo-500/30 rounded-xl text-left transition-all"
              data-testid={`saved-constellation-${constellation.id}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Star className="w-5 h-5 text-indigo-400" />
                <h3 className="text-white font-semibold truncate">{constellation.name}</h3>
              </div>
              <p className="text-slate-500 text-xs">
                {constellation.stars?.length || 0} stars • {constellation.connections?.length || 0} lines
              </p>
              <p className="text-slate-600 text-xs mt-1">
                {new Date(constellation.created_at).toLocaleDateString()}
              </p>
            </button>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Star className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h3 className="text-xl text-slate-400 mb-2">No Constellations Yet</h3>
          <p className="text-slate-500">
            Create your first constellation in the Constellation Builder!
          </p>
        </div>
      )}
    </div>
  );
};

// ============ BEAT FORGE ============

const INSTRUMENTS = [
  { id: 'kick', name: 'Kick', freq: 60, type: 'sine', decay: 0.3, color: '#EF4444' },
  { id: 'snare', name: 'Snare', freq: 200, type: 'triangle', decay: 0.15, color: '#F59E0B' },
  { id: 'hihat', name: 'Hi-Hat', freq: 800, type: 'square', decay: 0.05, color: '#10B981' },
  { id: 'tom', name: 'Tom', freq: 100, type: 'sine', decay: 0.2, color: '#3B82F6' },
  { id: 'clap', name: 'Clap', freq: 400, type: 'sawtooth', decay: 0.1, color: '#8B5CF6' },
  { id: 'bass', name: 'Bass', freq: 80, type: 'sine', decay: 0.4, color: '#EC4899' },
  { id: 'bell', name: 'Bell', freq: 600, type: 'sine', decay: 0.5, color: '#14B8A6' },
  { id: 'click', name: 'Click', freq: 1200, type: 'square', decay: 0.02, color: '#F97316' }
];

const BeatForge = ({ childId, onComplete, onBack, onGoToSongForge }) => {
  const [tracks, setTracks] = useState(
    INSTRUMENTS.map(inst => ({
      instrument: inst.id,
      pattern: Array(16).fill(false)
    }))
  );
  const [tempo, setTempo] = useState(120);
  const [playing, setPlaying] = useState(false);
  const [currentStep, setCurrentStep] = useState(-1);
  const [beatName, setBeatName] = useState('');
  const [showSave, setShowSave] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedBeats, setSavedBeats] = useState([]);
  const [showSavedBeats, setShowSavedBeats] = useState(false);
  const [lastSavedBeat, setLastSavedBeat] = useState(null); // Track last saved beat
  
  const audioContextRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (childId) {
      loadSavedBeats();
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (audioContextRef.current) audioContextRef.current.close();
    };
  }, [childId]);

  const loadSavedBeats = async () => {
    try {
      const response = await beatsAPI.getAll(childId);
      setSavedBeats(response.data || []);
    } catch (error) {
      console.error('Error loading beats:', error);
    }
  };

  const getAudioContext = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    // Resume if suspended (browsers require user interaction)
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
    return audioContextRef.current;
  };

  const playSound = (instrumentId) => {
    const ctx = getAudioContext();
    const instrument = INSTRUMENTS.find(i => i.id === instrumentId);
    if (!instrument) return;

    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.type = instrument.type;
    oscillator.frequency.setValueAtTime(instrument.freq, ctx.currentTime);
    
    gainNode.gain.setValueAtTime(0.5, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + instrument.decay);
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + instrument.decay);
  };

  const toggleStep = (trackIndex, stepIndex) => {
    setTracks(prev => {
      const newTracks = [...prev];
      newTracks[trackIndex] = {
        ...newTracks[trackIndex],
        pattern: newTracks[trackIndex].pattern.map((v, i) => i === stepIndex ? !v : v)
      };
      return newTracks;
    });
  };

  const togglePlayback = () => {
    if (playing) {
      setPlaying(false);
      setCurrentStep(-1);
      if (intervalRef.current) clearInterval(intervalRef.current);
    } else {
      setPlaying(true);
      const stepDuration = (60 / tempo) * 1000 / 4; // 16th notes
      let step = 0;
      
      intervalRef.current = setInterval(() => {
        setCurrentStep(step);
        
        // Play sounds for current step
        tracks.forEach(track => {
          if (track.pattern[step]) {
            playSound(track.instrument);
          }
        });
        
        step = (step + 1) % 16;
      }, stepDuration);
    }
  };

  const handleSave = async () => {
    if (!beatName.trim()) {
      toast.error('Give your beat a name!');
      return;
    }
    
    // Check if any steps are active
    const hasNotes = tracks.some(t => t.pattern.some(p => p));
    if (!hasNotes) {
      toast.error('Add some beats first!');
      return;
    }
    
    setSaving(true);
    try {
      const response = await beatsAPI.save(childId, {
        name: beatName.trim(),
        tempo,
        tracks
      });
      
      // Track the last saved beat for Song Forge
      setLastSavedBeat({
        id: response.data.id,
        name: beatName.trim(),
        tempo,
        tracks
      });
      
      toast.success(`Beat saved! +${response.data.sparks_earned} creativity sparks!`);
      onComplete(response.data.sparks_earned);
      setBeatName('');
      setShowSave(false);
      loadSavedBeats();
    } catch (error) {
      toast.error('Failed to save beat');
    } finally {
      setSaving(false);
    }
  };

  const loadBeat = (beat) => {
    setTracks(beat.tracks);
    setTempo(beat.tempo);
    setShowSavedBeats(false);
    toast.success(`Loaded "${beat.name}"`);
  };

  const clearAll = () => {
    setTracks(INSTRUMENTS.map(inst => ({
      instrument: inst.id,
      pattern: Array(16).fill(false)
    })));
    setPlaying(false);
    setCurrentStep(-1);
    if (intervalRef.current) clearInterval(intervalRef.current);
  };

  return (
    <div className="glass-panel p-3 sm:p-6 rounded-2xl">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl font-philosopher font-bold text-white flex items-center gap-2 sm:gap-3">
          <Music className="w-5 h-5 sm:w-7 sm:h-7 text-pink-400" />
          Beat Forge
        </h2>
        <button onClick={onBack} className="text-slate-400 hover:text-white transition-colors text-sm sm:text-base">
          ← Back
        </button>
      </div>

      {/* Controls - Stack on mobile */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4 sm:mb-6 p-3 sm:p-4 bg-slate-900/50 rounded-xl">
        <div className="flex items-center gap-3 sm:gap-4">
          <button
            onClick={togglePlayback}
            className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all ${
              playing ? 'bg-red-500 hover:bg-red-600' : 'bg-pink-500 hover:bg-pink-600'
            }`}
            data-testid="play-pause-btn"
          >
            {playing ? <Pause className="w-5 h-5 sm:w-6 sm:h-6 text-white" /> : <Play className="w-5 h-5 sm:w-6 sm:h-6 text-white ml-0.5" />}
          </button>
          <div className="flex items-center gap-2">
            <span className="text-slate-400 text-xs sm:text-sm">Tempo:</span>
            <input
              type="range"
              min="60"
              max="140"
              value={tempo}
              onChange={(e) => setTempo(Number(e.target.value))}
              className="w-16 sm:w-24 accent-pink-500"
            />
            <span className="text-white text-xs sm:text-sm w-10 sm:w-12">{tempo}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setShowSavedBeats(!showSavedBeats)}
            className="px-2 sm:px-3 py-1.5 sm:py-2 bg-slate-800/50 hover:bg-slate-700/50 text-slate-300 rounded-lg text-xs sm:text-sm"
          >
            My Beats ({savedBeats.length})
          </button>
          <button
            onClick={clearAll}
            className="px-2 sm:px-3 py-1.5 sm:py-2 bg-slate-800/50 hover:bg-slate-700/50 text-slate-300 rounded-lg text-xs sm:text-sm"
          >
            Clear
          </button>
          <button
            onClick={() => setShowSave(true)}
            className="px-2 sm:px-3 py-1.5 sm:py-2 bg-pink-500/20 hover:bg-pink-500/30 text-pink-300 rounded-lg text-xs sm:text-sm"
          >
            Save Beat
          </button>
        </div>
      </div>

      {/* Saved Beats Dropdown */}
      {showSavedBeats && savedBeats.length > 0 && (
        <div className="mb-4 p-3 sm:p-4 bg-slate-900/50 rounded-xl">
          <h3 className="text-white font-semibold mb-3 text-sm sm:text-base">Your Saved Beats</h3>
          <div className="flex flex-wrap gap-2">
            {savedBeats.map(beat => (
              <button
                key={beat.id}
                onClick={() => loadBeat(beat)}
                className="px-2 sm:px-3 py-1 sm:py-1.5 bg-pink-500/20 hover:bg-pink-500/30 text-pink-300 rounded-lg text-xs sm:text-sm"
              >
                {beat.name} ({beat.tempo} BPM)
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Save Modal */}
      {showSave && (
        <div className="mb-4 p-3 sm:p-4 bg-slate-900/50 rounded-xl">
          <h3 className="text-white font-semibold mb-3 text-sm sm:text-base">Save Your Beat</h3>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              value={beatName}
              onChange={(e) => setBeatName(e.target.value)}
              placeholder="Name your beat..."
              className="flex-1 px-3 sm:px-4 py-2 bg-slate-900/50 border border-white/10 rounded-lg text-white text-sm"
              disabled={saving}
            />
            <div className="flex gap-2">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-pink-500/20 hover:bg-pink-500/30 text-pink-300 rounded-lg disabled:opacity-50 text-xs sm:text-sm"
              >
                {saving ? 'Saving...' : 'Save +10 ✨'}
              </button>
              <button
                onClick={() => setShowSave(false)}
                className="px-3 sm:px-4 py-2 text-slate-400 hover:text-white text-xs sm:text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Beat Grid - Scrollable on mobile */}
      <div className="space-y-1.5 sm:space-y-2 overflow-x-auto pb-2">
        {/* Step Numbers */}
        <div className="flex items-center gap-0.5 sm:gap-1 ml-14 sm:ml-20 min-w-max">
          {Array(16).fill(0).map((_, i) => (
            <div
              key={i}
              className={`w-6 sm:w-8 h-5 sm:h-6 flex items-center justify-center text-[10px] sm:text-xs ${
                currentStep === i ? 'text-pink-400 font-bold' : 'text-slate-600'
              } ${i % 4 === 0 ? 'text-slate-400' : ''}`}
            >
              {i + 1}
            </div>
          ))}
        </div>
        
        {/* Tracks */}
        {tracks.map((track, trackIndex) => {
          const instrument = INSTRUMENTS.find(i => i.id === track.instrument);
          return (
            <div key={track.instrument} className="flex items-center gap-0.5 sm:gap-1 min-w-max">
              <button
                onClick={() => playSound(track.instrument)}
                className="w-14 sm:w-20 px-1 sm:px-2 py-1.5 sm:py-2 text-left text-[10px] sm:text-sm font-medium rounded-lg hover:bg-white/5 transition-colors truncate"
                style={{ color: instrument.color }}
              >
                {instrument.name}
              </button>
              {track.pattern.map((active, stepIndex) => (
                <button
                  key={stepIndex}
                  onClick={() => toggleStep(trackIndex, stepIndex)}
                  className={`w-6 h-6 sm:w-8 sm:h-8 rounded transition-all ${
                    stepIndex % 4 === 0 ? 'ml-0.5 sm:ml-1' : ''
                  } ${
                    active 
                      ? 'scale-95' 
                      : 'bg-slate-800/50 hover:bg-slate-700/50'
                  } ${
                    currentStep === stepIndex && active ? 'ring-2 ring-white' : ''
                  }`}
                  style={{ 
                    backgroundColor: active ? instrument.color : undefined,
                    opacity: active ? (currentStep === stepIndex ? 1 : 0.8) : undefined
                  }}
                  data-testid={`beat-${track.instrument}-${stepIndex}`}
                />
              ))}
            </div>
          );
        })}
      </div>

      <p className="text-slate-500 text-xs sm:text-sm mt-3 sm:mt-4 text-center">
        Click pads to add beats • Click instrument names to preview sounds
      </p>
      
      {/* Take to Song Forge - shown after saving a beat */}
      {lastSavedBeat && onGoToSongForge && (
        <div className="mt-4 p-3 sm:p-4 bg-fuchsia-500/10 border border-fuchsia-500/30 rounded-xl">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-center sm:text-left">
              <p className="text-fuchsia-300 font-medium text-sm">"{lastSavedBeat.name}" saved!</p>
              <p className="text-slate-400 text-xs">Add your voice to make it a song</p>
            </div>
            <button
              onClick={() => onGoToSongForge(lastSavedBeat)}
              className="px-4 py-2 bg-fuchsia-500 hover:bg-fuchsia-600 text-white rounded-lg flex items-center gap-2 text-sm font-medium transition-all"
              data-testid="take-to-song-forge-btn"
            >
              <Mic className="w-4 h-4" />
              Take to Song Forge
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ============ SONG FORGE ============

const SongForge = ({ childId, onComplete, onBack, initialBeat }) => {
  const [savedBeats, setSavedBeats] = useState([]);
  const [selectedBeat, setSelectedBeat] = useState(initialBeat || null);
  const [savedSongs, setSavedSongs] = useState([]);
  const [showSavedSongs, setShowSavedSongs] = useState(false);
  
  // Duration selection
  const [songDuration, setSongDuration] = useState(30); // 15, 30, or 60 seconds
  const DURATION_OPTIONS = [
    { value: 15, label: '15 sec', emoji: '⚡' },
    { value: 30, label: '30 sec', emoji: '🎵' },
    { value: 60, label: '60 sec', emoji: '🎶' }
  ];
  
  // Recording states - Lead vocals
  const [isRecording, setIsRecording] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [vocalBlob, setVocalBlob] = useState(null);
  const [vocalUrl, setVocalUrl] = useState(null);
  
  // Recording states - Second vocal layer (backing/ad-lib)
  const [secondVocalBlob, setSecondVocalBlob] = useState(null);
  const [secondVocalUrl, setSecondVocalUrl] = useState(null);
  const [isRecordingSecond, setIsRecordingSecond] = useState(false);
  const [secondVocalType, setSecondVocalType] = useState('backing'); // 'backing' | 'adlib' | 'intro'
  const [showSecondVocal, setShowSecondVocal] = useState(false);
  
  // Playback states
  const [isPlaying, setIsPlaying] = useState(false);
  const [playMode, setPlayMode] = useState('combined'); // 'beat' | 'combined'
  const [currentStep, setCurrentStep] = useState(-1);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [recordingProgress, setRecordingProgress] = useState(0);
  
  // Save states
  const [showSave, setShowSave] = useState(false);
  const [songName, setSongName] = useState('');
  const [saving, setSaving] = useState(false);
  
  const audioContextRef = useRef(null);
  const beatIntervalRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const secondMediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const secondAudioChunksRef = useRef([]);
  const vocalAudioRef = useRef(null);
  const secondVocalAudioRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const playbackTimerRef = useRef(null);
  
  useEffect(() => {
    if (childId) {
      loadSavedBeats();
      loadSavedSongs();
    }
    return () => {
      stopAll();
      if (vocalUrl) URL.revokeObjectURL(vocalUrl);
    };
  }, [childId]);
  
  useEffect(() => {
    if (initialBeat) {
      setSelectedBeat(initialBeat);
    }
  }, [initialBeat]);
  
  const loadSavedBeats = async () => {
    try {
      const response = await beatsAPI.getAll(childId);
      setSavedBeats(response.data || []);
    } catch (error) {
      console.error('Error loading beats:', error);
    }
  };
  
  const loadSavedSongs = async () => {
    try {
      const response = await songsAPI.getAll(childId);
      setSavedSongs(response.data || []);
    } catch (error) {
      console.error('Error loading songs:', error);
    }
  };
  
  const getAudioContext = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    // Resume if suspended (browsers require user interaction)
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
    return audioContextRef.current;
  };
  
  const playBeatSound = (instrumentId) => {
    const ctx = getAudioContext();
    const instrument = INSTRUMENTS.find(i => i.id === instrumentId);
    if (!instrument) return;
    
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.type = instrument.type;
    oscillator.frequency.setValueAtTime(instrument.freq, ctx.currentTime);
    gainNode.gain.setValueAtTime(0.5, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + instrument.decay);
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + instrument.decay);
  };
  
  const startBeatPlayback = () => {
    if (!selectedBeat) return;
    
    const stepDuration = (60 / selectedBeat.tempo) * 1000 / 4;
    let step = 0;
    
    beatIntervalRef.current = setInterval(() => {
      setCurrentStep(step);
      
      selectedBeat.tracks.forEach(track => {
        if (track.pattern[step]) {
          playBeatSound(track.instrument);
        }
      });
      
      step = (step + 1) % 16;
    }, stepDuration);
  };
  
  const stopBeatPlayback = () => {
    if (beatIntervalRef.current) {
      clearInterval(beatIntervalRef.current);
      beatIntervalRef.current = null;
    }
    setCurrentStep(-1);
  };
  
  const stopAll = () => {
    stopBeatPlayback();
    setIsPlaying(false);
    setIsRecording(false);
    setIsRecordingSecond(false);
    setRecordingProgress(0);
    setElapsedTime(0);
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    if (playbackTimerRef.current) {
      clearInterval(playbackTimerRef.current);
      playbackTimerRef.current = null;
    }
    if (vocalAudioRef.current) {
      vocalAudioRef.current.pause();
      vocalAudioRef.current.currentTime = 0;
    }
    if (secondVocalAudioRef.current) {
      secondVocalAudioRef.current.pause();
      secondVocalAudioRef.current.currentTime = 0;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
  };
  
  const startCountdownAndRecord = async (isSecondVocal = false) => {
    if (!selectedBeat) {
      toast.error('Select a beat first!');
      return;
    }
    
    // Pre-warm the AudioContext (must happen on user gesture)
    const ctx = getAudioContext();
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }
    
    // Request microphone permission
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Countdown
      setCountdown(3);
      await new Promise(r => setTimeout(r, 1000));
      setCountdown(2);
      await new Promise(r => setTimeout(r, 1000));
      setCountdown(1);
      await new Promise(r => setTimeout(r, 1000));
      setCountdown(null);
      
      // Start recording
      const mediaRecorder = new MediaRecorder(stream);
      const chunksRef = isSecondVocal ? secondAudioChunksRef : audioChunksRef;
      
      if (isSecondVocal) {
        secondMediaRecorderRef.current = mediaRecorder;
        setIsRecordingSecond(true);
      } else {
        mediaRecorderRef.current = mediaRecorder;
        setIsRecording(true);
      }
      chunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const url = URL.createObjectURL(blob);
        
        if (isSecondVocal) {
          if (secondVocalUrl) URL.revokeObjectURL(secondVocalUrl);
          setSecondVocalBlob(blob);
          setSecondVocalUrl(url);
          setIsRecordingSecond(false);
          toast.success('Second vocal recorded!');
        } else {
          if (vocalUrl) URL.revokeObjectURL(vocalUrl);
          setVocalBlob(blob);
          setVocalUrl(url);
          setIsRecording(false);
          toast.success('Lead vocals recorded! Click Play to hear your song.');
        }
        
        stream.getTracks().forEach(track => track.stop());
        stopBeatPlayback();
        setRecordingProgress(0);
        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current);
          recordingTimerRef.current = null;
        }
      };
      
      // Start beat playback and recording together
      startBeatPlayback();
      mediaRecorder.start();
      
      // Progress timer
      const startTime = Date.now();
      setRecordingProgress(0);
      recordingTimerRef.current = setInterval(() => {
        const elapsed = (Date.now() - startTime) / 1000;
        const progress = Math.min((elapsed / songDuration) * 100, 100);
        setRecordingProgress(progress);
      }, 100);
      
      // Auto-stop after selected duration (beat loops automatically)
      setTimeout(() => {
        const recorder = isSecondVocal ? secondMediaRecorderRef.current : mediaRecorderRef.current;
        if (recorder && recorder.state === 'recording') {
          recorder.stop();
        }
      }, songDuration * 1000);
      
    } catch (error) {
      console.error('Microphone error:', error);
      setCountdown(null);
      if (error.name === 'NotAllowedError') {
        toast.error('Please allow microphone access to record vocals');
      } else {
        toast.error('Could not access microphone');
      }
    }
  };
  
  const stopRecording = (isSecondVocal = false) => {
    const recorder = isSecondVocal ? secondMediaRecorderRef.current : mediaRecorderRef.current;
    if (recorder && recorder.state === 'recording') {
      recorder.stop();
    }
  };
  
  const playCombined = () => {
    if (!selectedBeat) return;
    
    setIsPlaying(true);
    setPlayMode('combined');
    setElapsedTime(0);
    
    // Start beat (will loop automatically via the interval)
    startBeatPlayback();
    
    // Start lead vocals if available
    if (vocalUrl) {
      vocalAudioRef.current = new Audio(vocalUrl);
      vocalAudioRef.current.play();
    }
    
    // Start second vocals if available
    if (secondVocalUrl) {
      secondVocalAudioRef.current = new Audio(secondVocalUrl);
      secondVocalAudioRef.current.play();
    }
    
    // Track playback progress
    const startTime = Date.now();
    playbackTimerRef.current = setInterval(() => {
      const elapsed = (Date.now() - startTime) / 1000;
      setElapsedTime(elapsed);
      
      // Stop after song duration
      if (elapsed >= songDuration) {
        stopPlayback();
      }
    }, 100);
  };
  
  const stopPlayback = () => {
    stopBeatPlayback();
    if (vocalAudioRef.current) {
      vocalAudioRef.current.pause();
      vocalAudioRef.current.currentTime = 0;
    }
    if (secondVocalAudioRef.current) {
      secondVocalAudioRef.current.pause();
      secondVocalAudioRef.current.currentTime = 0;
    }
    if (playbackTimerRef.current) {
      clearInterval(playbackTimerRef.current);
      playbackTimerRef.current = null;
    }
    setIsPlaying(false);
    setElapsedTime(0);
  };
  
  const reRecord = () => {
    // Clear lead vocals but keep beat
    if (vocalUrl) URL.revokeObjectURL(vocalUrl);
    setVocalBlob(null);
    setVocalUrl(null);
    toast.info('Ready to record new lead vocals');
  };
  
  const clearSecondVocal = () => {
    if (secondVocalUrl) URL.revokeObjectURL(secondVocalUrl);
    setSecondVocalBlob(null);
    setSecondVocalUrl(null);
    toast.info('Second vocal cleared');
  };
  
  const handleSaveSong = async () => {
    if (!songName.trim()) {
      toast.error('Give your song a name!');
      return;
    }
    
    if (!vocalBlob) {
      toast.error('Record some lead vocals first!');
      return;
    }
    
    setSaving(true);
    try {
      // Convert vocal blob to base64
      const vocalBase64 = await blobToBase64(vocalBlob);
      let secondVocalBase64 = null;
      
      if (secondVocalBlob) {
        secondVocalBase64 = await blobToBase64(secondVocalBlob);
      }
      
      const response = await songsAPI.save(childId, {
        name: songName.trim(),
        beat_id: selectedBeat.id,
        beat_name: selectedBeat.name,
        beat_data: {
          tempo: selectedBeat.tempo,
          tracks: selectedBeat.tracks
        },
        vocal_data: vocalBase64,
        second_vocal_data: secondVocalBase64,
        second_vocal_type: secondVocalBlob ? secondVocalType : null,
        duration: songDuration
      });
      
      toast.success(`Song saved! +15 creativity sparks!`);
      onComplete(15);
      setSongName('');
      setShowSave(false);
      loadSavedSongs();
    } catch (error) {
      toast.error('Failed to save song');
    } finally {
      setSaving(false);
    }
  };
  
  // Helper to convert blob to base64
  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
    });
  };
  
  return (
    <div className="glass-panel p-3 sm:p-6 rounded-2xl">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl font-philosopher font-bold text-white flex items-center gap-2 sm:gap-3">
          <Mic className="w-5 h-5 sm:w-7 sm:h-7 text-fuchsia-400" />
          Song Forge
        </h2>
        <button onClick={onBack} className="text-slate-400 hover:text-white transition-colors text-sm sm:text-base">
          ← Back
        </button>
      </div>
      
      {/* Step 1: Select a Beat */}
      <div className="mb-4 sm:mb-6">
        <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-fuchsia-500 text-white text-sm flex items-center justify-center">1</span>
          Choose a Beat
        </h3>
        
        {savedBeats.length === 0 ? (
          <div className="p-4 bg-slate-900/50 rounded-xl text-center">
            <p className="text-slate-400 mb-2">No beats yet!</p>
            <p className="text-slate-500 text-sm">Create a beat in Beat Forge first</p>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {savedBeats.map(beat => (
              <button
                key={beat.id}
                onClick={() => setSelectedBeat(beat)}
                className={`px-3 py-2 rounded-lg text-sm transition-all ${
                  selectedBeat?.id === beat.id
                    ? 'bg-fuchsia-500 text-white'
                    : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700/50'
                }`}
              >
                {beat.name} ({beat.tempo} BPM)
              </button>
            ))}
          </div>
        )}
        
        {selectedBeat && (
          <div className="mt-3 p-3 bg-fuchsia-500/10 border border-fuchsia-500/30 rounded-lg">
            <p className="text-fuchsia-300 text-sm">
              Selected: <span className="font-semibold">{selectedBeat.name}</span> at {selectedBeat.tempo} BPM
            </p>
          </div>
        )}
      </div>
      
      {/* Step 1.5: Choose Duration */}
      {selectedBeat && (
        <div className="mb-4 sm:mb-6">
          <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-fuchsia-500/70 text-white text-sm flex items-center justify-center">⏱</span>
            Song Length
          </h3>
          <div className="flex flex-wrap gap-2">
            {DURATION_OPTIONS.map(option => (
              <button
                key={option.value}
                onClick={() => setSongDuration(option.value)}
                disabled={isRecording || isRecordingSecond}
                className={`px-4 py-2 rounded-lg text-sm transition-all flex items-center gap-2 ${
                  songDuration === option.value
                    ? 'bg-fuchsia-500 text-white ring-2 ring-fuchsia-300'
                    : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700/50'
                } disabled:opacity-50`}
                data-testid={`duration-${option.value}`}
              >
                <span>{option.emoji}</span>
                {option.label}
              </button>
            ))}
          </div>
          <p className="text-slate-400 text-xs mt-2">
            The beat will loop automatically for the full {songDuration} seconds
          </p>
        </div>
      )}
      
      {/* Step 2: Record Lead Vocals */}
      <div className="mb-4 sm:mb-6">
        <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-fuchsia-500 text-white text-sm flex items-center justify-center">2</span>
          Record Lead Vocals
        </h3>
        
        {/* Countdown Overlay */}
        {countdown !== null && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
            <div className="text-center">
              <div className="text-8xl font-bold text-fuchsia-400 animate-pulse">{countdown}</div>
              <p className="text-white text-xl mt-4">Get Ready!</p>
            </div>
          </div>
        )}
        
        <div className="flex flex-wrap gap-3 items-center">
          {!isRecording ? (
            <button
              onClick={() => startCountdownAndRecord(false)}
              disabled={!selectedBeat || isRecordingSecond}
              className="px-4 py-3 bg-red-500 hover:bg-red-600 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded-xl flex items-center gap-2 font-medium transition-all"
              data-testid="record-btn"
            >
              <Mic className="w-5 h-5" />
              {vocalUrl ? 'Re-Record Lead' : `Record ${songDuration}s`}
            </button>
          ) : (
            <button
              onClick={() => stopRecording(false)}
              className="px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl flex items-center gap-2 font-medium animate-pulse"
              data-testid="stop-record-btn"
            >
              <X className="w-5 h-5" />
              Stop Recording
            </button>
          )}
          
          {vocalUrl && !isRecording && (
            <button
              onClick={reRecord}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm"
            >
              Clear Lead
            </button>
          )}
        </div>
        
        {/* Recording Progress Bar */}
        {isRecording && (
          <div className="mt-3">
            <div className="flex justify-between text-xs text-slate-400 mb-1">
              <span>Recording lead vocals...</span>
              <span>{Math.round(recordingProgress)}%</span>
            </div>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-red-500 to-fuchsia-500 transition-all duration-100"
                style={{ width: `${recordingProgress}%` }}
              />
            </div>
            <p className="text-red-300 text-sm mt-2 flex items-center gap-2">
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
              Sing along with the beat!
            </p>
          </div>
        )}
        
        {vocalUrl && !isRecording && (
          <div className="mt-3 p-3 bg-green-500/20 border border-green-500/30 rounded-lg">
            <p className="text-green-300 flex items-center gap-2">
              <Check className="w-4 h-4" />
              Lead vocals recorded!
            </p>
          </div>
        )}
      </div>
      
      {/* Step 2.5: Optional Second Vocal Layer */}
      {vocalUrl && !isRecording && (
        <div className="mb-4 sm:mb-6">
          <button
            onClick={() => setShowSecondVocal(!showSecondVocal)}
            className="text-fuchsia-400 hover:text-fuchsia-300 text-sm flex items-center gap-2 mb-3"
          >
            <span className={`transition-transform ${showSecondVocal ? 'rotate-90' : ''}`}>▶</span>
            Add Second Vocal Layer (Optional)
          </button>
          
          {showSecondVocal && (
            <div className="p-4 bg-slate-900/50 rounded-xl border border-fuchsia-500/20">
              <p className="text-slate-300 text-sm mb-3">
                Add a backing vocal, ad-lib, or spoken intro to your song
              </p>
              
              {/* Vocal Type Selector */}
              <div className="flex flex-wrap gap-2 mb-4">
                {[
                  { value: 'backing', label: 'Backing Vocal', emoji: '🎤' },
                  { value: 'adlib', label: 'Ad-lib', emoji: '✨' },
                  { value: 'intro', label: 'Spoken Intro', emoji: '🎬' }
                ].map(type => (
                  <button
                    key={type.value}
                    onClick={() => setSecondVocalType(type.value)}
                    disabled={isRecordingSecond}
                    className={`px-3 py-1.5 rounded-lg text-xs transition-all flex items-center gap-1.5 ${
                      secondVocalType === type.value
                        ? 'bg-purple-500 text-white'
                        : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700/50'
                    }`}
                  >
                    <span>{type.emoji}</span>
                    {type.label}
                  </button>
                ))}
              </div>
              
              {/* Second Vocal Recording */}
              <div className="flex flex-wrap gap-3 items-center">
                {!isRecordingSecond ? (
                  <button
                    onClick={() => startCountdownAndRecord(true)}
                    disabled={!selectedBeat}
                    className="px-4 py-2 bg-purple-500 hover:bg-purple-600 disabled:bg-slate-600 text-white rounded-lg flex items-center gap-2 text-sm font-medium transition-all"
                    data-testid="record-second-btn"
                  >
                    <Mic className="w-4 h-4" />
                    {secondVocalUrl ? 'Re-Record' : `Record ${secondVocalType}`}
                  </button>
                ) : (
                  <button
                    onClick={() => stopRecording(true)}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 text-sm font-medium animate-pulse"
                    data-testid="stop-second-btn"
                  >
                    <X className="w-4 h-4" />
                    Stop
                  </button>
                )}
                
                {secondVocalUrl && !isRecordingSecond && (
                  <button
                    onClick={clearSecondVocal}
                    className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-xs"
                  >
                    Clear
                  </button>
                )}
              </div>
              
              {/* Second Vocal Progress */}
              {isRecordingSecond && (
                <div className="mt-3">
                  <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-100"
                      style={{ width: `${recordingProgress}%` }}
                    />
                  </div>
                </div>
              )}
              
              {secondVocalUrl && !isRecordingSecond && (
                <div className="mt-3 p-2 bg-purple-500/20 border border-purple-500/30 rounded-lg">
                  <p className="text-purple-300 text-xs flex items-center gap-2">
                    <Check className="w-3 h-3" />
                    {secondVocalType === 'backing' ? 'Backing vocal' : secondVocalType === 'adlib' ? 'Ad-lib' : 'Intro'} added!
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* Step 3: Play Your Song */}
      <div className="mb-4 sm:mb-6">
        <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-fuchsia-500 text-white text-sm flex items-center justify-center">3</span>
          Listen & Save
        </h3>
        
        {/* Song layers indicator */}
        <div className="flex flex-wrap gap-2 mb-3">
          <span className="px-2 py-1 bg-slate-800/50 text-slate-300 rounded text-xs flex items-center gap-1">
            🥁 Beat
          </span>
          {vocalUrl && (
            <span className="px-2 py-1 bg-fuchsia-500/30 text-fuchsia-300 rounded text-xs flex items-center gap-1">
              🎤 Lead Vocals
            </span>
          )}
          {secondVocalUrl && (
            <span className="px-2 py-1 bg-purple-500/30 text-purple-300 rounded text-xs flex items-center gap-1">
              {secondVocalType === 'backing' ? '🎶' : secondVocalType === 'adlib' ? '✨' : '🎬'} {secondVocalType}
            </span>
          )}
        </div>
        
        <div className="flex flex-wrap gap-3">
          {!isPlaying ? (
            <button
              onClick={playCombined}
              disabled={!selectedBeat}
              className="px-4 py-3 bg-fuchsia-500 hover:bg-fuchsia-600 disabled:bg-slate-600 text-white rounded-xl flex items-center gap-2 font-medium transition-all"
              data-testid="play-song-btn"
            >
              <Play className="w-5 h-5" />
              Play Song ({songDuration}s)
            </button>
          ) : (
            <button
              onClick={stopPlayback}
              className="px-4 py-3 bg-fuchsia-600 hover:bg-fuchsia-700 text-white rounded-xl flex items-center gap-2 font-medium"
              data-testid="stop-song-btn"
            >
              <Pause className="w-5 h-5" />
              Stop
            </button>
          )}
          
          {vocalUrl && (
            <button
              onClick={() => setShowSave(true)}
              className="px-4 py-3 bg-green-500 hover:bg-green-600 text-white rounded-xl flex items-center gap-2 font-medium transition-all"
              data-testid="save-song-btn"
            >
              <Star className="w-5 h-5" />
              Save Song +15 ✨
            </button>
          )}
        </div>
        
        {/* Playback Progress */}
        {isPlaying && selectedBeat && (
          <div className="mt-3">
            <div className="flex justify-between text-xs text-slate-400 mb-1">
              <span>Playing...</span>
              <span>{Math.round(elapsedTime)}s / {songDuration}s</span>
            </div>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-fuchsia-500 to-pink-500 transition-all duration-100"
                style={{ width: `${(elapsedTime / songDuration) * 100}%` }}
              />
            </div>
          </div>
        )}
        
        {/* Beat visualization when playing */}
        {isPlaying && selectedBeat && (
          <div className="mt-3 flex gap-1 justify-center">
            {Array(16).fill(0).map((_, i) => (
              <div
                key={i}
                className={`w-3 h-8 rounded transition-all ${
                  currentStep === i ? 'bg-fuchsia-400 scale-110' : 'bg-slate-700'
                }`}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* Save Modal */}
      {showSave && (
        <div className="mb-4 p-4 bg-slate-900/50 rounded-xl border border-fuchsia-500/30">
          <h3 className="text-white font-semibold mb-3">Save Your Song</h3>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              value={songName}
              onChange={(e) => setSongName(e.target.value)}
              placeholder="Name your song..."
              className="flex-1 px-4 py-2 bg-slate-900/50 border border-white/10 rounded-lg text-white text-sm"
              disabled={saving}
            />
            <div className="flex gap-2">
              <button
                onClick={handleSaveSong}
                disabled={saving}
                className="flex-1 sm:flex-none px-4 py-2 bg-fuchsia-500 hover:bg-fuchsia-600 text-white rounded-lg disabled:opacity-50 text-sm font-medium"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => setShowSave(false)}
                className="px-4 py-2 text-slate-400 hover:text-white text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* My Songs */}
      <div className="mt-4 pt-4 border-t border-white/10">
        <button
          onClick={() => setShowSavedSongs(!showSavedSongs)}
          className="text-fuchsia-400 hover:text-fuchsia-300 text-sm flex items-center gap-2"
        >
          <Music className="w-4 h-4" />
          My Songs ({savedSongs.length})
        </button>
        
        {showSavedSongs && savedSongs.length > 0 && (
          <div className="mt-3 space-y-2">
            {savedSongs.map(song => (
              <div key={song.id} className="p-3 bg-slate-800/50 rounded-lg flex items-center justify-between">
                <div>
                  <p className="text-white font-medium text-sm">{song.name}</p>
                  <p className="text-slate-400 text-xs">Beat: {song.beat_name}</p>
                </div>
                <span className="text-slate-500 text-xs">
                  {new Date(song.created_at).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
      
      <p className="text-slate-500 text-xs sm:text-sm mt-4 text-center">
        Select a beat • Record your voice • Save as a finished song!
      </p>
    </div>
  );
};

// ============ MY BEATS GALLERY ============

const MyBeatsGallery = ({ childId, onBack }) => {
  const [beats, setBeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playingBeat, setPlayingBeat] = useState(null);
  const [editingName, setEditingName] = useState(null);
  const [newName, setNewName] = useState('');
  const [currentStep, setCurrentStep] = useState(-1);
  
  const audioContextRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (childId) {
      loadBeats();
    }
    return () => {
      stopPlayback();
    };
  }, [childId]);

  const loadBeats = async () => {
    try {
      setLoading(true);
      const response = await beatsAPI.getAll(childId);
      setBeats(response.data || []);
    } catch (error) {
      console.error('Error loading beats:', error);
      toast.error('Failed to load beats');
    } finally {
      setLoading(false);
    }
  };

  const getAudioContext = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    // Resume if suspended (browsers require user interaction)
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
    return audioContextRef.current;
  };

  const playSound = (instrumentId) => {
    const ctx = getAudioContext();
    const instrument = INSTRUMENTS.find(i => i.id === instrumentId);
    if (!instrument) return;

    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.type = instrument.type;
    oscillator.frequency.setValueAtTime(instrument.freq, ctx.currentTime);
    
    gainNode.gain.setValueAtTime(0.4, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + instrument.decay);
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + instrument.decay);
  };

  const playBeat = (beat) => {
    stopPlayback();
    setPlayingBeat(beat.id);
    
    const stepDuration = (60 / beat.tempo) * 1000 / 4;
    let step = 0;
    
    intervalRef.current = setInterval(() => {
      setCurrentStep(step);
      
      beat.tracks.forEach(track => {
        if (track.pattern[step]) {
          playSound(track.instrument);
        }
      });
      
      step = (step + 1) % 16;
    }, stepDuration);
  };

  const stopPlayback = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setPlayingBeat(null);
    setCurrentStep(-1);
  };

  const handleDelete = async (beatId, name) => {
    if (!confirm(`Delete "${name}"? This cannot be undone.`)) return;
    
    try {
      await beatsAPI.delete(childId, beatId);
      toast.success('Beat deleted');
      if (playingBeat === beatId) {
        stopPlayback();
      }
      loadBeats();
    } catch (error) {
      toast.error('Failed to delete beat');
    }
  };

  if (loading) {
    return (
      <div className="glass-panel p-8 rounded-2xl text-center">
        <div className="animate-spin w-8 h-8 border-2 border-white/20 border-t-pink-500 rounded-full mx-auto mb-4"></div>
        <p className="text-slate-400">Loading your beats...</p>
      </div>
    );
  }

  return (
    <div className="glass-panel p-8 rounded-2xl">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-philosopher font-bold text-white flex items-center gap-3">
          <Volume2 className="w-7 h-7 text-rose-400" />
          My Beats
        </h2>
        <button
          onClick={onBack}
          className="text-slate-400 hover:text-white transition-colors"
        >
          ← Back
        </button>
      </div>

      {beats.length > 0 ? (
        <div className="space-y-4">
          {beats.map((beat) => {
            const isPlaying = playingBeat === beat.id;
            const activeTrackCount = beat.tracks.filter(t => t.pattern.some(p => p)).length;
            
            return (
              <div 
                key={beat.id}
                className={`p-4 rounded-xl transition-all ${
                  isPlaying 
                    ? 'bg-pink-500/20 border border-pink-500/30' 
                    : 'bg-slate-900/50 border border-white/10 hover:border-pink-500/20'
                }`}
                data-testid={`saved-beat-${beat.id}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => isPlaying ? stopPlayback() : playBeat(beat)}
                      className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                        isPlaying ? 'bg-red-500 hover:bg-red-600' : 'bg-pink-500 hover:bg-pink-600'
                      }`}
                    >
                      {isPlaying ? (
                        <Pause className="w-5 h-5 text-white" />
                      ) : (
                        <Play className="w-5 h-5 text-white ml-0.5" />
                      )}
                    </button>
                    <div>
                      <h3 className="text-white font-semibold">{beat.name}</h3>
                      <p className="text-slate-500 text-xs">
                        {beat.tempo} BPM • {activeTrackCount} tracks
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500 text-xs">
                      {new Date(beat.created_at).toLocaleDateString()}
                    </span>
                    <button
                      onClick={() => handleDelete(beat.id, beat.name)}
                      className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                {/* Beat Pattern Preview */}
                <div className="flex gap-0.5 overflow-hidden rounded">
                  {Array(16).fill(0).map((_, i) => {
                    const hasNote = beat.tracks.some(t => t.pattern[i]);
                    const isCurrentStep = isPlaying && currentStep === i;
                    
                    return (
                      <div
                        key={i}
                        className={`flex-1 h-2 rounded-sm transition-all ${
                          isCurrentStep 
                            ? 'bg-white' 
                            : hasNote 
                              ? 'bg-pink-500/60' 
                              : 'bg-slate-700/50'
                        }`}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12">
          <Music className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h3 className="text-xl text-slate-400 mb-2">No Beats Yet</h3>
          <p className="text-slate-500">
            Create your first beat in the Beat Forge!
          </p>
        </div>
      )}
    </div>
  );
};

// ============ STORY SEEDS ============

const StorySeeds = ({ childId, childName, onComplete, onBack }) => {
  const [prompt, setPrompt] = useState(null);
  const [story, setStory] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedStories, setSavedStories] = useState([]);
  const [showSavedStories, setShowSavedStories] = useState(false);
  const [viewingStory, setViewingStory] = useState(null);

  useEffect(() => {
    loadNewPrompt();
    if (childId) {
      loadSavedStories();
    }
  }, [childId]);

  const loadNewPrompt = async () => {
    try {
      setLoading(true);
      const response = await storiesAPI.getRandomPrompt();
      setPrompt(response.data);
      setStory('');
    } catch (error) {
      console.error('Error loading prompt:', error);
      toast.error('Failed to load story prompt');
    } finally {
      setLoading(false);
    }
  };

  const loadSavedStories = async () => {
    try {
      const response = await storiesAPI.getAll(childId);
      setSavedStories(response.data || []);
    } catch (error) {
      console.error('Error loading stories:', error);
    }
  };

  const handleSave = async () => {
    if (story.trim().length < 20) {
      toast.error('Write at least 20 characters to continue the story!');
      return;
    }
    
    setSaving(true);
    try {
      const response = await storiesAPI.save(childId, {
        prompt_id: prompt.id,
        prompt_text: prompt.text,
        continuation: story.trim()
      });
      
      toast.success(`Story saved! +${response.data.sparks_earned} creativity sparks!`);
      onComplete(response.data.sparks_earned);
      loadSavedStories();
      
      // Ask if they want to continue with a new prompt
      if (confirm('Great story! Would you like to write another?')) {
        loadNewPrompt();
      }
    } catch (error) {
      toast.error('Failed to save story');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStory = async (storyId) => {
    if (!confirm('Delete this story?')) return;
    
    try {
      await storiesAPI.delete(childId, storyId);
      toast.success('Story deleted');
      loadSavedStories();
      setViewingStory(null);
    } catch (error) {
      toast.error('Failed to delete story');
    }
  };

  if (loading) {
    return (
      <div className="glass-panel p-8 rounded-2xl text-center">
        <div className="animate-spin w-8 h-8 border-2 border-white/20 border-t-amber-500 rounded-full mx-auto mb-4"></div>
        <p className="text-slate-400">The Orb is thinking of a story...</p>
      </div>
    );
  }

  return (
    <div className="glass-panel p-6 rounded-2xl">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-philosopher font-bold text-white flex items-center gap-3">
          <BookOpen className="w-7 h-7 text-amber-400" />
          Story Seeds
        </h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowSavedStories(!showSavedStories)}
            className="px-3 py-2 bg-slate-800/50 hover:bg-slate-700/50 text-slate-300 rounded-lg text-sm"
          >
            My Stories ({savedStories.length})
          </button>
          <button onClick={onBack} className="text-slate-400 hover:text-white transition-colors">
            ← Back
          </button>
        </div>
      </div>

      {/* Saved Stories Dropdown */}
      {showSavedStories && (
        <div className="mb-6 p-4 bg-slate-900/50 rounded-xl max-h-64 overflow-y-auto">
          <h3 className="text-white font-semibold mb-3">Your Stories</h3>
          {savedStories.length > 0 ? (
            <div className="space-y-2">
              {savedStories.map(s => (
                <button
                  key={s.id}
                  onClick={() => setViewingStory(s)}
                  className="w-full p-3 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/20 rounded-lg text-left transition-all"
                >
                  <p className="text-amber-300 text-sm italic line-clamp-1">"{s.prompt_text}"</p>
                  <p className="text-slate-400 text-xs mt-1">
                    {new Date(s.created_at).toLocaleDateString()}
                  </p>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-sm">No stories yet. Write your first!</p>
          )}
        </div>
      )}

      {/* Viewing Saved Story */}
      {viewingStory ? (
        <div>
          <div className="mb-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 bg-amber-500/10 p-4 rounded-xl border border-amber-500/20">
                <p className="text-amber-300 italic">"{viewingStory.prompt_text}"</p>
                <p className="text-slate-500 text-xs mt-2">— The Knowledge Orb</p>
              </div>
            </div>
            
            <div className="bg-slate-900/50 p-4 rounded-xl mb-4">
              <p className="text-slate-300 whitespace-pre-wrap">{viewingStory.continuation}</p>
              <p className="text-slate-600 text-xs mt-3">
                Written on {new Date(viewingStory.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
          
          <div className="flex justify-between">
            <button
              onClick={() => handleDeleteStory(viewingStory.id)}
              className="px-4 py-2 text-red-400 hover:text-red-300"
            >
              Delete Story
            </button>
            <button
              onClick={() => setViewingStory(null)}
              className="px-4 py-2 bg-slate-800/50 hover:bg-slate-700/50 text-white rounded-lg"
            >
              Back to Writing
            </button>
          </div>
        </div>
      ) : (
        <div>
          {/* Story Prompt */}
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 bg-amber-500/10 p-4 rounded-xl border border-amber-500/20">
              <p className="text-amber-300 text-lg italic">"{prompt?.text}"</p>
              <p className="text-slate-500 text-sm mt-2">— The Knowledge Orb invites you to continue this tale, {childName}...</p>
            </div>
          </div>

          {/* Story Input */}
          <div className="mb-6">
            <textarea
              value={story}
              onChange={(e) => setStory(e.target.value)}
              placeholder="Continue the story..."
              rows={8}
              className="w-full px-4 py-3 bg-slate-900/50 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:border-amber-500/50 resize-none"
              disabled={saving}
            />
            <div className="flex justify-between text-sm mt-2">
              <span className={`${story.length >= 20 ? 'text-green-400' : 'text-slate-500'}`}>
                {story.length} characters {story.length < 20 && '(min 20)'}
              </span>
              <span className="text-amber-400">+8 sparks on save</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-between">
            <button
              onClick={loadNewPrompt}
              className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
            >
              New Prompt
            </button>
            <button
              onClick={handleSave}
              disabled={saving || story.length < 20}
              className="px-6 py-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {saving ? 'Saving...' : 'Save Story +8 ✨'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const CourtyardPage = () => {
  const navigate = useNavigate();
  const [selectedChild, setSelectedChild] = useState(null);
  const [sparks, setSparks] = useState({});
  const [activeActivity, setActiveActivity] = useState(null);
  const [courtyardUnlocked, setCourtyardUnlocked] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false); // Admin mode for full feature access
  const [beatForSongForge, setBeatForSongForge] = useState(null); // Beat to pass to Song Forge
  
  // Activity limit state
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitData, setLimitData] = useState({ activitiesCompleted: 0, dailyLimit: 5 });

  // Scroll to top when an activity becomes active (fixes auto-scroll to bottom issue)
  useEffect(() => {
    if (activeActivity) {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [activeActivity]);

  // ============ TV/REMOTE NAVIGATION ============
  const activitiesRef = useRef(null);
  
  // Use keyboard navigation hook for activity cards
  useKeyboardNavigation({
    containerRef: activitiesRef,
    onEscape: () => {
      if (activeActivity) {
        setActiveActivity(null);
      } else {
        navigate('/temple');
      }
    }
  });



  // Puzzle state - enhanced with hints and retry
  const [currentPuzzle, setCurrentPuzzle] = useState(null);
  const [puzzleAnswer, setPuzzleAnswer] = useState(null);
  const [puzzleResult, setPuzzleResult] = useState(null);
  const [puzzlesSolved, setPuzzlesSolved] = useState(() => {
    // Load from localStorage based on child ID
    const child = localStorage.getItem('selectedChild');
    if (child) {
      const childData = JSON.parse(child);
      const saved = localStorage.getItem(`puzzlesSolved_${childData.id}`);
      return saved ? parseInt(saved, 10) : 0;
    }
    return 0;
  });
  const [showHint, setShowHint] = useState(false);
  const [wrongAttempts, setWrongAttempts] = useState(0);
  
  // Save puzzles solved to localStorage when it changes
  useEffect(() => {
    if (selectedChild?.id && puzzlesSolved > 0) {
      localStorage.setItem(`puzzlesSolved_${selectedChild.id}`, puzzlesSolved.toString());
    }
  }, [puzzlesSolved, selectedChild?.id]);
  
  // Music Forge state
  const [musicForgeActive, setMusicForgeActive] = useState(false);
  const [currentRhythm, setCurrentRhythm] = useState(null);
  const [rhythmPhase, setRhythmPhase] = useState('listen'); // 'listen', 'play', 'result'
  const [playerTaps, setPlayerTaps] = useState([]);
  const [rhythmScore, setRhythmScore] = useState(0);
  const [rhythmLevel, setRhythmLevel] = useState('beginner');
  const rhythmAudioRef = useRef(null);
  
  // Chess Garden state - Enhanced with learning mode + Play mode
  const [chessActive, setChessActive] = useState(false);
  const [currentChessPuzzle, setCurrentChessPuzzle] = useState(null);
  const [chessPuzzlesSolved, setChessPuzzlesSolved] = useState(0);
  const [chessShowHint, setChessShowHint] = useState(false);
  const [chessAnswer, setChessAnswer] = useState(null);
  const [chessMode, setChessMode] = useState('intro'); // 'intro', 'learn', 'puzzle', 'play-menu', 'play-ai', 'play-friend'
  const [currentLearningCard, setCurrentLearningCard] = useState(0);
  const [learnedPieces, setLearnedPieces] = useState([]);
  const [chessExplanation, setChessExplanation] = useState(null);
  // Chess Play Mode state
  const [chessGameBoard, setChessGameBoard] = useState(null);
  const [chessSelectedSquare, setChessSelectedSquare] = useState(null);
  const [chessLegalMoves, setChessLegalMoves] = useState([]);
  const [chessTurn, setChessTurn] = useState('white'); // 'white' or 'black'
  const [chessGameStatus, setChessGameStatus] = useState(null); // 'check', 'checkmate', 'draw', null
  const [chessGameHistory, setChessGameHistory] = useState([]);
  const [chessShowMoveHints, setChessShowMoveHints] = useState(true);
  const [chessPlayMode, setChessPlayMode] = useState(null); // 'ai' or 'friend'
  const [chessAIThinking, setChessAIThinking] = useState(false);
  
  // Chess Multiplayer State
  const [chessMultiplayerMode, setChessMultiplayerMode] = useState(null); // 'create', 'join', 'playing'
  const [chessMatchCode, setChessMatchCode] = useState('');
  const [chessMatchData, setChessMatchData] = useState(null);
  const [chessJoinCode, setChessJoinCode] = useState('');
  const [chessMultiplayerLoading, setChessMultiplayerLoading] = useState(false);
  const [chessMultiplayerError, setChessMultiplayerError] = useState(null);
  const chessPollingRef = useRef(null);
  
  // TV Game Mode - cursor position for arrow key navigation
  const [chessCursorPos, setChessCursorPos] = useState({ file: 4, rank: 4 }); // Start at e4
  const [tvGameMode, setTvGameMode] = useState(false); // Auto-enable on arrow key press

  // Constellation state
  const [selectedConstellation, setSelectedConstellation] = useState(null);

  useEffect(() => {
    const child = localStorage.getItem('selectedChild');
    if (!child) {
      navigate('/child-select');
      return;
    }
    
    try {
      const parsedChild = JSON.parse(child);
      setSelectedChild(parsedChild);
      
      questsAPI.getSparks(parsedChild.id)
        .then(res => setSparks(res.data?.sparks || {}))
        .catch(console.error);
      
      // Check if user is admin for full feature access
      betaAPI.checkAdmin()
        .then(res => {
          if (res.data?.is_admin) {
            setIsAdmin(true);
          }
        })
        .catch(() => setIsAdmin(false));
    } catch (error) {
      navigate('/child-select');
    }
  }, [navigate]);

  // Check activity limit before starting an activity
  const checkActivityLimit = async () => {
    if (!selectedChild) return true; // No child selected, can't check
    
    try {
      const res = await billingAPI.checkActivityLimit(selectedChild.id);
      const data = res.data;
      
      setLimitData({
        activitiesCompleted: data.activities_today || 0,
        dailyLimit: data.daily_limit || 5
      });
      
      if (!data.can_proceed) {
        setShowLimitModal(true);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error checking activity limit:', error);
      // On error, allow the activity (fail open)
      return true;
    }
  };

  // Start an activity with limit check
  const startActivityWithLimitCheck = async (activityName, callback) => {
    const canProceed = await checkActivityLimit();
    if (canProceed) {
      // Scroll to top when starting an activity (especially important for TV/large screens)
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      
      setActiveActivity(activityName);
      if (callback) callback();
    }
  };

  const startPuzzle = () => {
    // Use the new dynamic puzzle generator
    const newPuzzle = generatePuzzle(puzzlesSolved);
    setCurrentPuzzle(newPuzzle);
    setPuzzleAnswer(null);
    setPuzzleResult(null);
    setShowHint(false);
    setWrongAttempts(0);
  };

  const submitPuzzleAnswer = async (answer) => {
    setPuzzleAnswer(answer);
    
    // For pattern puzzles, compare objects
    let isCorrect;
    if (currentPuzzle.type === 'pattern' || currentPuzzle.type === 'odd_one_out') {
      if (typeof answer === 'object') {
        isCorrect = answer.shape === currentPuzzle.answer.shape && answer.color === currentPuzzle.answer.color;
      } else {
        isCorrect = answer === currentPuzzle.answer;
      }
    } else {
      isCorrect = String(answer) === String(currentPuzzle.answer);
    }
    
    if (isCorrect) {
      setPuzzleResult(true);
      setPuzzlesSolved(prev => prev + 1);
      toast.success(`Correct! +${currentPuzzle.sparks} sparks!`);
    } else {
      setWrongAttempts(prev => prev + 1);
      setPuzzleResult(false);
      
      // After 2 wrong attempts, show hint automatically
      if (wrongAttempts >= 1) {
        setShowHint(true);
        toast.error('Not quite! Here\'s a hint to help you.');
      } else {
        toast.error('Not quite! Try again.');
      }
    }
  };
  
  const retryPuzzle = () => {
    setPuzzleAnswer(null);
    setPuzzleResult(null);
  };

  const handleConstellationComplete = (sparksAwarded) => {
    setSelectedConstellation(null);
    // Could add backend call to award sparks here
  };
  
  // ============ MUSIC FORGE FUNCTIONS ============
  const audioContext = useRef(null);
  
  const playBeep = useCallback((frequency = 440, duration = 0.1) => {
    if (!audioContext.current) {
      audioContext.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    const ctx = audioContext.current;
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.frequency.value = frequency;
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);
    
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + duration);
  }, []);
  
  const startMusicForge = () => {
    const patterns = RHYTHM_PATTERNS[rhythmLevel];
    const pattern = patterns[Math.floor(Math.random() * patterns.length)];
    setCurrentRhythm(pattern);
    setRhythmPhase('listen');
    setPlayerTaps([]);
    setMusicForgeActive(true);
    
    // Play the rhythm after a short delay
    setTimeout(() => playRhythmPattern(pattern), 500);
  };
  
  const playRhythmPattern = (rhythm) => {
    const interval = 60000 / rhythm.bpm;
    rhythm.pattern.forEach((beat, index) => {
      if (beat === 1) {
        setTimeout(() => playBeep(440, 0.15), index * interval);
      }
    });
    
    // Switch to play phase after pattern finishes
    setTimeout(() => {
      setRhythmPhase('play');
      setPlayerTaps([]);
    }, rhythm.pattern.length * interval + 500);
  };
  
  const handleRhythmTap = () => {
    if (rhythmPhase !== 'play' || !currentRhythm) return;
    
    playBeep(550, 0.1); // Play tap sound
    const now = Date.now();
    setPlayerTaps(prev => [...prev, now]);
    
    // Check if enough taps received
    const expectedTaps = currentRhythm.pattern.filter(b => b === 1).length;
    if (playerTaps.length + 1 >= expectedTaps) {
      setTimeout(() => evaluateRhythm(), 300);
    }
  };
  
  const evaluateRhythm = () => {
    if (!currentRhythm) return;
    
    const expectedTaps = currentRhythm.pattern.filter(b => b === 1).length;
    const actualTaps = playerTaps.length + 1; // Include the final tap
    
    // Simple scoring based on tap count
    const accuracy = Math.min(100, Math.round((actualTaps / expectedTaps) * 100));
    setRhythmScore(accuracy);
    setRhythmPhase('result');
    
    if (accuracy >= 70) {
      toast.success(`Great rhythm! ${accuracy}% accuracy - +5 sparks`);
    } else {
      toast.info(`Keep practicing! ${accuracy}% accuracy`);
    }
  };
  
  // ============ CHESS GARDEN FUNCTIONS - ENHANCED LEARNING SYSTEM ============
  const startChessGarden = () => {
    // If pieces haven't been learned, start with intro
    if (learnedPieces.length < 4) {
      setChessMode('intro');
    } else {
      setChessMode('puzzle');
      selectNextChessPuzzle();
    }
    setChessActive(true);
  };
  
  const startChessLearning = () => {
    setChessMode('learn');
    setCurrentLearningCard(0);
  };
  
  const nextLearningCard = () => {
    const currentCard = CHESS_LEARNING_CARDS[currentLearningCard];
    if (!learnedPieces.includes(currentCard.id)) {
      setLearnedPieces(prev => [...prev, currentCard.id]);
    }
    
    if (currentLearningCard < CHESS_LEARNING_CARDS.length - 1) {
      setCurrentLearningCard(prev => prev + 1);
    } else {
      // All pieces learned, go to puzzles
      setChessMode('puzzle');
      selectNextChessPuzzle();
    }
  };
  
  const prevLearningCard = () => {
    if (currentLearningCard > 0) {
      setCurrentLearningCard(prev => prev - 1);
    }
  };
  
  const skipToPuzzles = () => {
    // Mark all pieces as learned and go to puzzles
    setLearnedPieces(CHESS_LEARNING_CARDS.map(c => c.id));
    setChessMode('puzzle');
    selectNextChessPuzzle();
  };
  
  const selectNextChessPuzzle = () => {
    // Get appropriate puzzle based on solved count
    const maxLevel = Math.min(4, Math.floor(chessPuzzlesSolved / 2) + 1);
    
    // Determine puzzle type based on progression
    let puzzleType;
    if (chessPuzzlesSolved < 4) {
      puzzleType = 'recognition';
    } else if (chessPuzzlesSolved < 8) {
      puzzleType = 'movement';
    } else if (chessPuzzlesSolved < 12) {
      puzzleType = 'capture';
    } else {
      puzzleType = 'checkmate';
    }
    
    // Get puzzles of the appropriate type
    const typePuzzles = CHESS_PUZZLES_ENHANCED[puzzleType] || CHESS_PUZZLES_ENHANCED.recognition;
    const puzzle = typePuzzles[Math.floor(Math.random() * typePuzzles.length)];
    
    setCurrentChessPuzzle(puzzle);
    setChessShowHint(false);
    setChessAnswer(null);
    setChessExplanation(null);
  };
  
  const submitChessAnswer = (answer) => {
    setChessAnswer(answer);
    const isCorrect = answer === currentChessPuzzle.answer;
    
    if (isCorrect) {
      setChessPuzzlesSolved(prev => prev + 1);
      setChessExplanation(currentChessPuzzle.explanation);
      toast.success(`Correct! +${currentChessPuzzle.sparks} sparks - Great strategic thinking!`);
    } else {
      setChessShowHint(true);
      toast.error('Not quite right. Here\'s a hint to help you think it through.');
    }
  };
  
  const renderChessBoard = (board, highlight = [], size = 'md', focusSquare = null) => {
    const files = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
    const ranks = [8, 7, 6, 5, 4, 3, 2, 1];
    // Responsive sizes - use max-w and width percentages
    const sizeClass = size === 'sm' 
      ? 'w-24 sm:w-32 max-w-[128px]' 
      : size === 'lg' 
        ? 'w-full max-w-[320px]' 
        : 'w-full max-w-[280px] sm:max-w-[320px]';
    const iconSize = size === 'sm' ? 12 : size === 'lg' ? 28 : 22;
    
    // Find the focus piece square if not explicitly provided
    let pieceSquare = focusSquare;
    if (!pieceSquare && board) {
      // Find the first piece on the board (usually the one being focused)
      const pieceEntry = Object.entries(board).find(([key]) => key.match(/^[KQRNBPkqrnbp]/i));
      if (pieceEntry) {
        pieceSquare = pieceEntry[1];
      }
    }
    
    // Helper function to get the piece key (strips numbers for multi-piece boards)
    const getPieceKey = (pieceId) => {
      return pieceId.replace(/[0-9]/g, '');
    };
    
    return (
      <div className={`grid grid-cols-8 gap-0 ${sizeClass} mx-auto border-2 border-amber-600 rounded-lg overflow-hidden shadow-lg`}>
        {ranks.map(rank => 
          files.map(file => {
            const square = `${file}${rank}`;
            const isLight = (files.indexOf(file) + rank) % 2 === 0;
            const isHighlighted = highlight.includes(square);
            const isFocusPiece = pieceSquare === square;
            
            // Find piece on this square
            const piece = Object.entries(board || {}).find(([_, pos]) => pos === square);
            const pieceKey = piece ? getPieceKey(piece[0]) : null;
            const pieceInfo = pieceKey ? CHESS_PIECES[pieceKey] : null;
            
            // Determine if this is a capture square (highlighted + has opponent piece)
            const isCapture = isHighlighted && pieceInfo;
            
            // Get the SVG icon for the piece
            const PieceIcon = pieceInfo && TEMPLE_PIECE_SVGS[pieceInfo.icon];
            
            return (
              <div
                key={square}
                className={`aspect-square flex items-center justify-center relative ${
                  isLight ? 'bg-amber-100' : 'bg-amber-700'
                } ${isFocusPiece ? 'ring-4 ring-yellow-400 ring-inset animate-pulse-slow bg-yellow-400/30' : ''} ${
                  isHighlighted && !isFocusPiece ? (isCapture ? 'ring-2 ring-red-500 ring-inset bg-red-500/40' : 'ring-2 ring-emerald-400 ring-inset bg-emerald-500/30') : ''
                }`}
              >
                {pieceInfo && PieceIcon && (
                  <div className={`flex items-center justify-center ${isFocusPiece ? 'scale-110' : ''}`}>
                    {PieceIcon(iconSize)}
                  </div>
                )}
                {/* Fallback to symbol if no SVG */}
                {pieceInfo && !PieceIcon && (
                  <span className={`text-${size === 'sm' ? 'lg' : size === 'lg' ? '3xl' : '2xl'} ${
                    pieceInfo.color === 'white' 
                      ? 'text-white drop-shadow-[0_2px_2px_rgba(0,0,0,0.8)]' 
                      : 'text-slate-900'
                  } ${isFocusPiece ? 'scale-110' : ''}`}>
                    {pieceInfo.symbol}
                  </span>
                )}
                {/* Green circle for legal move squares */}
                {isHighlighted && !pieceInfo && !isFocusPiece && (
                  <div className="w-4 h-4 rounded-full bg-emerald-500/80 animate-pulse shadow-lg shadow-emerald-500/50" />
                )}
                {/* Red spark indicator for capture squares */}
                {isCapture && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="absolute w-full h-full bg-red-500/20 animate-pulse" />
                    <Sparkles className="absolute top-0 right-0 w-3 h-3 text-red-400 animate-pulse" />
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    );
  };
  
  // Render mini board for learning cards
  const renderMiniBoard = (board, highlight = [], focusSquare = null) => {
    return renderChessBoard(board, highlight, 'sm', focusSquare);
  };

  // ============ CHESS PLAY MODE FUNCTIONS ============
  const INITIAL_CHESS_POSITION = {
    // White pieces
    R1: 'a1', N1: 'b1', B1: 'c1', Q: 'd1', K: 'e1', B2: 'f1', N2: 'g1', R2: 'h1',
    P1: 'a2', P2: 'b2', P3: 'c2', P4: 'd2', P5: 'e2', P6: 'f2', P7: 'g2', P8: 'h2',
    // Black pieces
    r1: 'a8', n1: 'b8', b1: 'c8', q: 'd8', k: 'e8', b2: 'f8', n2: 'g8', r2: 'h8',
    p1: 'a7', p2: 'b7', p3: 'c7', p4: 'd7', p5: 'e7', p6: 'f7', p7: 'g7', p8: 'h7'
  };

  const startChessGame = (mode) => {
    setChessPlayMode(mode);
    setChessGameBoard({ ...INITIAL_CHESS_POSITION });
    setChessTurn('white');
    setChessSelectedSquare(null);
    setChessLegalMoves([]);
    setChessGameStatus(null);
    setChessGameHistory([]);
    setChessMode(mode === 'ai' ? 'play-ai' : 'play-friend');
  };

  // ========== CHESS MULTIPLAYER FUNCTIONS ==========
  
  // Create a new multiplayer match
  const createChessMatch = async () => {
    if (!selectedChild) return;
    setChessMultiplayerLoading(true);
    setChessMultiplayerError(null);
    
    try {
      const response = await api.post('/chess/create-match', {
        child_id: selectedChild.id,
        child_name: selectedChild.name
      });
      
      setChessMatchCode(response.data.match_code);
      setChessMultiplayerMode('create');
      setChessMode('play-friend-online');
      toast.success('Match created! Share the code with a friend.');
    } catch (err) {
      console.error('Failed to create match:', err);
      setChessMultiplayerError(err.response?.data?.detail || 'Failed to create match');
      toast.error('Failed to create match');
    } finally {
      setChessMultiplayerLoading(false);
    }
  };
  
  // Join an existing match
  const joinChessMatch = async () => {
    if (!selectedChild || !chessJoinCode.trim()) {
      toast.error('Please enter a match code');
      return;
    }
    
    setChessMultiplayerLoading(true);
    setChessMultiplayerError(null);
    
    try {
      const response = await api.post(`/chess/join-match/${chessJoinCode.trim().toUpperCase()}`, {
        child_id: selectedChild.id,
        child_name: selectedChild.name
      });
      
      setChessMatchCode(chessJoinCode.trim().toUpperCase());
      setChessMultiplayerMode('playing');
      setChessMode('play-friend-online');
      toast.success(`Joined ${response.data.opponent_name}'s game!`);
      
      // Start polling for game state
      startChessPolling(chessJoinCode.trim().toUpperCase());
    } catch (err) {
      console.error('Failed to join match:', err);
      setChessMultiplayerError(err.response?.data?.detail || 'Failed to join match');
      toast.error(err.response?.data?.detail || 'Failed to join match');
    } finally {
      setChessMultiplayerLoading(false);
    }
  };
  
  // Poll for match updates
  const startChessPolling = useCallback((code) => {
    if (chessPollingRef.current) {
      clearInterval(chessPollingRef.current);
    }
    
    const poll = async () => {
      try {
        const response = await api.get(`/chess/match/${code}/poll?child_id=${selectedChild?.id}`);
        setChessMatchData(response.data);
        
        // If game ended, stop polling
        if (response.data.status !== 'waiting' && response.data.status !== 'playing') {
          clearInterval(chessPollingRef.current);
          chessPollingRef.current = null;
          
          if (response.data.winner) {
            const winnerName = response.data.winner === 'white' 
              ? response.data.white_player_name 
              : response.data.black_player_name;
            toast.success(`Game over! ${winnerName} wins!`);
          }
        }
        
        // If opponent joined, update mode
        if (response.data.status === 'playing' && chessMultiplayerMode === 'create') {
          setChessMultiplayerMode('playing');
          toast.success(`${response.data.black_player_name} joined!`);
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    };
    
    // Initial poll
    poll();
    
    // Poll every 2 seconds
    chessPollingRef.current = setInterval(poll, 2000);
  }, [selectedChild, chessMultiplayerMode]);
  
  // Make a move in multiplayer
  const makeChessMultiplayerMove = async (from, to, promotion = null) => {
    if (!chessMatchCode || !selectedChild) return;
    
    try {
      const response = await api.post(`/chess/match/${chessMatchCode}/move`, {
        child_id: selectedChild.id,
        from_square: from,
        to_square: to,
        promotion
      });
      
      // Update local state immediately
      setChessMatchData(prev => ({
        ...prev,
        board: response.data.board,
        current_turn: response.data.current_turn,
        status: response.data.status,
        winner: response.data.winner
      }));
      
      if (response.data.status === 'checkmate') {
        toast.success('Checkmate! You win!');
      }
      
      return true;
    } catch (err) {
      console.error('Failed to make move:', err);
      toast.error(err.response?.data?.detail || 'Invalid move');
      return false;
    }
  };
  
  // Clean up polling on unmount or mode change
  useEffect(() => {
    return () => {
      if (chessPollingRef.current) {
        clearInterval(chessPollingRef.current);
        chessPollingRef.current = null;
      }
    };
  }, []);
  
  // Start polling when entering multiplayer mode with a code
  useEffect(() => {
    if (chessMode === 'play-friend-online' && chessMatchCode && selectedChild) {
      startChessPolling(chessMatchCode);
    }
    
    return () => {
      if (chessPollingRef.current) {
        clearInterval(chessPollingRef.current);
        chessPollingRef.current = null;
      }
    };
  }, [chessMode, chessMatchCode, selectedChild, startChessPolling]);
  
  // Reset multiplayer state when leaving chess
  const exitChessMultiplayer = () => {
    if (chessPollingRef.current) {
      clearInterval(chessPollingRef.current);
      chessPollingRef.current = null;
    }
    setChessMultiplayerMode(null);
    setChessMatchCode('');
    setChessMatchData(null);
    setChessJoinCode('');
    setChessMultiplayerError(null);
    setChessMode('play-menu');
  };

  // ========== END CHESS MULTIPLAYER ==========

  const getSquareFromPosition = (pos) => {
    const file = pos.charCodeAt(0) - 'a'.charCodeAt(0);
    const rank = parseInt(pos[1]) - 1;
    return { file, rank };
  };

  const getPositionFromSquare = (file, rank) => {
    return String.fromCharCode('a'.charCodeAt(0) + file) + (rank + 1);
  };

  const getPieceAtSquare = (board, square) => {
    const entry = Object.entries(board).find(([_, pos]) => pos === square);
    return entry ? entry[0] : null;
  };

  const getPieceColor = (piece) => {
    if (!piece) return null;
    return piece[0] === piece[0].toUpperCase() ? 'white' : 'black';
  };

  const getPieceType = (piece) => {
    if (!piece) return null;
    return piece[0].toUpperCase();
  };

  // Check if a square is attacked by any piece of the given color
  const isSquareAttacked = (board, square, byColor) => {
    for (const [piece, pos] of Object.entries(board)) {
      if (getPieceColor(piece) !== byColor) continue;
      
      const { file, rank } = getSquareFromPosition(pos);
      const { file: targetFile, rank: targetRank } = getSquareFromPosition(square);
      const type = getPieceType(piece);
      const df = targetFile - file;
      const dr = targetRank - rank;
      
      // Pawn attacks (diagonal only)
      if (type === 'P') {
        const direction = byColor === 'white' ? 1 : -1;
        if (dr === direction && Math.abs(df) === 1) return true;
      }
      
      // Knight attacks
      if (type === 'N') {
        if ((Math.abs(df) === 2 && Math.abs(dr) === 1) || 
            (Math.abs(df) === 1 && Math.abs(dr) === 2)) return true;
      }
      
      // Rook/Queen straight line attacks
      if (type === 'R' || type === 'Q') {
        if (df === 0 || dr === 0) {
          const stepF = df === 0 ? 0 : (df > 0 ? 1 : -1);
          const stepR = dr === 0 ? 0 : (dr > 0 ? 1 : -1);
          let blocked = false;
          let f = file + stepF, r = rank + stepR;
          while (f !== targetFile || r !== targetRank) {
            const checkPos = getPositionFromSquare(f, r);
            if (getPieceAtSquare(board, checkPos)) {
              blocked = true;
              break;
            }
            f += stepF;
            r += stepR;
          }
          if (!blocked) return true;
        }
      }
      
      // Bishop/Queen diagonal attacks
      if (type === 'B' || type === 'Q') {
        if (Math.abs(df) === Math.abs(dr) && df !== 0) {
          const stepF = df > 0 ? 1 : -1;
          const stepR = dr > 0 ? 1 : -1;
          let blocked = false;
          let f = file + stepF, r = rank + stepR;
          while (f !== targetFile || r !== targetRank) {
            const checkPos = getPositionFromSquare(f, r);
            if (getPieceAtSquare(board, checkPos)) {
              blocked = true;
              break;
            }
            f += stepF;
            r += stepR;
          }
          if (!blocked) return true;
        }
      }
      
      // King attacks (adjacent squares)
      if (type === 'K') {
        if (Math.abs(df) <= 1 && Math.abs(dr) <= 1 && (df !== 0 || dr !== 0)) return true;
      }
    }
    return false;
  };

  // Check if a player's king is in check
  const isKingInCheck = (board, kingColor) => {
    const kingPiece = kingColor === 'white' ? 'K' : 'k';
    const kingPos = board[kingPiece];
    if (!kingPos) return false;
    
    const attackingColor = kingColor === 'white' ? 'black' : 'white';
    return isSquareAttacked(board, kingPos, attackingColor);
  };

  // Simulate a move and check if it leaves the king in check
  const wouldBeInCheck = (board, from, to, movingPiece) => {
    const newBoard = { ...board };
    const capturedPiece = getPieceAtSquare(newBoard, to);
    
    // Remove captured piece if any
    if (capturedPiece) {
      delete newBoard[capturedPiece];
    }
    
    // Move piece
    newBoard[movingPiece] = to;
    
    const movingColor = getPieceColor(movingPiece);
    return isKingInCheck(newBoard, movingColor);
  };

  // Check if player has any legal moves
  const hasLegalMoves = (board, color) => {
    for (const [piece, pos] of Object.entries(board)) {
      if (getPieceColor(piece) !== color) continue;
      const pseudoMoves = calculatePseudoLegalMoves(board, pos, piece);
      for (const move of pseudoMoves) {
        if (!wouldBeInCheck(board, pos, move, piece)) {
          return true;
        }
      }
    }
    return false;
  };

  // Get piece symbol from piece key (for multiplayer board)
  const getPieceSymbol = (pieceKey) => {
    if (!pieceKey) return '';
    const baseKey = pieceKey[0];
    const pieceData = CHESS_PIECES[baseKey] || CHESS_PIECES[baseKey.toLowerCase()];
    return pieceData?.symbol || '?';
  };

  // Handle square click in multiplayer mode
  const handleMultiplayerSquareClick = (square, pieceKey, matchData) => {
    if (!matchData?.is_your_turn) return;
    
    const playerColor = matchData.your_color;
    const board = matchData.board;
    
    // If a square is already selected
    if (chessSelectedSquare) {
      // Check if clicking on a legal move
      if (chessLegalMoves.includes(square)) {
        // Make the move
        makeChessMultiplayerMove(chessSelectedSquare, square);
        setChessSelectedSquare(null);
        setChessLegalMoves([]);
        return;
      }
      
      // Check if clicking on another of player's pieces
      if (pieceKey) {
        const pieceColor = pieceKey[0] === pieceKey[0].toUpperCase() ? 'white' : 'black';
        if (pieceColor === playerColor) {
          // Select this piece instead
          setChessSelectedSquare(square);
          const moves = calculatePseudoLegalMoves(board, square, pieceKey);
          const legalMoves = moves.filter(m => !wouldBeInCheck(board, square, m, pieceKey));
          setChessLegalMoves(legalMoves);
          return;
        }
      }
      
      // Deselect
      setChessSelectedSquare(null);
      setChessLegalMoves([]);
      return;
    }
    
    // If no square selected, try to select a piece
    if (pieceKey) {
      const pieceColor = pieceKey[0] === pieceKey[0].toUpperCase() ? 'white' : 'black';
      if (pieceColor === playerColor) {
        setChessSelectedSquare(square);
        const moves = calculatePseudoLegalMoves(board, square, pieceKey);
        const legalMoves = moves.filter(m => !wouldBeInCheck(board, square, m, pieceKey));
        setChessLegalMoves(legalMoves);
      }
    }
  };

  // ========== TV GAME MODE - Arrow Key Chess Control ==========
  // Refs to avoid dependency issues with callbacks
  const chessSquareClickRef = useRef(null);
  const multiplayerSquareClickRef = useRef(null);
  
  // Update refs when functions are available
  useEffect(() => {
    chessSquareClickRef.current = (square) => {
      // This will be called by TV game mode
      // We need to trigger a click on the square
      const squareBtn = document.querySelector(`[data-testid="chess-square-${square}"]`);
      if (squareBtn) squareBtn.click();
    };
  }, []);
  
  // TV Game Mode - Arrow key navigation for chess
  useEffect(() => {
    const isChessActive = chessMode === 'play-ai' || chessMode === 'play-friend' || 
                          (chessMode === 'play-friend-online' && chessMatchData?.status === 'playing');
    
    if (!isChessActive) return;
    
    const handleKeyDown = (e) => {
      // Arrow keys move cursor on board
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault();
        setTvGameMode(true);
        
        const flipControls = chessMatchData?.your_color === 'black';
        
        setChessCursorPos(prev => {
          let { file, rank } = prev;
          
          if (e.key === 'ArrowUp') {
            rank = flipControls ? Math.max(0, rank - 1) : Math.min(7, rank + 1);
          } else if (e.key === 'ArrowDown') {
            rank = flipControls ? Math.min(7, rank + 1) : Math.max(0, rank - 1);
          } else if (e.key === 'ArrowLeft') {
            file = flipControls ? Math.min(7, file + 1) : Math.max(0, file - 1);
          } else if (e.key === 'ArrowRight') {
            file = flipControls ? Math.max(0, file - 1) : Math.min(7, file + 1);
          }
          
          return { file, rank };
        });
      }
      
      // Enter/Space = Select or Move
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (chessSquareClickRef.current) {
          const square = String.fromCharCode('a'.charCodeAt(0) + chessCursorPos.file) + (chessCursorPos.rank + 1);
          chessSquareClickRef.current(square);
        }
      }
      
      // Escape/B button = Deselect
      if (e.key === 'Escape' || e.key === 'Backspace') {
        setChessSelectedSquare(null);
        setChessLegalMoves([]);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [chessMode, chessMatchData, chessCursorPos]);
  
  // Gamepad support for chess
  useEffect(() => {
    const isChessActive = chessMode === 'play-ai' || chessMode === 'play-friend' || 
                          (chessMode === 'play-friend-online' && chessMatchData?.status === 'playing');
    
    if (!isChessActive || typeof navigator === 'undefined' || !navigator.getGamepads) return;
    
    let animationFrame = null;
    let lastButtons = {};
    let lastStickMove = 0;
    
    const pollGamepad = () => {
      const gamepads = navigator.getGamepads();
      let gamepad = null;
      for (let i = 0; i < gamepads.length; i++) {
        if (gamepads[i]) {
          gamepad = gamepads[i];
          break;
        }
      }
      
      if (!gamepad) {
        animationFrame = requestAnimationFrame(pollGamepad);
        return;
      }
      
      const buttons = gamepad.buttons;
      const axes = gamepad.axes;
      const now = Date.now();
      
      const isNewPress = (idx) => {
        const pressed = buttons[idx]?.pressed;
        const was = lastButtons[idx];
        lastButtons[idx] = pressed;
        return pressed && !was;
      };
      
      const flipControls = chessMatchData?.your_color === 'black';
      
      // D-pad: 12=Up, 13=Down, 14=Left, 15=Right
      if (isNewPress(12)) {
        setTvGameMode(true);
        setChessCursorPos(p => ({ ...p, rank: flipControls ? Math.max(0, p.rank - 1) : Math.min(7, p.rank + 1) }));
      }
      if (isNewPress(13)) {
        setTvGameMode(true);
        setChessCursorPos(p => ({ ...p, rank: flipControls ? Math.min(7, p.rank + 1) : Math.max(0, p.rank - 1) }));
      }
      if (isNewPress(14)) {
        setTvGameMode(true);
        setChessCursorPos(p => ({ ...p, file: flipControls ? Math.min(7, p.file + 1) : Math.max(0, p.file - 1) }));
      }
      if (isNewPress(15)) {
        setTvGameMode(true);
        setChessCursorPos(p => ({ ...p, file: flipControls ? Math.max(0, p.file - 1) : Math.min(7, p.file + 1) }));
      }
      
      // Left stick navigation (with rate limiting)
      if (now - lastStickMove > 200) {
        const threshold = 0.5;
        if (Math.abs(axes[0]) > threshold || Math.abs(axes[1]) > threshold) {
          setTvGameMode(true);
          lastStickMove = now;
          
          if (axes[1] < -threshold) setChessCursorPos(p => ({ ...p, rank: flipControls ? Math.max(0, p.rank - 1) : Math.min(7, p.rank + 1) }));
          if (axes[1] > threshold) setChessCursorPos(p => ({ ...p, rank: flipControls ? Math.min(7, p.rank + 1) : Math.max(0, p.rank - 1) }));
          if (axes[0] < -threshold) setChessCursorPos(p => ({ ...p, file: flipControls ? Math.min(7, p.file + 1) : Math.max(0, p.file - 1) }));
          if (axes[0] > threshold) setChessCursorPos(p => ({ ...p, file: flipControls ? Math.max(0, p.file - 1) : Math.min(7, p.file + 1) }));
        }
      }
      
      // A button = Select/Move
      if (isNewPress(0) && chessSquareClickRef.current) {
        const square = String.fromCharCode('a'.charCodeAt(0) + chessCursorPos.file) + (chessCursorPos.rank + 1);
        chessSquareClickRef.current(square);
      }
      
      // B button = Deselect/Back
      if (isNewPress(1)) {
        setChessSelectedSquare(null);
        setChessLegalMoves([]);
      }
      
      animationFrame = requestAnimationFrame(pollGamepad);
    };
    
    animationFrame = requestAnimationFrame(pollGamepad);
    return () => { if (animationFrame) cancelAnimationFrame(animationFrame); };
  }, [chessMode, chessMatchData, chessCursorPos]);

  // Calculate pseudo-legal moves (moves ignoring check)
  const calculatePseudoLegalMoves = (board, square, piece) => {
    const moves = [];
    const { file, rank } = getSquareFromPosition(square);
    const color = getPieceColor(piece);
    const type = getPieceType(piece);

    const addMoveIfValid = (f, r, canCapture = true) => {
      if (f < 0 || f > 7 || r < 0 || r > 7) return false;
      const pos = getPositionFromSquare(f, r);
      const targetPiece = getPieceAtSquare(board, pos);
      
      if (!targetPiece) {
        moves.push(pos);
        return true;
      } else if (canCapture && getPieceColor(targetPiece) !== color) {
        moves.push(pos);
        return false;
      }
      return false;
    };

    // Pawn moves
    if (type === 'P') {
      const direction = color === 'white' ? 1 : -1;
      const startRank = color === 'white' ? 1 : 6;
      
      // Forward move
      const forwardPos = getPositionFromSquare(file, rank + direction);
      if (!getPieceAtSquare(board, forwardPos)) {
        moves.push(forwardPos);
        // Double move from start
        if (rank === startRank) {
          const doublePos = getPositionFromSquare(file, rank + 2 * direction);
          if (!getPieceAtSquare(board, doublePos)) {
            moves.push(doublePos);
          }
        }
      }
      // Captures
      [-1, 1].forEach(df => {
        const capturePos = getPositionFromSquare(file + df, rank + direction);
        const targetPiece = getPieceAtSquare(board, capturePos);
        if (targetPiece && getPieceColor(targetPiece) !== color) {
          moves.push(capturePos);
        }
      });
    }

    // Knight moves
    if (type === 'N') {
      [[-2, -1], [-2, 1], [-1, -2], [-1, 2], [1, -2], [1, 2], [2, -1], [2, 1]].forEach(([df, dr]) => {
        addMoveIfValid(file + df, rank + dr);
      });
    }

    // Rook moves
    if (type === 'R' || type === 'Q') {
      [[0, 1], [0, -1], [1, 0], [-1, 0]].forEach(([df, dr]) => {
        for (let i = 1; i < 8; i++) {
          if (!addMoveIfValid(file + df * i, rank + dr * i)) break;
          if (getPieceAtSquare(board, getPositionFromSquare(file + df * i, rank + dr * i))) break;
        }
      });
    }

    // Bishop moves
    if (type === 'B' || type === 'Q') {
      [[1, 1], [1, -1], [-1, 1], [-1, -1]].forEach(([df, dr]) => {
        for (let i = 1; i < 8; i++) {
          if (!addMoveIfValid(file + df * i, rank + dr * i)) break;
          if (getPieceAtSquare(board, getPositionFromSquare(file + df * i, rank + dr * i))) break;
        }
      });
    }

    // King moves
    if (type === 'K') {
      [[-1, -1], [-1, 0], [-1, 1], [0, -1], [0, 1], [1, -1], [1, 0], [1, 1]].forEach(([df, dr]) => {
        addMoveIfValid(file + df, rank + dr);
      });
    }

    return moves;
  };

  // Calculate truly legal moves (filtering out moves that leave king in check)
  const calculateLegalMoves = (board, square, piece) => {
    const pseudoMoves = calculatePseudoLegalMoves(board, square, piece);
    
    // Filter out moves that would leave our king in check
    return pseudoMoves.filter(to => !wouldBeInCheck(board, square, to, piece));
  };

  const handleChessSquareClick = (square) => {
    if (chessGameStatus === 'checkmate' || chessGameStatus === 'draw') return;
    
    const piece = getPieceAtSquare(chessGameBoard, square);
    const pieceColor = getPieceColor(piece);
    
    // If we have a selected piece and click a legal move square, make the move
    if (chessSelectedSquare && chessLegalMoves.includes(square)) {
      makeChessMove(chessSelectedSquare, square);
      return;
    }
    
    // In friend mode, allow selecting pieces of the current turn's color
    // In AI mode, only allow white (player) to select
    const canSelect = chessPlayMode === 'friend' 
      ? pieceColor === chessTurn 
      : pieceColor === 'white' && chessTurn === 'white';
    
    if (piece && canSelect) {
      setChessSelectedSquare(square);
      if (chessShowMoveHints) {
        setChessLegalMoves(calculateLegalMoves(chessGameBoard, square, piece));
      }
    } else {
      setChessSelectedSquare(null);
      setChessLegalMoves([]);
    }
  };

  const makeChessMove = (from, to) => {
    const piece = getPieceAtSquare(chessGameBoard, from);
    const capturedPiece = getPieceAtSquare(chessGameBoard, to);
    
    // Create new board
    const newBoard = { ...chessGameBoard };
    
    // Remove captured piece if any
    if (capturedPiece) {
      delete newBoard[capturedPiece];
    }
    
    // Move piece
    newBoard[piece] = to;
    
    setChessGameBoard(newBoard);
    setChessGameHistory(prev => [...prev, { from, to, piece, captured: capturedPiece }]);
    setChessSelectedSquare(null);
    setChessLegalMoves([]);
    
    // Determine opponent's color
    const opponentColor = chessTurn === 'white' ? 'black' : 'white';
    
    // Check if opponent's king is in check
    const opponentInCheck = isKingInCheck(newBoard, opponentColor);
    
    // Check if opponent has any legal moves
    const opponentCanMove = hasLegalMoves(newBoard, opponentColor);
    
    // Determine game status
    if (opponentInCheck && !opponentCanMove) {
      // Checkmate!
      setChessGameStatus('checkmate');
      toast.success(`Checkmate! ${chessTurn === 'white' ? 'White' : 'Black'} wins!`, {
        duration: 5000
      });
      return; // Don't switch turns - game is over
    } else if (!opponentInCheck && !opponentCanMove) {
      // Stalemate
      setChessGameStatus('draw');
      toast.info('Stalemate! The game is a draw.', { duration: 5000 });
      return; // Don't switch turns - game is over
    } else if (opponentInCheck) {
      // Just check
      setChessGameStatus('check');
      toast.info('Check!');
    } else {
      setChessGameStatus(null);
    }
    
    // Switch turns
    const nextTurn = chessTurn === 'white' ? 'black' : 'white';
    setChessTurn(nextTurn);
    
    // In AI mode, make AI move after player
    if (chessPlayMode === 'ai' && nextTurn === 'black') {
      setTimeout(() => makeAIMove(newBoard), 1000);
    }
  };

  const makeAIMove = (board) => {
    setChessAIThinking(true);
    
    // Simple AI: Make a random legal move with preference for captures
    const blackPieces = Object.entries(board).filter(([p]) => getPieceColor(p) === 'black');
    let allMoves = [];
    
    blackPieces.forEach(([piece, pos]) => {
      const moves = calculateLegalMoves(board, pos, piece);
      moves.forEach(to => {
        const captured = getPieceAtSquare(board, to);
        allMoves.push({ piece, from: pos, to, captured });
      });
    });
    
    if (allMoves.length === 0) {
      // No legal moves - check if it's checkmate or stalemate
      if (isKingInCheck(board, 'black')) {
        setChessGameStatus('checkmate');
        toast.success('Checkmate! White wins!', { duration: 5000 });
      } else {
        setChessGameStatus('draw');
        toast.info('Stalemate! The game is a draw.', { duration: 5000 });
      }
      setChessAIThinking(false);
      return;
    }
    
    // Prioritize captures, then center control
    allMoves.sort((a, b) => {
      if (a.captured && !b.captured) return -1;
      if (!a.captured && b.captured) return 1;
      return Math.random() - 0.5;
    });
    
    const move = allMoves[0];
    
    // Execute the move
    const newBoard = { ...board };
    if (move.captured) {
      delete newBoard[move.captured];
    }
    newBoard[move.piece] = move.to;
    
    setChessGameBoard(newBoard);
    setChessGameHistory(prev => [...prev, { from: move.from, to: move.to, piece: move.piece, captured: move.captured }]);
    
    // Check if white is in check/checkmate/stalemate
    const whiteInCheck = isKingInCheck(newBoard, 'white');
    const whiteCanMove = hasLegalMoves(newBoard, 'white');
    
    if (whiteInCheck && !whiteCanMove) {
      setChessGameStatus('checkmate');
      toast.success('Checkmate! Black wins!', { duration: 5000 });
      setChessAIThinking(false);
      return;
    } else if (!whiteInCheck && !whiteCanMove) {
      setChessGameStatus('draw');
      toast.info('Stalemate! The game is a draw.', { duration: 5000 });
      setChessAIThinking(false);
      return;
    } else if (whiteInCheck) {
      setChessGameStatus('check');
      toast.info('Check!');
    } else {
      setChessGameStatus(null);
    }
    
    setChessTurn('white');
    setChessAIThinking(false);
  };

  const getAIHint = () => {
    if (chessTurn !== 'white' || !chessGameBoard) return;
    
    const whitePieces = Object.entries(chessGameBoard).filter(([p]) => getPieceColor(p) === 'white');
    let bestMoves = [];
    
    whitePieces.forEach(([piece, pos]) => {
      const moves = calculateLegalMoves(chessGameBoard, pos, piece);
      moves.forEach(to => {
        const captured = getPieceAtSquare(chessGameBoard, to);
        if (captured) {
          bestMoves.push({ piece, from: pos, to });
        }
      });
    });
    
    if (bestMoves.length > 0) {
      const hint = bestMoves[0];
      setChessSelectedSquare(hint.from);
      setChessLegalMoves([hint.to]);
      toast.info(`Try moving ${getPieceType(hint.piece)} to capture!`);
    } else {
      // Just highlight a random piece with moves
      for (const [piece, pos] of whitePieces) {
        const moves = calculateLegalMoves(chessGameBoard, pos, piece);
        if (moves.length > 0) {
          setChessSelectedSquare(pos);
          setChessLegalMoves(moves);
          toast.info(`Try moving your ${CHESS_PIECES[getPieceType(piece)]?.name || 'piece'}!`);
          break;
        }
      }
    }
  };

  const renderInteractiveChessBoard = () => {
    const files = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
    const ranks = [8, 7, 6, 5, 4, 3, 2, 1];
    
    return (
      <div className="w-80 h-80 mx-auto border-2 border-amber-600 rounded-lg overflow-hidden relative shadow-lg">
        {/* TV Game Mode Controls Hint */}
        {tvGameMode && (
          <div className="absolute -top-8 left-0 right-0 flex items-center justify-center gap-3 text-xs text-slate-400">
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-slate-700 rounded text-[10px]">↑↓←→</kbd> Move
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-slate-700 rounded text-[10px]">A</kbd> Select
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-slate-700 rounded text-[10px]">B</kbd> Cancel
            </span>
          </div>
        )}
        <div className="grid grid-cols-8 gap-0 w-full h-full">
          {ranks.map((rank, rowIndex) => 
            files.map((file, colIndex) => {
              const square = `${file}${rank}`;
              const isLight = (files.indexOf(file) + rank) % 2 === 0;
              const isSelected = chessSelectedSquare === square;
              const isLegalMove = chessLegalMoves.includes(square);
              
              // TV cursor position (e4 format to col/row)
              const isCursor = tvGameMode && chessCursorPos.file === colIndex && chessCursorPos.rank === (7 - rowIndex);
              
              const piece = getPieceAtSquare(chessGameBoard, square);
              const pieceInfo = piece ? {
                ...CHESS_PIECES[getPieceType(piece)],
                color: getPieceColor(piece)
              } : null;
              
              // Check if this is a capture move
              const isCapture = isLegalMove && pieceInfo;
              
              // Get the SVG icon for the piece
              const PieceIcon = pieceInfo && TEMPLE_PIECE_SVGS[pieceInfo.icon];
              
              return (
                <button
                  key={square}
                  onClick={() => handleChessSquareClick(square)}
                  className={`aspect-square flex items-center justify-center transition-all relative ${
                    isLight ? 'bg-amber-100' : 'bg-amber-700'
                  } ${isSelected ? 'ring-4 ring-yellow-400 ring-inset bg-yellow-400/30' : ''} ${
                    isCursor ? 'ring-4 ring-cyan-400 ring-inset z-10' : ''
                  } ${
                    isLegalMove && !isCapture ? 'ring-2 ring-emerald-400 ring-inset bg-emerald-500/30' : ''
                  } ${isCapture ? 'ring-2 ring-red-500 ring-inset bg-red-500/40' : ''} ${
                    pieceInfo && !isSelected ? 'hover:brightness-110 cursor-pointer' : ''
                  }`}
                  disabled={chessAIThinking}
                  data-testid={`chess-square-${square}`}
                >
                  {/* TV Cursor indicator */}
                  {isCursor && (
                    <div className="absolute inset-1 border-2 border-cyan-400 rounded animate-pulse pointer-events-none" />
                  )}
                  {pieceInfo && PieceIcon && (
                    <div className="flex items-center justify-center">
                      {PieceIcon(28)}
                    </div>
                  )}
                  {pieceInfo && !PieceIcon && (
                    <span className={`text-2xl ${
                      pieceInfo.color === 'white' 
                        ? 'text-white drop-shadow-[0_2px_2px_rgba(0,0,0,0.8)]' 
                        : 'text-slate-900'
                    }`}>
                      {pieceInfo.symbol}
                    </span>
                  )}
                  {/* Green circle for legal move */}
                  {isLegalMove && !pieceInfo && (
                    <div className="w-4 h-4 rounded-full bg-emerald-500/80 shadow-lg shadow-emerald-500/50 animate-pulse" />
                  )}
                  {/* Red spark for capture */}
                  {isCapture && (
                    <div className="absolute inset-0 pointer-events-none">
                      <Sparkles className="absolute top-0.5 right-0.5 w-3 h-3 text-red-400 animate-pulse" />
                    </div>
                  )}
                </button>
              );
            })
          )}
        </div>
        
        {/* File labels */}
        <div className="absolute -bottom-5 left-0 right-0 flex justify-around text-xs text-slate-500">
          {files.map(f => <span key={f}>{f}</span>)}
        </div>
        {/* Rank labels */}
        <div className="absolute -left-4 top-0 bottom-0 flex flex-col justify-around text-xs text-slate-500">
          {ranks.map(r => <span key={r}>{r}</span>)}
        </div>
      </div>
    );
  };

  if (!courtyardUnlocked) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <Puzzle className="w-16 h-16 text-purple-400 mx-auto mb-4 opacity-50" />
          <h1 className="text-2xl font-philosopher font-bold text-white mb-2">
            Courtyard Locked
          </h1>
          <p className="text-slate-400 mb-6">
            Complete more lessons to unlock the Temple Courtyard!
          </p>
          <button
            onClick={() => navigate('/temple')}
            className="bg-white/10 hover:bg-white/20 text-white rounded-full px-6 py-3 transition-all"
          >
            Back to Temple
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F172A] px-3 sm:px-4 py-6 sm:py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
          <div className="flex items-center gap-3 sm:gap-4">
            <button
              onClick={() => navigate('/temple')}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors shrink-0"
              data-testid="back-to-temple"
            >
              <ArrowLeft className="w-5 sm:w-6 h-5 sm:h-6 text-slate-400" />
            </button>
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-philosopher font-bold text-white truncate">Temple Courtyard</h1>
              <p className="text-slate-400 text-sm sm:text-base">A place to explore, create, and play!</p>
            </div>
          </div>
          {selectedChild && (
            <div className="flex items-center gap-3 glass-panel px-3 py-2 rounded-full self-end sm:self-auto">
              {/* Full Body Avatar */}
              {selectedChild.avatar && Object.keys(selectedChild.avatar).length > 0 ? (
                <div className="relative" style={{ width: 48, height: 72 }}>
                  <FullBodyAvatarPreview 
                    avatar={selectedChild.avatar} 
                    size="sm" 
                    type="explorer"
                    isIdle={true}
                  />
                </div>
              ) : (
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white"
                  style={{ backgroundColor: selectedChild.avatar_color || '#818CF8' }}
                >
                  <User className="w-5 h-5" />
                </div>
              )}
              <div className="flex flex-col">
                <span className="text-white font-semibold text-sm">{selectedChild.name}</span>
                <div className="flex items-center gap-1 text-yellow-400 text-sm">
                  <Sparkles className="w-4 h-4" />
                  <span className="font-bold">{Object.values(sparks).reduce((a, b) => a + b, 0)}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Activity Selection */}
        {!activeActivity && (
          <div ref={activitiesRef} className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8" role="grid">
            {/* Puzzle Stones - BETA STABLE */}
            <button
              onClick={() => startActivityWithLimitCheck('puzzle', startPuzzle)}
              className="glass-panel p-5 sm:p-8 rounded-2xl text-left hover:border-purple-500/50 transition-all group tv-card"
              data-testid="activity-puzzle"
              data-nav-item
              tabIndex={0}
            >
              <Puzzle className="w-10 sm:w-12 h-10 sm:h-12 text-purple-400 mb-3 sm:mb-4 group-hover:scale-110 transition-transform" />
              <h2 className="text-xl sm:text-2xl font-philosopher font-bold text-white mb-2">Puzzle Stones</h2>
              <p className="text-slate-400 text-sm sm:text-base">Solve magical logic puzzles and earn sparks!</p>
              <div className="mt-3 sm:mt-4 flex items-center gap-2 text-purple-400 text-sm">
                <Trophy className="w-4 h-4" />
                <span>{puzzlesSolved} puzzles solved</span>
              </div>
            </button>
            
            {/* Chess Garden - BETA STABLE */}
            <button
              onClick={() => startActivityWithLimitCheck('chess-garden', startChessGarden)}
              className="glass-panel p-5 sm:p-8 rounded-2xl text-left hover:border-amber-500/50 transition-all group relative overflow-hidden tv-card"
              data-testid="activity-chess-garden"
              data-nav-item
              tabIndex={0}
            >
              {/* Play Mode badge */}
              <div className="absolute top-2 sm:top-3 right-2 sm:right-3 px-2 py-1 bg-green-500/20 border border-green-500/30 rounded-full">
                <span className="text-green-400 text-xs font-medium">Play Mode</span>
              </div>
              
              <Crown className="w-10 sm:w-12 h-10 sm:h-12 text-amber-500 mb-3 sm:mb-4 group-hover:scale-110 transition-transform" />
              <h2 className="text-xl sm:text-2xl font-philosopher font-bold text-white mb-2">Chess Garden</h2>
              <p className="text-slate-400 text-sm sm:text-base">Learn chess, solve puzzles, and play games!</p>
              <div className="mt-3 sm:mt-4 flex flex-wrap items-center gap-3 sm:gap-4 text-sm">
                <span className="text-amber-500 flex items-center gap-1">
                  <BookOpen className="w-4 h-4" />
                  Learn
                </span>
                <span className="text-purple-400 flex items-center gap-1">
                  <Puzzle className="w-4 h-4" />
                  Practice
                </span>
                <span className="text-green-400 flex items-center gap-1">
                  <Crown className="w-4 h-4" />
                  Play
                </span>
              </div>
            </button>
            
            {/* Typing Dojo - BETA STABLE - Link to dedicated page */}
            <button
              onClick={() => navigate('/typing-dojo')}
              className="glass-panel p-5 sm:p-8 rounded-2xl text-left hover:border-cyan-500/50 transition-all group tv-card" data-nav-item tabIndex={0}
              data-testid="activity-typing-dojo"
            >
              <Keyboard className="w-10 sm:w-12 h-10 sm:h-12 text-cyan-400 mb-3 sm:mb-4 group-hover:scale-110 transition-transform" />
              <h2 className="text-xl sm:text-2xl font-philosopher font-bold text-white mb-2">Typing Dojo</h2>
              <p className="text-slate-400 text-sm sm:text-base">Practice typing with fun challenges!</p>
              <div className="mt-3 sm:mt-4 flex items-center gap-2 text-cyan-400 text-sm">
                <Sparkles className="w-4 h-4" />
                <span>Improve your keyboard skills</span>
              </div>
            </button>

            {/* Music Learning Garden - NEW FEATURE */}
            <button
              onClick={() => startActivityWithLimitCheck('music-learning')}
              className="glass-panel p-5 sm:p-8 rounded-2xl text-left hover:border-purple-500/50 transition-all group relative overflow-hidden tv-card" data-nav-item tabIndex={0}
              data-testid="activity-music-learning"
            >
              {/* New badge */}
              <div className="absolute top-2 sm:top-3 right-2 sm:right-3 px-2 py-1 bg-gradient-to-r from-purple-500/30 to-cyan-500/30 border border-purple-500/50 rounded-full">
                <span className="text-purple-300 text-xs font-medium flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> New
                </span>
              </div>
              
              <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-purple-500/5 to-cyan-500/5" />
              <Music className="w-10 sm:w-12 h-10 sm:h-12 text-purple-400 mb-3 sm:mb-4 group-hover:scale-110 transition-transform" />
              <h2 className="text-xl sm:text-2xl font-philosopher font-bold text-white mb-2">Music Learning Garden</h2>
              <p className="text-slate-400 text-sm sm:text-base">Learn to read music through interactive activities!</p>
              <div className="mt-3 sm:mt-4 flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm">
                <span className="text-purple-400 flex items-center gap-1">
                  🎼 Staff
                </span>
                <span className="text-cyan-400 flex items-center gap-1">
                  🥁 Rhythm
                </span>
                <span className="text-amber-400 flex items-center gap-1">
                  ⭐ Play
                </span>
                <span className="text-pink-400 flex items-center gap-1">
                  ✨ Compose
                </span>
              </div>
            </button>

            {/* Game Forge - NEW FEATURE */}
            <button
              onClick={() => startActivityWithLimitCheck('game-forge')}
              className="glass-panel p-5 sm:p-8 rounded-2xl text-left hover:border-amber-500/50 transition-all group relative overflow-hidden tv-activity"
              data-testid="activity-game-forge"
              data-nav-item
              tabIndex={0}
            >
              {/* New badge */}
              <div className="absolute top-2 sm:top-3 right-2 sm:right-3 px-2 py-1 bg-gradient-to-r from-amber-500/30 to-orange-500/30 border border-amber-500/50 rounded-full">
                <span className="text-amber-300 text-xs font-medium flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> New
                </span>
              </div>
              
              <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-amber-500/5 to-orange-500/5" />
              <Gamepad2 className="w-10 sm:w-12 h-10 sm:h-12 text-amber-400 mb-3 sm:mb-4 group-hover:scale-110 transition-transform" />
              <h2 className="text-xl sm:text-2xl font-philosopher font-bold text-white mb-2">Game Forge</h2>
              <p className="text-slate-400 text-sm sm:text-base">Build and play simple 8-bit games!</p>
              <div className="mt-3 sm:mt-4 flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm">
                <span className="text-amber-400 flex items-center gap-1">
                  🎮 Build
                </span>
                <span className="text-green-400 flex items-center gap-1">
                  ▶️ Test
                </span>
                <span className="text-blue-400 flex items-center gap-1">
                  💾 Save
                </span>
                <span className="text-purple-400 flex items-center gap-1">
                  ⭐ +20 Sparks
                </span>
              </div>
            </button>

            {/* Temple Listens - Reading Area */}
            <button
              onClick={() => startActivityWithLimitCheck('temple-listens')}
              className="glass-panel p-5 sm:p-8 rounded-2xl text-left hover:border-cyan-500/50 transition-all group relative overflow-hidden tv-activity"
              data-testid="activity-temple-listens"
              data-nav-item
              tabIndex={0}
            >
              {/* New badge */}
              <div className="absolute top-2 sm:top-3 right-2 sm:right-3 px-2 py-1 bg-gradient-to-r from-cyan-500/30 to-teal-500/30 border border-cyan-500/50 rounded-full">
                <span className="text-cyan-300 text-xs font-medium flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> New
                </span>
              </div>
              
              <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-cyan-500/5 to-teal-500/5" />
              <Mic className="w-10 sm:w-12 h-10 sm:h-12 text-cyan-400 mb-3 sm:mb-4 group-hover:scale-110 transition-transform" />
              <h2 className="text-xl sm:text-2xl font-philosopher font-bold text-white mb-2">Temple Listens</h2>
              <p className="text-slate-400 text-sm sm:text-base">Read stories aloud with Aret'Hera listening!</p>
              <div className="mt-3 sm:mt-4 flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm">
                <span className="text-cyan-400 flex items-center gap-1">
                  📖 Read
                </span>
                <span className="text-purple-400 flex items-center gap-1">
                  🦉 Listen
                </span>
                <span className="text-green-400 flex items-center gap-1">
                  ✨ Learn
                </span>
                <span className="text-amber-400 flex items-center gap-1">
                  ⭐ +10 Sparks
                </span>
              </div>
            </button>

            {/* Spark Store - Spend your sparks! */}
            <button
              onClick={() => setActiveActivity('spark-store')}
              className="glass-panel p-5 sm:p-8 rounded-2xl text-left hover:border-pink-500/50 transition-all group relative overflow-hidden tv-activity"
              data-testid="activity-spark-store"
              data-nav-item
              tabIndex={0}
            >
              {/* New badge */}
              <div className="absolute top-2 sm:top-3 right-2 sm:right-3 px-2 py-1 bg-gradient-to-r from-pink-500/30 to-purple-500/30 border border-pink-500/50 rounded-full">
                <span className="text-pink-300 text-xs font-medium flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> New
                </span>
              </div>
              
              <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-pink-500/5 to-purple-500/5" />
              <ShoppingBag className="w-10 sm:w-12 h-10 sm:h-12 text-pink-400 mb-3 sm:mb-4 group-hover:scale-110 transition-transform" />
              <h2 className="text-xl sm:text-2xl font-philosopher font-bold text-white mb-2">Spark Store</h2>
              <p className="text-slate-400 text-sm sm:text-base">Spend your sparks on cool avatar items!</p>
              <div className="mt-3 sm:mt-4 flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm">
                <span className="text-pink-400 flex items-center gap-1">
                  👕 Outfits
                </span>
                <span className="text-purple-400 flex items-center gap-1">
                  ✂️ Hairstyles
                </span>
                <span className="text-amber-400 flex items-center gap-1">
                  👑 Accessories
                </span>
                <span className="text-green-400 flex items-center gap-1">
                  🎮 Game Items
                </span>
              </div>
            </button>

            {/* BETA NOTE - Show all portals so testers can see full concept */}
            <div className="col-span-full mb-4 px-3 sm:px-4 py-2 sm:py-3 bg-indigo-500/10 border border-indigo-500/30 rounded-lg">
              <p className="text-indigo-300 text-xs sm:text-sm">
                <span className="font-medium">Welcome, Explorer!</span> You're one of the first to explore the Temple. Some areas are still being built — your feedback helps us grow!
              </p>
            </div>

            {/* Constellation Builder - Available to all beta users */}
            <button
              onClick={() => startActivityWithLimitCheck('constellation')}
              className="glass-panel p-5 sm:p-8 rounded-2xl text-left hover:border-blue-500/50 transition-all group"
              data-testid="activity-constellation"
            >
              <Star className="w-10 sm:w-12 h-10 sm:h-12 text-blue-400 mb-3 sm:mb-4 group-hover:scale-110 transition-transform" />
              <h2 className="text-xl sm:text-2xl font-philosopher font-bold text-white mb-2">Constellation Builder</h2>
              <p className="text-slate-400 text-sm sm:text-base">Place stars in the sky and connect them to create patterns!</p>
              <div className="mt-3 sm:mt-4 flex items-center gap-2 text-blue-400 text-sm">
                <Sparkles className="w-4 h-4" />
                <span>Create your own night sky</span>
              </div>
            </button>

            {/* My Constellations - Available to all beta users */}
            <button
              onClick={() => startActivityWithLimitCheck('my-constellations')}
              className="glass-panel p-5 sm:p-8 rounded-2xl text-left hover:border-indigo-500/50 transition-all group"
              data-testid="activity-my-constellations"
            >
              <Eye className="w-10 sm:w-12 h-10 sm:h-12 text-indigo-400 mb-3 sm:mb-4 group-hover:scale-110 transition-transform" />
              <h2 className="text-xl sm:text-2xl font-philosopher font-bold text-white mb-2">My Constellations</h2>
              <p className="text-slate-400 text-sm sm:text-base">View and revisit the constellations you've created!</p>
              <div className="mt-4 flex items-center gap-2 text-indigo-400 text-sm">
                <Star className="w-4 h-4" />
                <span>Your Temple Sky collection</span>
              </div>
            </button>

            {/* Beat Forge - Available to all beta users */}
            <button
              onClick={() => startActivityWithLimitCheck('beat-forge')}
              className="glass-panel p-5 sm:p-8 rounded-2xl text-left hover:border-pink-500/50 transition-all group"
              data-testid="activity-beat-forge"
            >
              <Music className="w-10 sm:w-12 h-10 sm:h-12 text-pink-400 mb-3 sm:mb-4 group-hover:scale-110 transition-transform" />
              <h2 className="text-xl sm:text-2xl font-philosopher font-bold text-white mb-2">Beat Forge</h2>
              <p className="text-slate-400 text-sm sm:text-base">Create musical beats and rhythms!</p>
              <div className="mt-3 sm:mt-4 flex items-center gap-2 text-pink-400 text-sm">
                <Sparkles className="w-4 h-4" />
                <span>+10 sparks for each beat</span>
              </div>
            </button>

            {/* Song Forge - Create songs with vocals */}
            <button
              onClick={() => startActivityWithLimitCheck('song-forge')}
              className="glass-panel p-5 sm:p-8 rounded-2xl text-left hover:border-fuchsia-500/50 transition-all group relative"
              data-testid="activity-song-forge"
            >
              <span className="absolute top-2 right-2 px-2 py-0.5 bg-fuchsia-500/30 text-fuchsia-300 text-xs rounded-full">New</span>
              <Mic className="w-10 sm:w-12 h-10 sm:h-12 text-fuchsia-400 mb-3 sm:mb-4 group-hover:scale-110 transition-transform" />
              <h2 className="text-xl sm:text-2xl font-philosopher font-bold text-white mb-2">Song Forge</h2>
              <p className="text-slate-400 text-sm sm:text-base">Add your voice to beats and make songs!</p>
              <div className="mt-3 sm:mt-4 flex items-center gap-2 text-fuchsia-400 text-sm">
                <Sparkles className="w-4 h-4" />
                <span>+15 sparks for each song</span>
              </div>
            </button>

            {/* My Beats - Available to all beta users */}
            <button
              onClick={() => startActivityWithLimitCheck('my-beats')}
              className="glass-panel p-5 sm:p-8 rounded-2xl text-left hover:border-rose-500/50 transition-all group"
              data-testid="activity-my-beats"
            >
              <Volume2 className="w-10 sm:w-12 h-10 sm:h-12 text-rose-400 mb-3 sm:mb-4 group-hover:scale-110 transition-transform" />
              <h2 className="text-xl sm:text-2xl font-philosopher font-bold text-white mb-2">My Beats</h2>
              <p className="text-slate-400 text-sm sm:text-base">Listen to and manage your saved beats!</p>
              <div className="mt-3 sm:mt-4 flex items-center gap-2 text-rose-400 text-sm">
                <Music className="w-4 h-4" />
                <span>Your rhythm collection</span>
              </div>
            </button>

            {/* Story Seeds - Available to all beta users */}
            <button
              onClick={() => startActivityWithLimitCheck('story-seeds')}
              className="glass-panel p-5 sm:p-8 rounded-2xl text-left hover:border-amber-500/50 transition-all group tv-card" data-nav-item tabIndex={0}
              data-testid="activity-story-seeds"
            >
              <BookOpen className="w-10 sm:w-12 h-10 sm:h-12 text-amber-400 mb-3 sm:mb-4 group-hover:scale-110 transition-transform" />
              <h2 className="text-xl sm:text-2xl font-philosopher font-bold text-white mb-2">Story Seeds</h2>
              <p className="text-slate-400 text-sm sm:text-base">Continue magical stories with your imagination!</p>
              <div className="mt-3 sm:mt-4 flex items-center gap-2 text-amber-400 text-sm">
                <Sparkles className="w-4 h-4" />
                <span>+8 sparks for each story</span>
              </div>
            </button>
            
            {/* Music Forge - Available to all beta users */}
            <button
              onClick={() => startActivityWithLimitCheck('music-forge', startMusicForge)}
              className="glass-panel p-5 sm:p-8 rounded-2xl text-left hover:border-pink-500/50 transition-all group"
              data-testid="activity-music-forge"
            >
              <Music className="w-10 sm:w-12 h-10 sm:h-12 text-pink-400 mb-3 sm:mb-4 group-hover:scale-110 transition-transform" />
              <h2 className="text-xl sm:text-2xl font-philosopher font-bold text-white mb-2">Music Forge</h2>
              <p className="text-slate-400 text-sm sm:text-base">Learn rhythm patterns and create beats!</p>
              <div className="mt-3 sm:mt-4 flex items-center gap-2 text-pink-400 text-sm">
                <Sparkles className="w-4 h-4" />
                <span>+5 sparks per rhythm</span>
              </div>
            </button>
            
            {/* Vibe Coding Garden - Available to all beta users */}
            <button
              onClick={() => startActivityWithLimitCheck('coding-garden')}
              className="glass-panel p-5 sm:p-8 rounded-2xl text-left hover:border-purple-500/50 transition-all group"
              data-testid="activity-coding-garden"
            >
              <Code className="w-10 sm:w-12 h-10 sm:h-12 text-purple-400 mb-3 sm:mb-4 group-hover:scale-110 transition-transform" />
              <h2 className="text-xl sm:text-2xl font-philosopher font-bold text-white mb-2">Vibe Coding Garden</h2>
              <p className="text-slate-400 text-sm sm:text-base">Learn coding with visual blocks!</p>
              <div className="mt-3 sm:mt-4 flex items-center gap-2 text-purple-400 text-sm">
                <Sparkles className="w-4 h-4" />
                <span>+5-10 sparks per challenge</span>
              </div>
            </button>
          </div>
        )}

        {/* Puzzle Activity - Enhanced with dynamic puzzles */}
        {activeActivity === 'puzzle' && currentPuzzle && (
          <div className="glass-panel p-4 sm:p-8 rounded-2xl">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
              <h2 className="text-xl font-philosopher font-bold text-white flex items-center gap-2">
                <Puzzle className="w-6 h-6 text-purple-400" />
                {currentPuzzle.type === 'pattern' ? 'Pattern Puzzle' : 
                 currentPuzzle.type === 'math' ? 'Math Magic' :
                 currentPuzzle.type === 'odd_one_out' ? 'Odd One Out' :
                 currentPuzzle.type === 'sequence' ? 'Number Sequence' : 'Puzzle Stone'}
              </h2>
              <div className="flex items-center gap-3">
                <span className="text-purple-400 text-sm">Difficulty: {'⭐'.repeat(currentPuzzle.difficulty)}</span>
                <span className="text-amber-400 text-sm">+{currentPuzzle.sparks} sparks</span>
              </div>
            </div>

            {/* Pattern puzzle with visual sequence */}
            {currentPuzzle.type === 'pattern' && currentPuzzle.sequence && (
              <div className="mb-6">
                <div className="flex items-center justify-center gap-2 flex-wrap mb-4">
                  {currentPuzzle.sequence.map((item, i) => (
                    <div
                      key={i}
                      className="w-12 h-12 rounded-lg flex items-center justify-center text-2xl"
                      style={{ backgroundColor: PUZZLE_COLORS[item.color]?.bg || '#64748B' }}
                    >
                      {PUZZLE_SHAPES[item.shape]?.emoji || '?'}
                    </div>
                  ))}
                  <div className="w-12 h-12 rounded-lg border-2 border-dashed border-purple-500 flex items-center justify-center text-purple-400 text-2xl">
                    ?
                  </div>
                </div>
              </div>
            )}
            
            {/* Odd one out visual display */}
            {currentPuzzle.type === 'odd_one_out' && currentPuzzle.items && (
              <div className="mb-6">
                <div className="flex items-center justify-center gap-3 flex-wrap mb-4">
                  {currentPuzzle.items.map((item, i) => (
                    <div
                      key={i}
                      className="flex flex-col items-center"
                    >
                      <div
                        className="w-14 h-14 rounded-lg flex items-center justify-center text-2xl"
                        style={{ backgroundColor: PUZZLE_COLORS[item.color]?.bg || '#64748B' }}
                      >
                        {PUZZLE_SHAPES[item.shape]?.emoji || '?'}
                      </div>
                      <span className="text-slate-500 text-xs mt-1">{i + 1}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <p className="text-2xl text-white text-center mb-8">{currentPuzzle.question}</p>
            
            {/* Hint display */}
            {showHint && currentPuzzle.hint && (
              <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl flex items-start gap-3">
                <Lightbulb className="w-6 h-6 text-amber-400 flex-shrink-0" />
                <p className="text-amber-300">{currentPuzzle.hint}</p>
              </div>
            )}

            {puzzleResult === null || puzzleResult === false ? (
              <div>
                {/* Options grid */}
                <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
                  {currentPuzzle.options.map((option, i) => {
                    // Render pattern options as colored shapes
                    if (currentPuzzle.type === 'pattern' && typeof option === 'object') {
                      return (
                        <button
                          key={i}
                          onClick={() => submitPuzzleAnswer(option)}
                          disabled={puzzleResult !== null && puzzleResult !== false}
                          className="p-4 bg-slate-800/50 hover:bg-purple-500/20 border border-white/10 hover:border-purple-500/50 rounded-xl transition-all flex items-center justify-center"
                          data-testid={`puzzle-option-${i}`}
                        >
                          <div
                            className="w-10 h-10 rounded-lg flex items-center justify-center text-xl"
                            style={{ backgroundColor: PUZZLE_COLORS[option.color]?.bg || '#64748B' }}
                          >
                            {PUZZLE_SHAPES[option.shape]?.emoji || '?'}
                          </div>
                        </button>
                      );
                    }
                    return (
                      <button
                        key={i}
                        onClick={() => submitPuzzleAnswer(option)}
                        disabled={puzzleResult !== null && puzzleResult !== false}
                        className="p-4 bg-slate-800/50 hover:bg-purple-500/20 border border-white/10 hover:border-purple-500/50 rounded-xl text-white text-xl transition-all disabled:opacity-50"
                        data-testid={`puzzle-option-${i}`}
                      >
                        {option}
                      </button>
                    );
                  })}
                </div>
                
                {/* Hint button (before showing hint automatically) */}
                {!showHint && wrongAttempts === 0 && (
                  <button
                    onClick={() => setShowHint(true)}
                    className="mt-4 flex items-center gap-2 mx-auto text-slate-400 hover:text-amber-400 transition-colors text-sm"
                  >
                    <Lightbulb className="w-4 h-4" />
                    Need a hint?
                  </button>
                )}
                
                {/* Retry button after wrong answer */}
                {puzzleResult === false && (
                  <div className="mt-6 text-center">
                    <p className="text-red-400 mb-3">Not quite right! Try again.</p>
                    <button
                      onClick={retryPuzzle}
                      className="flex items-center gap-2 mx-auto px-6 py-3 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 rounded-full transition-all"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Try Again
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center">
                <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-green-500/20">
                  <Check className="w-8 h-8 text-green-400" />
                </div>
                <p className="text-xl mb-4 text-green-400">
                  Correct! +{currentPuzzle.sparks} sparks
                </p>
                <button
                  onClick={startPuzzle}
                  className="flex items-center gap-2 mx-auto px-6 py-3 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 rounded-full transition-all"
                >
                  <RefreshCw className="w-4 h-4" />
                  Next Puzzle
                </button>
              </div>
            )}

            <div className="mt-8 text-center">
              <button
                onClick={() => {
                  setActiveActivity(null);
                  setCurrentPuzzle(null);
                }}
                className="text-slate-400 hover:text-white transition-colors"
              >
                ← Back to Courtyard
              </button>
            </div>
          </div>
        )}

        {/* Constellation Builder Activity */}
        {activeActivity === 'constellation' && (
          <div className="glass-panel p-6 rounded-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-philosopher font-bold text-white flex items-center gap-2">
                <Star className="w-6 h-6 text-blue-400" />
                Constellation Builder
              </h2>
              {!selectedConstellation && (
                <button
                  onClick={() => setActiveActivity(null)}
                  className="text-slate-400 hover:text-white transition-colors text-sm"
                >
                  ← Back
                </button>
              )}
            </div>

            {!selectedConstellation ? (
              <div className="grid md:grid-cols-3 gap-4">
                {CONSTELLATIONS.map(constellation => (
                  <button
                    key={constellation.id}
                    onClick={() => setSelectedConstellation(constellation)}
                    className="p-6 bg-slate-900/50 hover:bg-blue-500/10 border border-white/10 hover:border-blue-500/30 rounded-xl text-left transition-all"
                    data-testid={`constellation-${constellation.id}`}
                  >
                    <h3 className="text-white font-semibold mb-2">{constellation.name}</h3>
                    <p className="text-slate-400 text-sm mb-3">{constellation.hint}</p>
                    <div className="flex items-center gap-2 text-yellow-400 text-sm">
                      <Sparkles className="w-4 h-4" />
                      <span>+{constellation.sparks} sparks</span>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <ConstellationLearningFlow
                constellation={selectedConstellation}
                onComplete={handleConstellationComplete}
                onBack={() => setSelectedConstellation(null)}
                childId={selectedChild?.id}
              />
            )}
          </div>
        )}

        {/* My Constellations Gallery */}
        {activeActivity === 'my-constellations' && (
          <MyConstellationsGallery
            childId={selectedChild?.id}
            onBack={() => setActiveActivity(null)}
          />
        )}

        {/* Beat Forge */}
        {activeActivity === 'beat-forge' && (
          <BeatForge
            childId={selectedChild?.id}
            onComplete={(sparksEarned) => {
              setSparks(prev => ({ ...prev, creativity: (prev.creativity || 0) + sparksEarned }));
            }}
            onBack={() => setActiveActivity(null)}
            onGoToSongForge={(beat) => {
              setBeatForSongForge(beat);
              setActiveActivity('song-forge');
            }}
          />
        )}

        {/* Song Forge */}
        {activeActivity === 'song-forge' && (
          <SongForge
            childId={selectedChild?.id}
            onComplete={(sparksEarned) => {
              setSparks(prev => ({ ...prev, creativity: (prev.creativity || 0) + sparksEarned }));
            }}
            onBack={() => {
              setBeatForSongForge(null);
              setActiveActivity(null);
            }}
            initialBeat={beatForSongForge}
          />
        )}

        {/* My Beats Gallery */}
        {activeActivity === 'my-beats' && (
          <MyBeatsGallery
            childId={selectedChild?.id}
            onBack={() => setActiveActivity(null)}
          />
        )}

        {/* Story Seeds */}
        {activeActivity === 'story-seeds' && (
          <StorySeeds
            childId={selectedChild?.id}
            childName={selectedChild?.name}
            onComplete={(sparksEarned) => {
              setSparks(prev => ({ ...prev, creativity: (prev.creativity || 0) + sparksEarned }));
            }}
            onBack={() => setActiveActivity(null)}
          />
        )}

        {/* Music Forge */}
        {activeActivity === 'music-forge' && currentRhythm && (
          <div className="glass-panel p-8 rounded-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-philosopher font-bold text-white flex items-center gap-2">
                <Music className="w-6 h-6 text-pink-400" />
                Music Forge - {currentRhythm.name}
              </h2>
              <div className="flex items-center gap-3">
                <span className="text-pink-400 text-sm capitalize">Level: {rhythmLevel}</span>
                <span className="text-amber-400 text-sm">+5 sparks</span>
              </div>
            </div>
            
            {/* Rhythm visualization */}
            <div className="bg-slate-900/50 rounded-xl p-6 mb-6">
              <div className="flex justify-center gap-3 mb-4">
                {currentRhythm.pattern.map((beat, i) => (
                  <div
                    key={i}
                    className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                      beat === 1 
                        ? 'bg-pink-500/30 border-2 border-pink-400' 
                        : 'bg-slate-800 border-2 border-slate-700'
                    } ${rhythmPhase === 'listen' && beat === 1 ? 'animate-pulse' : ''}`}
                  >
                    {beat === 1 && <span className="text-xl">🥁</span>}
                  </div>
                ))}
              </div>
              
              <p className="text-center text-slate-400 text-sm">
                {rhythmPhase === 'listen' && 'Listen to the rhythm...'}
                {rhythmPhase === 'play' && 'Now tap the rhythm back!'}
                {rhythmPhase === 'result' && `Score: ${rhythmScore}%`}
              </p>
            </div>
            
            {/* Tap button */}
            {rhythmPhase === 'play' && (
              <button
                onClick={handleRhythmTap}
                className="w-full h-24 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-2xl text-2xl font-bold hover:opacity-90 transition-all active:scale-95"
                data-testid="rhythm-tap-btn"
              >
                TAP! 🥁
              </button>
            )}
            
            {/* Result */}
            {rhythmPhase === 'result' && (
              <div className="text-center">
                <div className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${
                  rhythmScore >= 70 ? 'bg-green-500/20' : 'bg-amber-500/20'
                }`}>
                  {rhythmScore >= 70 ? (
                    <Check className="w-10 h-10 text-green-400" />
                  ) : (
                    <Music className="w-10 h-10 text-amber-400" />
                  )}
                </div>
                <p className={`text-xl mb-4 ${rhythmScore >= 70 ? 'text-green-400' : 'text-amber-400'}`}>
                  {rhythmScore >= 70 ? 'Great rhythm! +5 sparks' : 'Good try! Practice makes perfect'}
                </p>
                <button
                  onClick={startMusicForge}
                  className="px-6 py-3 bg-pink-500/20 hover:bg-pink-500/30 text-pink-300 rounded-full transition-all"
                >
                  <RefreshCw className="w-4 h-4 inline mr-2" />
                  Try Another Rhythm
                </button>
              </div>
            )}
            
            {/* Level selector */}
            <div className="mt-6 flex justify-center gap-2">
              {['beginner', 'intermediate', 'advanced'].map(level => (
                <button
                  key={level}
                  onClick={() => setRhythmLevel(level)}
                  className={`px-4 py-2 rounded-lg text-sm capitalize transition-all ${
                    rhythmLevel === level 
                      ? 'bg-pink-500/30 text-pink-300 border border-pink-500' 
                      : 'bg-slate-800 text-slate-400 hover:text-white'
                  }`}
                >
                  {level}
                </button>
              ))}
            </div>
            
            <div className="mt-6 text-center">
              <button
                onClick={() => {
                  setActiveActivity(null);
                  setMusicForgeActive(false);
                }}
                className="text-slate-400 hover:text-white transition-colors"
              >
                ← Back to Courtyard
              </button>
            </div>
          </div>
        )}

        {/* Chess Garden - Enhanced Learning Mode */}
        {activeActivity === 'chess-garden' && chessActive && (
          <div className="glass-panel p-4 sm:p-8 rounded-2xl">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
              <h2 className="text-lg sm:text-xl font-philosopher font-bold text-white flex items-center gap-2">
                <Crown className="w-5 sm:w-6 h-5 sm:h-6 text-amber-500" />
                Chess Garden
              </h2>
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs sm:text-sm">
                {chessMode === 'puzzle' && currentChessPuzzle && (
                  <>
                    <span className="text-amber-500">Level {currentChessPuzzle.level}</span>
                    <span className="text-amber-400">+{currentChessPuzzle.sparks} sparks</span>
                  </>
                )}
                <span className="text-slate-400">{chessPuzzlesSolved} puzzles solved</span>
              </div>
            </div>
            
            {/* INTRO MODE - Welcome & Chess Overview */}
            {chessMode === 'intro' && (
              <div className="text-center">
                <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-amber-400 to-amber-600 rounded-2xl flex items-center justify-center shadow-lg shadow-amber-500/30">
                  <span className="text-5xl">♔</span>
                </div>
                
                <h3 className="text-2xl font-philosopher font-bold text-white mb-4">
                  Welcome to the Chess Garden!
                </h3>
                
                <div className="bg-slate-900/50 p-6 rounded-xl mb-6 max-w-lg mx-auto text-left">
                  <p className="text-slate-300 mb-4">
                    <strong className="text-amber-400">The Goal of Chess:</strong> Protect your King while trying to trap (checkmate) your opponent's King!
                  </p>
                  <p className="text-slate-300 mb-4">
                    <strong className="text-amber-400">Each piece is special:</strong> They all move in different ways across the board.
                  </p>
                  <p className="text-slate-400 text-sm">
                    Let's learn about the chess pieces before we start solving puzzles!
                  </p>
                </div>
                
                {/* Visual board preview */}
                <div className="mb-6">
                  <p className="text-slate-400 text-sm mb-3">A chess board with some pieces:</p>
                  {renderChessBoard({ K: 'e1', Q: 'd1', R: 'a1', N: 'g1', k: 'e8', q: 'd8', r: 'h8', n: 'b8' }, [])}
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={startChessLearning}
                    className="px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-xl font-semibold hover:opacity-90 transition-all"
                    data-testid="start-chess-learning-btn"
                  >
                    <BookOpen className="w-5 h-5 inline mr-2" />
                    Learn the Pieces
                  </button>
                  <button
                    onClick={skipToPuzzles}
                    className="px-6 py-3 bg-slate-800/50 hover:bg-slate-700/50 text-slate-300 rounded-xl transition-all"
                    data-testid="skip-to-puzzles-btn"
                  >
                    Practice Puzzles
                  </button>
                </div>
                
                {/* Play Chess shortcut */}
                <div className="mt-6 pt-6 border-t border-slate-700/50">
                  <p className="text-slate-500 text-sm mb-3 text-center">Already know how to play?</p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <button
                      onClick={() => startChessGame('ai')}
                      className="px-5 py-2 bg-green-500/20 hover:bg-green-500/30 border border-green-500/30 text-green-300 rounded-lg transition-all text-sm"
                      data-testid="quick-play-ai-btn"
                    >
                      🤖 Play vs AI
                    </button>
                    <button
                      onClick={() => startChessGame('friend')}
                      className="px-5 py-2 bg-pink-500/20 hover:bg-pink-500/30 border border-pink-500/30 text-pink-300 rounded-lg transition-all text-sm"
                      data-testid="quick-play-friend-btn"
                    >
                      🎮 Same Device
                    </button>
                    <button
                      onClick={() => setChessMode('play-friend-online-menu')}
                      className="px-5 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/30 text-cyan-300 rounded-lg transition-all text-sm"
                      data-testid="quick-play-online-btn"
                    >
                      🌐 Play Online
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* LEARN MODE - Piece Learning Cards */}
            {chessMode === 'learn' && (
              <div>
                {/* Progress indicator */}
                <div className="flex justify-center gap-2 mb-6">
                  {CHESS_LEARNING_CARDS.map((card, i) => (
                    <div
                      key={card.id}
                      className={`w-3 h-3 rounded-full transition-all ${
                        i < currentLearningCard ? 'bg-green-500' :
                        i === currentLearningCard ? 'bg-amber-500 scale-125' :
                        'bg-slate-700'
                      }`}
                    />
                  ))}
                </div>
                
                {/* Current Learning Card */}
                {(() => {
                  const card = CHESS_LEARNING_CARDS[currentLearningCard];
                  return (
                    <div className="max-w-2xl mx-auto">
                      {/* Card Header */}
                      <div 
                        className="text-center p-6 rounded-t-2xl"
                        style={{ backgroundColor: `${card.color}20`, borderColor: `${card.color}50` }}
                      >
                        <span className="text-6xl mb-2 block" style={{ textShadow: `0 0 20px ${card.color}` }}>
                          {card.symbol}
                        </span>
                        <h3 className="text-3xl font-philosopher font-bold text-white">
                          The {card.name}
                        </h3>
                      </div>
                      
                      {/* Card Content */}
                      <div className="bg-slate-900/70 p-6 rounded-b-2xl border-x border-b border-slate-700">
                        <p className="text-slate-300 text-lg mb-4">{card.description}</p>
                        
                        <div className="bg-slate-800/50 p-4 rounded-xl mb-4">
                          <p className="text-amber-400 font-semibold mb-2">How it moves:</p>
                          <p className="text-white">{card.movement}</p>
                        </div>
                        
                        {/* Visual movement example */}
                        <div className="mb-4">
                          <p className="text-slate-400 text-sm mb-3 text-center">See where it can move (green squares):</p>
                          <div className="flex justify-center">
                            {renderChessBoard(card.exampleBoard, card.highlightSquares)}
                          </div>
                        </div>
                        
                        <div className="bg-blue-500/10 border border-blue-500/30 p-3 rounded-xl">
                          <p className="text-blue-300 text-sm">
                            <Lightbulb className="w-4 h-4 inline mr-2" />
                            <strong>Tip:</strong> {card.tip}
                          </p>
                        </div>
                      </div>
                      
                      {/* Navigation */}
                      <div className="flex justify-between items-center mt-6">
                        <button
                          onClick={prevLearningCard}
                          disabled={currentLearningCard === 0}
                          className="px-4 py-2 text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                          ← Previous
                        </button>
                        
                        <span className="text-slate-500 text-sm">
                          {currentLearningCard + 1} of {CHESS_LEARNING_CARDS.length}
                        </span>
                        
                        <button
                          onClick={nextLearningCard}
                          className="px-6 py-3 bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 rounded-xl transition-all"
                          data-testid="next-piece-btn"
                        >
                          {currentLearningCard === CHESS_LEARNING_CARDS.length - 1 ? 'Start Puzzles! 🎯' : 'Next Piece →'}
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
            
            {/* PUZZLE MODE - Interactive Puzzles */}
            {chessMode === 'puzzle' && currentChessPuzzle && (
              <div>
                {/* Puzzle type badge */}
                <div className="flex justify-center mb-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    currentChessPuzzle.type === 'recognition' ? 'bg-blue-500/20 text-blue-300' :
                    currentChessPuzzle.type === 'movement' ? 'bg-green-500/20 text-green-300' :
                    currentChessPuzzle.type === 'capture' ? 'bg-orange-500/20 text-orange-300' :
                    currentChessPuzzle.type === 'check' ? 'bg-yellow-500/20 text-yellow-300' :
                    currentChessPuzzle.type === 'safe_move' ? 'bg-cyan-500/20 text-cyan-300' :
                    currentChessPuzzle.type === 'checkmate_in_one' ? 'bg-purple-500/20 text-purple-300' :
                    currentChessPuzzle.type === 'board_position' ? 'bg-pink-500/20 text-pink-300' :
                    'bg-red-500/20 text-red-300'
                  }`}>
                    {currentChessPuzzle.type === 'recognition' ? '🎯 Piece Recognition' :
                     currentChessPuzzle.type === 'movement' ? '♟️ Movement Puzzle' :
                     currentChessPuzzle.type === 'capture' ? '⚔️ Capture Puzzle' :
                     currentChessPuzzle.type === 'check' ? '⚡ Find the Check' :
                     currentChessPuzzle.type === 'safe_move' ? '🛡️ Safe Move' :
                     currentChessPuzzle.type === 'checkmate_in_one' ? '🏆 Checkmate in One!' :
                     currentChessPuzzle.type === 'board_position' ? '🔍 Board Position' :
                     '👑 Checkmate Challenge'}
                  </span>
                </div>
                
                {/* Chess board for board-based puzzles */}
                {currentChessPuzzle.board && (
                  <div className="mb-6">
                    {renderChessBoard(currentChessPuzzle.board, currentChessPuzzle.highlight || [])}
                    {/* Board Legend - Clear move indicators for children */}
                    <div className="flex flex-wrap justify-center gap-4 mt-4 text-xs">
                      <div className="flex items-center gap-2 bg-yellow-500/10 px-3 py-1.5 rounded-full border border-yellow-500/30">
                        <div className="w-5 h-5 bg-yellow-400/30 ring-2 ring-yellow-400 rounded animate-pulse"></div>
                        <span className="text-yellow-300 font-medium">Your piece</span>
                      </div>
                      <div className="flex items-center gap-2 bg-emerald-500/10 px-3 py-1.5 rounded-full border border-emerald-500/30">
                        <div className="w-5 h-5 bg-emerald-500/30 ring-2 ring-emerald-400 rounded flex items-center justify-center">
                          <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                        </div>
                        <span className="text-emerald-300 font-medium">Can move here</span>
                      </div>
                      <div className="flex items-center gap-2 bg-red-500/10 px-3 py-1.5 rounded-full border border-red-500/30">
                        <div className="w-5 h-5 bg-red-500/40 ring-2 ring-red-500 rounded relative">
                          <Sparkles className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 text-red-400" />
                        </div>
                        <span className="text-red-300 font-medium">Can capture!</span>
                      </div>
                    </div>
                  </div>
                )}
                
                <p className="text-xl text-white text-center mb-6">{currentChessPuzzle.question}</p>
                
                {/* Hint display */}
                {chessShowHint && currentChessPuzzle.hint && (
                  <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl flex items-start gap-3 max-w-md mx-auto">
                    <Lightbulb className="w-6 h-6 text-amber-400 flex-shrink-0" />
                    <p className="text-amber-300">{currentChessPuzzle.hint}</p>
                  </div>
                )}
                
                {/* Explanation after correct answer */}
                {chessExplanation && (
                  <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl max-w-md mx-auto">
                    <p className="text-green-300 text-sm">{chessExplanation}</p>
                  </div>
                )}
                
                {/* Options */}
                {chessAnswer === null || chessAnswer !== currentChessPuzzle.answer ? (
                  <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
                    {currentChessPuzzle.options.map((option, i) => (
                      <button
                        key={i}
                        onClick={() => submitChessAnswer(option)}
                        disabled={chessAnswer === option && chessAnswer !== currentChessPuzzle.answer}
                        className={`p-4 rounded-xl text-white transition-all ${
                          chessAnswer === option && chessAnswer !== currentChessPuzzle.answer
                            ? 'bg-red-500/30 border-2 border-red-500 animate-shake'
                            : 'bg-slate-800/50 hover:bg-amber-500/20 border border-white/10 hover:border-amber-500/50'
                        }`}
                        data-testid={`chess-option-${i}`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-green-500/20">
                      <Check className="w-8 h-8 text-green-400" />
                    </div>
                    <p className="text-xl mb-4 text-green-400">
                      Excellent strategic thinking! +{currentChessPuzzle.sparks} sparks
                    </p>
                    <button
                      onClick={selectNextChessPuzzle}
                      className="px-6 py-3 bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 rounded-full transition-all"
                      data-testid="next-chess-puzzle-btn"
                    >
                      <RefreshCw className="w-4 h-4 inline mr-2" />
                      Next Puzzle
                    </button>
                  </div>
                )}
                
                {/* Hint button */}
                {!chessShowHint && chessAnswer === null && (
                  <button
                    onClick={() => setChessShowHint(true)}
                    className="mt-4 flex items-center gap-2 mx-auto text-slate-400 hover:text-amber-400 transition-colors text-sm"
                  >
                    <Lightbulb className="w-4 h-4" />
                    Need a hint?
                  </button>
                )}
                
                {/* Review pieces button */}
                <div className="mt-6 text-center">
                  <button
                    onClick={() => setChessMode('learn')}
                    className="text-sm text-slate-500 hover:text-amber-400 transition-colors"
                  >
                    <BookOpen className="w-4 h-4 inline mr-1" />
                    Review piece movements
                  </button>
                </div>
                
                {/* Play Chess button */}
                <div className="mt-4 text-center">
                  <button
                    onClick={() => setChessMode('play-menu')}
                    className="text-sm text-green-400 hover:text-green-300 transition-colors"
                  >
                    <Crown className="w-4 h-4 inline mr-1" />
                    Ready to Play Chess!
                  </button>
                </div>
              </div>
            )}
            
            {/* PLAY MENU - Choose game mode */}
            {chessMode === 'play-menu' && (
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-green-400 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg shadow-green-500/30">
                  <span className="text-5xl">♟️</span>
                </div>
                
                <h3 className="text-2xl font-philosopher font-bold text-white mb-2">
                  Play Chess!
                </h3>
                <p className="text-slate-400 mb-8">
                  Choose how you want to play
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl mx-auto">
                  {/* Play vs AI */}
                  <button
                    onClick={() => startChessGame('ai')}
                    className="p-6 bg-gradient-to-br from-blue-500/20 to-indigo-500/20 border border-blue-500/30 rounded-2xl hover:border-blue-400/50 transition-all group"
                    data-testid="play-ai-btn"
                  >
                    <div className="w-16 h-16 mx-auto mb-3 bg-blue-500/20 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                      <span className="text-3xl">🤖</span>
                    </div>
                    <h4 className="text-white font-semibold mb-1">Temple Guardian</h4>
                    <p className="text-slate-400 text-sm">Play against a friendly AI</p>
                  </button>
                  
                  {/* Play with Friend (Same Device) */}
                  <button
                    onClick={() => startChessGame('friend')}
                    className="p-6 bg-gradient-to-br from-pink-500/20 to-purple-500/20 border border-pink-500/30 rounded-2xl hover:border-pink-400/50 transition-all group"
                    data-testid="play-friend-local-btn"
                  >
                    <div className="w-16 h-16 mx-auto mb-3 bg-pink-500/20 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                      <span className="text-3xl">🎮</span>
                    </div>
                    <h4 className="text-white font-semibold mb-1">Same Device</h4>
                    <p className="text-slate-400 text-sm">Pass & play on one device</p>
                  </button>
                  
                  {/* Play with Friend Online */}
                  <button
                    onClick={() => setChessMode('play-friend-online-menu')}
                    className="p-6 bg-gradient-to-br from-green-500/20 to-cyan-500/20 border border-green-500/30 rounded-2xl hover:border-green-400/50 transition-all group"
                    data-testid="play-friend-online-btn"
                  >
                    <div className="w-16 h-16 mx-auto mb-3 bg-green-500/20 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                      <span className="text-3xl">🌐</span>
                    </div>
                    <h4 className="text-white font-semibold mb-1">Play Online</h4>
                    <p className="text-slate-400 text-sm">Invite a friend with a code</p>
                  </button>
                </div>
                
                <button
                  onClick={() => setChessMode('puzzle')}
                  className="mt-6 text-slate-400 hover:text-white text-sm transition-colors"
                >
                  ← Back to Puzzles
                </button>
              </div>
            )}
            
            {/* PLAY ONLINE MENU - Create or Join */}
            {chessMode === 'play-friend-online-menu' && (
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-green-400 to-cyan-500 rounded-2xl flex items-center justify-center shadow-lg shadow-green-500/30">
                  <span className="text-5xl">🌐</span>
                </div>
                
                <h3 className="text-2xl font-philosopher font-bold text-white mb-2">
                  Play with a Friend Online
                </h3>
                <p className="text-slate-400 mb-8">
                  Play chess across different devices!
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg mx-auto">
                  {/* Create New Game */}
                  <button
                    onClick={createChessMatch}
                    disabled={chessMultiplayerLoading}
                    className="p-6 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-2xl hover:border-cyan-400/50 transition-all group disabled:opacity-50"
                    data-testid="create-match-btn"
                  >
                    <div className="w-16 h-16 mx-auto mb-3 bg-cyan-500/20 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                      <span className="text-3xl">✨</span>
                    </div>
                    <h4 className="text-white font-semibold mb-1">Start New Game</h4>
                    <p className="text-slate-400 text-sm">Get a code to share</p>
                  </button>
                  
                  {/* Join Existing Game */}
                  <div className="p-6 bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-2xl">
                    <div className="w-16 h-16 mx-auto mb-3 bg-purple-500/20 rounded-full flex items-center justify-center">
                      <span className="text-3xl">🎟️</span>
                    </div>
                    <h4 className="text-white font-semibold mb-2">Join a Game</h4>
                    <input
                      type="text"
                      value={chessJoinCode}
                      onChange={(e) => setChessJoinCode(e.target.value.toUpperCase())}
                      placeholder="ENTER CODE"
                      maxLength={6}
                      className="w-full px-4 py-2 bg-slate-900/50 border border-purple-500/30 rounded-lg text-white text-center text-lg font-mono tracking-widest placeholder-slate-500 focus:outline-none focus:border-purple-400 mb-3"
                      data-testid="join-code-input"
                    />
                    <button
                      onClick={joinChessMatch}
                      disabled={chessMultiplayerLoading || !chessJoinCode.trim()}
                      className="w-full py-2 bg-purple-500/30 hover:bg-purple-500/40 text-purple-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="join-match-btn"
                    >
                      {chessMultiplayerLoading ? 'Joining...' : 'Join Game'}
                    </button>
                  </div>
                </div>
                
                {chessMultiplayerError && (
                  <p className="text-red-400 text-sm mt-4">{chessMultiplayerError}</p>
                )}
                
                <button
                  onClick={() => setChessMode('play-menu')}
                  className="mt-6 text-slate-400 hover:text-white text-sm transition-colors"
                >
                  ← Back to Play Menu
                </button>
              </div>
            )}
            
            {/* PLAY ONLINE - Waiting for Opponent */}
            {chessMode === 'play-friend-online' && chessMultiplayerMode === 'create' && !chessMatchData?.black_player_name && (
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-cyan-400 to-green-500 rounded-2xl flex items-center justify-center shadow-lg shadow-cyan-500/30 animate-pulse">
                  <span className="text-5xl">⏳</span>
                </div>
                
                <h3 className="text-2xl font-philosopher font-bold text-white mb-2">
                  Waiting for Friend...
                </h3>
                <p className="text-slate-400 mb-6">
                  Share this code with a friend to start playing!
                </p>
                
                {/* Match Code Display */}
                <div className="bg-slate-800/60 border-2 border-cyan-500/40 rounded-2xl p-6 max-w-sm mx-auto mb-6">
                  <p className="text-slate-400 text-sm mb-2">Game Code</p>
                  <p className="text-5xl font-mono font-bold text-cyan-300 tracking-[0.3em] mb-4">
                    {chessMatchCode}
                  </p>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(chessMatchCode);
                      toast.success('Code copied!');
                    }}
                    className="px-6 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 rounded-lg transition-colors flex items-center gap-2 mx-auto"
                    data-testid="copy-code-btn"
                  >
                    <Copy className="w-4 h-4" />
                    Copy Code
                  </button>
                </div>
                
                <p className="text-slate-500 text-sm mb-6">
                  Tell your friend to go to Chess Garden → Play Online → Enter this code
                </p>
                
                <button
                  onClick={exitChessMultiplayer}
                  className="text-slate-400 hover:text-white text-sm transition-colors"
                >
                  ← Cancel
                </button>
              </div>
            )}
            
            {/* PLAY ONLINE - Active Game */}
            {chessMode === 'play-friend-online' && chessMatchData?.status === 'playing' && (
              <div>
                {/* Player Info Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      chessMatchData.your_color === 'white' ? 'bg-white text-slate-900' : 'bg-slate-800 text-white border border-slate-600'
                    }`}>
                      ♚
                    </div>
                    <div>
                      <p className="text-white font-medium">{selectedChild?.name}</p>
                      <p className="text-slate-500 text-xs capitalize">Playing as {chessMatchData.your_color}</p>
                    </div>
                  </div>
                  
                  <div className={`px-4 py-2 rounded-lg text-center ${
                    chessMatchData.is_your_turn 
                      ? 'bg-green-500/20 border border-green-500/40' 
                      : 'bg-slate-700/50'
                  }`}>
                    {chessMatchData.is_your_turn ? (
                      <span className="text-green-300 font-semibold animate-pulse">Your Turn!</span>
                    ) : (
                      <span className="text-slate-400">Waiting...</span>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div>
                      <p className="text-white font-medium text-right">
                        {chessMatchData.your_color === 'white' 
                          ? chessMatchData.black_player_name 
                          : chessMatchData.white_player_name}
                      </p>
                      <p className="text-slate-500 text-xs text-right capitalize">
                        Playing as {chessMatchData.your_color === 'white' ? 'black' : 'white'}
                      </p>
                    </div>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      chessMatchData.your_color === 'black' ? 'bg-white text-slate-900' : 'bg-slate-800 text-white border border-slate-600'
                    }`}>
                      ♚
                    </div>
                  </div>
                </div>
                
                {/* Chess Board - Online */}
                <div className="bg-slate-800/60 rounded-xl p-2 sm:p-4">
                  {/* TV Game Mode Controls Hint */}
                  {tvGameMode && (
                    <div className="flex items-center justify-center gap-4 mb-3 text-xs text-slate-400">
                      <span className="flex items-center gap-1">
                        <kbd className="px-2 py-1 bg-slate-700 rounded">↑↓←→</kbd> Move
                      </span>
                      <span className="flex items-center gap-1">
                        <kbd className="px-2 py-1 bg-slate-700 rounded">A</kbd> / 
                        <kbd className="px-2 py-1 bg-slate-700 rounded">Enter</kbd> Select
                      </span>
                      <span className="flex items-center gap-1">
                        <kbd className="px-2 py-1 bg-slate-700 rounded">B</kbd> Cancel
                      </span>
                    </div>
                  )}
                  <div className="grid grid-cols-8 gap-0 aspect-square max-w-md mx-auto border-2 border-amber-700/50 rounded-lg overflow-hidden">
                    {Array.from({ length: 64 }).map((_, i) => {
                      // Flip board for black player
                      const flipBoard = chessMatchData.your_color === 'black';
                      const displayIndex = flipBoard ? 63 - i : i;
                      const row = Math.floor(displayIndex / 8);
                      const col = displayIndex % 8;
                      const file = String.fromCharCode('a'.charCodeAt(0) + col);
                      const rank = 8 - row;
                      const square = file + rank;
                      const isLight = (row + col) % 2 === 0;
                      
                      // Find piece at this square
                      const piece = Object.entries(chessMatchData.board).find(([_, pos]) => pos === square);
                      const pieceKey = piece ? piece[0] : null;
                      
                      const isSelected = chessSelectedSquare === square;
                      const isLegalMove = chessLegalMoves.includes(square);
                      const hasPiece = !!pieceKey;
                      
                      // TV cursor position
                      const isCursor = tvGameMode && chessCursorPos.file === col && chessCursorPos.rank === (7 - row);
                      
                      // Check if this piece can be selected (player's turn and player's piece)
                      const canSelect = chessMatchData.is_your_turn && pieceKey && 
                        ((chessMatchData.your_color === 'white' && pieceKey[0] === pieceKey[0].toUpperCase()) ||
                         (chessMatchData.your_color === 'black' && pieceKey[0] === pieceKey[0].toLowerCase()));
                      
                      return (
                        <button
                          key={i}
                          onClick={() => handleMultiplayerSquareClick(square, pieceKey, chessMatchData)}
                          className={`aspect-square flex items-center justify-center text-2xl sm:text-3xl relative
                            ${isLight ? 'bg-amber-100' : 'bg-amber-700'}
                            ${isSelected ? 'ring-2 ring-blue-400 ring-inset' : ''}
                            ${isCursor ? 'ring-4 ring-cyan-400 ring-inset z-10' : ''}
                            ${canSelect ? 'cursor-pointer hover:brightness-110' : ''}
                            transition-all
                          `}
                          disabled={!chessMatchData.is_your_turn && !isSelected}
                        >
                          {/* TV Cursor indicator */}
                          {isCursor && (
                            <div className="absolute inset-1 border-2 border-cyan-400 rounded animate-pulse pointer-events-none" />
                          )}
                          {pieceKey && (
                            <span className={pieceKey[0] === pieceKey[0].toUpperCase() ? 'text-white drop-shadow-[0_2px_2px_rgba(0,0,0,0.8)]' : 'text-slate-900'}>
                              {getPieceSymbol(pieceKey)}
                            </span>
                          )}
                          {isLegalMove && (
                            <div className={`absolute inset-0 flex items-center justify-center ${hasPiece ? '' : ''}`}>
                              {hasPiece ? (
                                <div className="w-full h-full border-4 border-green-400/60 rounded-sm" />
                              ) : (
                                <div className="w-3 h-3 sm:w-4 sm:h-4 bg-green-400/60 rounded-full" />
                              )}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                {/* Match Code Display */}
                <div className="flex items-center justify-center gap-2 mt-4 text-slate-500 text-sm">
                  <span>Match Code:</span>
                  <span className="font-mono text-cyan-400">{chessMatchCode}</span>
                </div>
                
                <div className="flex justify-center mt-4">
                  <button
                    onClick={exitChessMultiplayer}
                    className="text-slate-400 hover:text-red-400 text-sm transition-colors"
                  >
                    Leave Game
                  </button>
                </div>
              </div>
            )}
            
            {/* PLAY ONLINE - Game Over */}
            {chessMode === 'play-friend-online' && chessMatchData?.status && !['waiting', 'playing'].includes(chessMatchData.status) && (
              <div className="text-center">
                <div className={`w-24 h-24 mx-auto mb-6 rounded-2xl flex items-center justify-center shadow-lg ${
                  chessMatchData.winner === chessMatchData.your_color 
                    ? 'bg-gradient-to-br from-yellow-400 to-amber-500 shadow-yellow-500/30' 
                    : 'bg-gradient-to-br from-slate-500 to-slate-600 shadow-slate-500/30'
                }`}>
                  <span className="text-6xl">{chessMatchData.winner === chessMatchData.your_color ? '👑' : '🤝'}</span>
                </div>
                
                <h3 className="text-3xl font-philosopher font-bold text-white mb-2">
                  {chessMatchData.winner === chessMatchData.your_color ? 'You Won!' : 
                   chessMatchData.winner ? `${chessMatchData.winner === 'white' ? chessMatchData.white_player_name : chessMatchData.black_player_name} Won!` :
                   'Game Over!'}
                </h3>
                
                <p className="text-slate-400 mb-8">
                  {chessMatchData.winner === chessMatchData.your_color 
                    ? 'Congratulations! You beat your friend!' 
                    : 'Good game! Want to try again?'}
                </p>
                
                <div className="flex gap-4 justify-center">
                  <button
                    onClick={createChessMatch}
                    className="px-6 py-3 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 rounded-lg transition-colors"
                    data-testid="rematch-btn"
                  >
                    🔄 Play Again
                  </button>
                  <button
                    onClick={exitChessMultiplayer}
                    className="px-6 py-3 bg-slate-700/50 hover:bg-slate-700/70 text-slate-300 rounded-lg transition-colors"
                  >
                    ← Back to Menu
                  </button>
                </div>
              </div>
            )}
            
            {/* PLAY AI MODE */}
            {chessMode === 'play-ai' && chessGameBoard && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">🤖</span>
                    <span className="text-slate-300">Temple Guardian</span>
                  </div>
                  {chessGameStatus && (
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      chessGameStatus === 'check' ? 'bg-yellow-500/20 text-yellow-300' :
                      chessGameStatus === 'checkmate' ? 'bg-red-500/20 text-red-300' :
                      'bg-slate-500/20 text-slate-300'
                    }`}>
                      {chessGameStatus === 'check' ? '⚡ Check!' : 
                       chessGameStatus === 'checkmate' ? '👑 Checkmate!' : 
                       '🤝 Draw'}
                    </span>
                  )}
                </div>
                
                {/* Game board */}
                <div className="mb-6 relative">
                  {renderInteractiveChessBoard()}
                  
                  {chessAIThinking && (
                    <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center">
                      <div className="text-center">
                        <div className="animate-spin w-8 h-8 border-2 border-white/20 border-t-blue-500 rounded-full mx-auto mb-2"></div>
                        <p className="text-white text-sm">Guardian is thinking...</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Game Over Overlay */}
                  {(chessGameStatus === 'checkmate' || chessGameStatus === 'draw') && (
                    <div className="absolute inset-0 bg-black/70 rounded-lg flex items-center justify-center">
                      <div className="text-center p-6">
                        <div className="text-6xl mb-4">
                          {chessGameStatus === 'checkmate' ? '👑' : '🤝'}
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-2">
                          {chessGameStatus === 'checkmate' ? 'Checkmate!' : 'Stalemate!'}
                        </h3>
                        <p className="text-slate-300">
                          {chessGameStatus === 'checkmate' 
                            ? (chessTurn === 'white' ? 'Black wins!' : 'White wins!') 
                            : 'The game is a draw.'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Turn indicator */}
                <div className="text-center mb-4">
                  <span className={`px-4 py-2 rounded-full text-sm ${
                    chessTurn === 'white' 
                      ? 'bg-white/20 text-white' 
                      : 'bg-slate-800 text-slate-300'
                  }`}>
                    {chessTurn === 'white' ? '⬜ Your turn' : '⬛ Guardian\'s turn'}
                  </span>
                </div>
                
                {/* Controls */}
                <div className="flex justify-center gap-4 mb-4">
                  <button
                    onClick={getAIHint}
                    disabled={chessTurn !== 'white' || chessAIThinking}
                    className="px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 rounded-lg text-sm disabled:opacity-50 transition-all"
                    data-testid="chess-hint-btn"
                  >
                    <Lightbulb className="w-4 h-4 inline mr-1" />
                    Hint
                  </button>
                  <button
                    onClick={() => setChessShowMoveHints(!chessShowMoveHints)}
                    className={`px-4 py-2 rounded-lg text-sm transition-all ${
                      chessShowMoveHints 
                        ? 'bg-green-500/20 text-green-300' 
                        : 'bg-slate-800/50 text-slate-400'
                    }`}
                  >
                    {chessShowMoveHints ? '✓ Show Moves' : 'Show Moves'}
                  </button>
                </div>
                
                {/* Move history */}
                {chessGameHistory.length > 0 && (
                  <div className="bg-slate-900/50 p-3 rounded-xl max-h-24 overflow-y-auto">
                    <p className="text-slate-500 text-xs mb-1">Move History:</p>
                    <div className="flex flex-wrap gap-1">
                      {chessGameHistory.map((move, i) => (
                        <span key={i} className="text-xs text-slate-400">
                          {i % 2 === 0 ? `${Math.floor(i/2) + 1}.` : ''} {move.from}-{move.to}
                          {move.captured && '×'}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Game over buttons */}
                {(chessGameStatus === 'checkmate' || chessGameStatus === 'draw') && (
                  <div className="mt-4 text-center">
                    <button
                      onClick={() => startChessGame('ai')}
                      className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-semibold hover:opacity-90 transition-all"
                    >
                      Play Again
                    </button>
                  </div>
                )}
                
                <button
                  onClick={() => setChessMode('play-menu')}
                  className="mt-4 block mx-auto text-slate-400 hover:text-white text-sm transition-colors"
                >
                  ← Change Game Mode
                </button>
              </div>
            )}
            
            {/* PLAY FRIEND MODE */}
            {chessMode === 'play-friend' && chessGameBoard && (
              <div>
                <div className="text-center mb-4">
                  <span className="text-2xl">👫</span>
                  <h3 className="text-lg text-white font-semibold">Play with Friend</h3>
                  <p className="text-slate-400 text-sm">Pass the device after each move!</p>
                </div>
                
                {chessGameStatus && (
                  <div className="text-center mb-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      chessGameStatus === 'check' ? 'bg-yellow-500/20 text-yellow-300' :
                      chessGameStatus === 'checkmate' ? 'bg-red-500/20 text-red-300' :
                      'bg-slate-500/20 text-slate-300'
                    }`}>
                      {chessGameStatus === 'check' ? '⚡ Check!' : 
                       chessGameStatus === 'checkmate' ? '👑 Checkmate!' : 
                       '🤝 Draw'}
                    </span>
                  </div>
                )}
                
                {/* Game board */}
                <div className="mb-6 relative">
                  {renderInteractiveChessBoard()}
                  
                  {/* Game Over Overlay */}
                  {(chessGameStatus === 'checkmate' || chessGameStatus === 'draw') && (
                    <div className="absolute inset-0 bg-black/70 rounded-lg flex items-center justify-center">
                      <div className="text-center p-6">
                        <div className="text-6xl mb-4">
                          {chessGameStatus === 'checkmate' ? '👑' : '🤝'}
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-2">
                          {chessGameStatus === 'checkmate' ? 'Checkmate!' : 'Stalemate!'}
                        </h3>
                        <p className="text-slate-300">
                          {chessGameStatus === 'checkmate' 
                            ? (chessTurn === 'white' ? 'Black wins!' : 'White wins!') 
                            : 'The game is a draw.'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Turn indicator */}
                <div className="text-center mb-4">
                  <span className={`px-4 py-2 rounded-full text-sm font-medium ${
                    chessTurn === 'white' 
                      ? 'bg-white/20 text-white' 
                      : 'bg-slate-700 text-slate-200'
                  }`}>
                    {chessTurn === 'white' ? '⬜ White\'s turn' : '⬛ Black\'s turn'}
                  </span>
                </div>
                
                {/* Show moves toggle */}
                <div className="flex justify-center mb-4">
                  <button
                    onClick={() => setChessShowMoveHints(!chessShowMoveHints)}
                    className={`px-4 py-2 rounded-lg text-sm transition-all ${
                      chessShowMoveHints 
                        ? 'bg-green-500/20 text-green-300' 
                        : 'bg-slate-800/50 text-slate-400'
                    }`}
                  >
                    {chessShowMoveHints ? '✓ Show Legal Moves' : 'Show Legal Moves'}
                  </button>
                </div>
                
                {/* Move history */}
                {chessGameHistory.length > 0 && (
                  <div className="bg-slate-900/50 p-3 rounded-xl max-h-24 overflow-y-auto">
                    <p className="text-slate-500 text-xs mb-1">Move History:</p>
                    <div className="flex flex-wrap gap-1">
                      {chessGameHistory.map((move, i) => (
                        <span key={i} className="text-xs text-slate-400">
                          {i % 2 === 0 ? `${Math.floor(i/2) + 1}.` : ''} {move.from}-{move.to}
                          {move.captured && '×'}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Game over buttons */}
                {(chessGameStatus === 'checkmate' || chessGameStatus === 'draw') && (
                  <div className="mt-4 text-center">
                    <button
                      onClick={() => startChessGame('friend')}
                      className="px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-xl font-semibold hover:opacity-90 transition-all"
                    >
                      Play Again
                    </button>
                  </div>
                )}
                
                <button
                  onClick={() => setChessMode('play-menu')}
                  className="mt-4 block mx-auto text-slate-400 hover:text-white text-sm transition-colors"
                >
                  ← Change Game Mode
                </button>
              </div>
            )}
            
            {/* Back to Courtyard */}
            <div className="mt-8 text-center">
              <button
                onClick={() => {
                  setActiveActivity(null);
                  setChessActive(false);
                  setChessMode('intro');
                }}
                className="text-slate-400 hover:text-white transition-colors"
              >
                ← Back to Courtyard
              </button>
            </div>
          </div>
        )}

        {/* Vibe Coding Garden */}
        {activeActivity === 'coding-garden' && (
          <VibeCodingGarden
            onComplete={(sparksEarned) => {
              setSparks(prev => ({ ...prev, discovery: (prev.discovery || 0) + sparksEarned }));
            }}
            onBack={() => setActiveActivity(null)}
          />
        )}

        {/* Music Learning Garden */}
        {activeActivity === 'music-learning' && (
          <MusicLearningGarden
            selectedChild={selectedChild}
            onBack={() => setActiveActivity(null)}
            onAwardSparks={async (amount, type) => {
              setSparks(prev => ({ ...prev, [type]: (prev[type] || 0) + amount }));
              // Optionally save sparks to backend
              try {
                if (selectedChild?.id) {
                  await questsAPI.getSparks(selectedChild.id);
                }
              } catch (err) {
                console.log('Spark save failed:', err);
              }
            }}
          />
        )}

        {/* Game Forge */}
        {activeActivity === 'game-forge' && (
          <GameForge
            selectedChild={selectedChild}
            onBack={() => setActiveActivity(null)}
            onAwardSparks={async (amount, type) => {
              setSparks(prev => ({ ...prev, [type]: (prev[type] || 0) + amount }));
              try {
                if (selectedChild?.id) {
                  await questsAPI.getSparks(selectedChild.id);
                }
              } catch (err) {
                console.log('Spark save failed:', err);
              }
            }}
          />
        )}

        {/* Temple Listens - Reading Area */}
        {activeActivity === 'temple-listens' && (
          <TempleListens
            selectedChild={selectedChild}
            onClose={() => setActiveActivity(null)}
          />
        )}

        {/* Spark Store - Spend Sparks */}
        {activeActivity === 'spark-store' && (
          <SparkStore
            selectedChild={selectedChild}
            onBack={() => setActiveActivity(null)}
          />
        )}

        {/* Orb Message */}
        {!activeActivity && (
          <div className="glass-panel p-6 rounded-2xl bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border-cyan-500/30">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-white font-medium">
                  "You have studied well, {selectedChild?.name || 'young explorer'}. Take time to play and create!"
                </p>
                <p className="text-slate-400 text-sm mt-1">— The Knowledge Orb</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Beta Feedback Button */}
        <FeedbackButton childName={selectedChild?.name} childId={selectedChild?.id} />
        
        {/* Activity Limit Modal */}
        <ActivityLimitModal
          isOpen={showLimitModal}
          onClose={() => setShowLimitModal(false)}
          childName={selectedChild?.name || 'Explorer'}
          activitiesCompleted={limitData.activitiesCompleted}
          dailyLimit={limitData.dailyLimit}
        />
      </div>
    </div>
  );
};

export default CourtyardPage;
